import React, {
    Fragment,
    PureComponent,
    ReactNode,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import moment from 'moment';
import Fuse from 'fuse.js';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import { AnimatePresence, motion } from 'framer-motion';
import { Dropdown, message, notification, Menu, Popover, DatePicker, Modal } from 'antd';
import { detectTouchscreen, getDayWord, getOrdinalSufics, getPxPrintUrl, lead0 } from './utils';
import {
    Appointment,
    AppointmentsResponse,
    Patient as AppointmentPatient,
    fetchTodayQueue,
    fetchCompletedAllTime,
    fetchOtherAppointmentAllTime,
    fetchUpcomingAppointmentAllTime,
    CountResponse,
    fetchFollowupQueue,
    CommunicationMetaItem,
    FollowupMessagePurpose,
} from '../api/fetchAppointmentsV2';
import { getAgeString } from '../store/utils';
import { BOOKING_SLUG } from '../api/constants';
import endAppointment from '../api/endAppointment';
import markPaymentAsPaid from '../api/markPaymentAsPaid';
import EmptyAppointmentView from '../components/EmptyAppointmentView';
import { track } from '../tracking';
import { Virtuoso } from 'react-virtuoso';
import DashboardInviteCodePanel from '../components/common/DashboardInviteCodePanel';
import sendReviewLink from '../api/sendReviewLink';
import { EkaCareIcon, followUpIcon } from '../components/common/Icons';
import { AntTooltip } from '../components/common/AntTooltip';
import sendSelfAssessmentLink, { Res } from '../api/sendSelfAssessmentLink';
import fetchApptConfigs, { TagRecord } from '../api/fetchAptConfig';
import linkTagWithApt from '../api/tagAnAppointment';
import createAptTag, { CreateAptTagResponse, CreateTagBody } from '../api/createAptTag';
import CreateTagsForm, { TagFormPropsType } from '../components/appointments/CreateTagsForm';
import AptTagsPanel, { AptTagsPanelProps } from '../components/appointments/AptTagsPanel';
import ColumnSelector from '../components/appointments/ColumnSelector';
import postQueueColumnConfig from '../api/postQueueColumnConfig';
import { FilterDialogV2 } from '../components/common/FiltersDialogV2';
import {
    initMedicalCertificate,
    getDoc,
    getLocalVisitByIds,
    toggleIframeActionDialog,
    onAppointmentAction,
    navigate,
    exportPtAsExcel,
    onReferAdocAction,
    onShowSAAction,
    onCreateTemplateAction,
    onAddVitalsAction,
    onOrderMedicineAction,
    isCanOrderMedicine,
    onRequestMedicalRecordsAction,
    onAddAddressAction,
    isCanBookLabTest,
    onBookLabTestAction,
    onBillingPopupAction,
    onRequestPaymentAction,
    onViewReceiptsPopupAction,
    onPatientEditAction,
    getEnabledDynamicFormFields,
    DFormEntity,
    onCreateAbha,
    isHmisEnabled,
    onSettleDuePopupAction,
    getMe,
    isDoctorOnly,
    showAccessDeniedError,
    isAccessAvailable,
} from '../bridge/methods';
import { AP } from './types/appointments';
import { EKA_EVENT } from '../bridge/events/dispatch';
import { IncomingEvents, REFRESH_APPT } from '../bridge/events/types';
import { CustomDatePicker } from '../components/common/CustomDateSelector';
import sendFollowupReminders from '../api/send-followup-reminder';
import changeFollowupDateOfApt from '../api/changeFollowupDate';
import checkInAppointment, { TrystTransaction } from '../api/checkInAppointment';
import archiveAppointment from '../api/archiveAppointment';
import sendAppointmentReminder from '../api/send-appointment-reminder';
import { getReceiptPaidAmount } from './types/receipt';
import { MyAccessibleDoctor } from '../bridge/types/doctorProfile';
import DoctorFilter from '../components/appointments/doctor-filter';
import { SUBJECT_KEYS } from './types/acl';

const POLL_INTERVAL_MS = 30 * 1000;

type Props = { cId: string; section: string; date?: string | null };

type TagLinkAction = 'ADDING' | 'DELETING' | 'NONE';

export const GENDER_CODE_TO_NAME: { [k: string]: string } = {
    M: 'Male',
    F: 'Female',
    O: 'Others',
};

const fuseConfig = {
    keys: ['_name', '_phone', '_uhid'],
    threshold: 0.1,
    ignoreLocation: true,
    useExtendedSearch: true,
};

const WAIT_SINCE_TEXT: { [k: string]: string } = {
    CN: 'Cancelled',
    CND: 'Cancelled',
    CNS: 'Cancelled',
    RE: 'Rescheduled',
    RED: 'Rescheduled',
    RES: 'Rescheduled',
};

const todayQueueColumnKeys = 'tdy_queue_cols';
const upcomingQueueColumnKeys = 'upco_queue_cols';
const completedQueueColumnKeys = 'cm_queue_cols';
const othersQueueColumnKeys = 'oth_queue_cols';
const followupQueueColumnKeys = 'flw_queue_cols';

export const isCNRE = (status: string): boolean => {
    return WAIT_SINCE_TEXT[status] !== undefined;
};

export type TagRecordWithCount = TagRecord & { count: number };

export type AppointmentConfigs = {
    tags: TagRecordWithCount[];
    user_preferences: {
        [todayQueueColumnKeys]: QueueColumnOptions[0]['key'][];
        [upcomingQueueColumnKeys]: QueueColumnOptions[0]['key'][];
        [completedQueueColumnKeys]: QueueColumnOptions[0]['key'][];
        [othersQueueColumnKeys]: QueueColumnOptions[0]['key'][];
        [followupQueueColumnKeys]: QueueColumnOptions[0]['key'][];
    };
    eligibile_for_payments: boolean;
};

type QueueColumnOptions = {
    priority: number;
    key: string;
    required?: boolean;
    renderer: {
        header: JSX.Element;
        columnSelectorHeader?: JSX.Element;
        renderCell: (a: AP, index: number) => JSX.Element;
    };
    exportInExcel?: {
        title: string;
        exporter: (a: AP) => string | number | Record<string, string | number>;
    };
}[];

type State = {
    canOrderMedicine?: boolean;
    canBookLabTest?: boolean;
    searchInput: string;
    appointments: AP[];
    todayAppointments: AP[];
    isLoading: boolean;
    isTodayLoading: boolean;
    requestedPaymentAppointment?: AP;
    referAP?: AP;
    generateInvoiceAppointment?: AP;
    saAppointment?: AP;
    activeFilterIndex: { [index: number]: boolean };
    visitTypeFilterDialogVisible?: boolean;
    genericFilterVisible?: boolean;
    filteredVisitType: Set<string>;
    countResponse?: CountResponse;
    totalCountResponse?: CountResponse;
    createTemplateAppointment?: AP;
    isCallbackAccordionOpen?: boolean;
    downloadExcelInProgress?: boolean;
    filteredTags: Set<number>;
    appointmentConfigs: AppointmentConfigs;
    tagsFilterDialogVisible?: boolean;
    columnsSelectorVisible?: boolean;
    queueWindowType?: QueueWindowDurationType;
    selectedQueueWindowDates?: [string | null, string | null] | null;
    customQueueWindowDate?: [string | undefined, string | undefined];
    showFollowupFeaturesModal: boolean;
    showFollowupDateSelectorFor?: string;
    tokenSortPreference: 'NONE' | 'ASC' | 'DESC';
    todayQueueOptions: QueueColumnOptions;
    completedQueueOptions: QueueColumnOptions;
    upcomingQueueOptions: QueueColumnOptions;
    othersQueueOptions: QueueColumnOptions;
    followupQueueOptions: QueueColumnOptions;
    filteredAppointmentList: AP[];
    isFiltered: boolean;
    isAppointmentsExporting: boolean;
    selectedDoctor?: MyAccessibleDoctor;
};

type QueueWindowDurationType =
    | 'tomorrow'
    | 'yesterday'
    | 'last-week'
    | 'next-week'
    | 'custom'
    | 'today';
type SectionData = {
    index: number;
    label: string;
    columns: QueueColumnOptions;
    api: (props: {
        cid: string;
        startDate?: string;
        endDate?: string;
        docIds: string[];
    }) => Promise<AppointmentsResponse>;
    showDateReperator: boolean;
    renderBoxContent: (countResponse?: CountResponse) => ReactNode;
    tableClassName: string;
    filters: { label: string; check: (a: AP) => boolean }[];
    emptyText: string;
    searchPlaceholder: string;
    defaultWindowDuration?: QueueWindowDurationType;
    queueWindowDurations: {
        type: QueueWindowDurationType;
        label: string;
    }[];
};

export type APP_VALID_SECTIONS = 'today-queue' | 'completed' | 'upcoming' | 'others' | 'follow-ups';

class Appointments extends PureComponent<Props, State> {
    pollingCount = 0;

    C_SEQ = {
        header: <div className="srno">Seq</div>,
        renderCell: (a: AP, index: number) => <div className="srno">{a.serialNum}</div>,
    };

    C_UHID = {
        header: <div className="uhid">UHID</div>,
        renderCell: (a: AP) => (
            <div className="uhid">{a.formData?.find((fd) => fd.key === 'uhid')?.value || '--'}</div>
        ),
    };

    C_TAGS = {
        header: <div className="appt-tags">Tags</div>,
        renderCell: (a: AP) => {
            const { tags, aid } = a;

            return (
                <MinimizedTags
                    filteredTags={this.state.filteredTags}
                    selectedTags={tags || []}
                    aid={aid}
                    onTagCreation={this.onTagCreation}
                    onTagClick={this.toggleTagForAnAppointment}
                    appointmentConfigs={this.state.appointmentConfigs}
                />
            );
        },
    };
    TOKEN_RENDERER = {
        header: (
            <TokenHeader
                onClick={(sortDirection) => {
                    this.setState((state) => ({
                        ...state,
                        tokenSortPreference: sortDirection,
                        appointments: state.appointments.sort(sortComparator(sortDirection)),
                    }));
                }}
                canSort={() => this.props.section === 'today-queue'}
            />
        ),
        columnSelectorHeader: <div className={'token'}>Token</div>,
        renderCell: (a: AP) => {
            return <div className="token">{a.token ? a.token : '--'}</div>;
        },
    };
    C_PATIENT_DETAILS_TODAY = {
        header: <div className="pt-name-cell">Patient Details</div>,
        renderCell: (a: AP, index: number): JSX.Element => {
            return (
                <PatientDetails
                    a={a}
                    index={index}
                    withVisitCount={true}
                    ptNameClikable={true}
                    showLastVisitedTime={false}
                    lastVisitedDateTime={a.last_visit_time || undefined}
                    onPatientNameClick={this.onPatientDetailsNameClick}
                />
            );
        },
    };
    C_PATIENT_DETAILS_UPCOMING = {
        header: <div className="pt-name-cell">Patient Details</div>,
        renderCell: (a: AP, index: number): JSX.Element => {
            return (
                <PatientDetails
                    a={a}
                    index={index}
                    withVisitCount={true}
                    ptNameClikable={true}
                    showLastVisitedTime={false}
                    lastVisitedDateTime={a.last_visit_time || undefined}
                    onPatientNameClick={this.onPatientDetailsNameClick}
                />
            );
        },
    };
    C_PATIENT_DETAILS_WITHOUT_COUNT = {
        header: <div className="pt-name-cell">Patient Details</div>,
        renderCell: (a: AP, index: number): JSX.Element => {
            return (
                <PatientDetails
                    a={a}
                    index={index}
                    withVisitCount={false}
                    ptNameClikable={true}
                    showLastVisitedTime={true}
                    lastVisitedDateTime={a.end_time}
                    onPatientNameClick={this.onPatientDetailsNameClick}
                />
            );
        },
    };
    C_CONTACT = {
        header: <div className="pt-phone">Contact</div>,
        renderCell: (a: AP) => <div className="pt-phone whitespace-nowrap">{a.patient.mobile}</div>,
    };
    C_VISIT_TYPE = {
        header: <div className="visit-type">Visit Type</div>,
        renderCell: (a: AP) => <div className="visit-type">{getVisitType(a)}</div>,
    };
    C_WAITING_TIME = {
        header: <div className="wait-time">Wait Time</div>,
        renderCell: (a: AP) => {
            if (!a.visit_checkin) {
                return <p className="wait-time text-darwin-neutral-500">--</p>;
            }
            const checkinTime = moment(a.visit_checkin);
            const now = moment();

            const minutes = now.diff(checkinTime, 'minutes') % 60;
            const hours = now.diff(checkinTime, 'hours');

            if (hours > 100) {
                return <p className="wait-time text-darwin-neutral-500">--</p>;
            }

            return (
                <p className={'wait-time text-14 font-700 leading-tight text-darwin-neutral-1000'}>
                    {`${hours ? `${hours}h ` : ''}${minutes}m`}
                </p>
            );
        },
    };
    C_LAST_VISIT = {
        header: <div className="last-visit">Last Visit</div>,
        renderCell: (a: AP) => (
            <div className="last-visit">
                <p>
                    {moment(a.visit_end).isSame(moment(), 'day')
                        ? 'Today'
                        : moment(a.visit_end).format('Do MMM YYYY')}
                </p>
                <p className="text-neutral-5 text-11">{moment(a.visit_end).format('hh:mm A')}</p>
            </div>
        ),
    };
    C_SLOT = {
        header: <div className="slot">Slot</div>,
        renderCell: (a: AP) => (
            <div className="slot justify-center items-center">
                {a.visit_type === 'FLW' && a.status !== 'BK' ? (
                    <p> -- </p>
                ) : (
                    <>
                        <p>{moment(a.start_time).format('hh:mm A')}</p>
                        <p className="text-neutral-5 text-11">
                            {moment(a.start_time).format('Do MMM YYYY')}
                        </p>
                    </>
                )}
            </div>
        ),
    };

    onCheckIn = async (a: AP): Promise<void> => {
        const { success, error, data } = await checkInAppointment(a.aid).catch((e) => {
            console.log('Checkin Appointment ERROR', e);
            return {
                success: false,
                error: 'Failed to checkin appointment.',
                data: {} as TrystTransaction,
            };
        });
        if (success) {
            notification.success({
                message: `${a.patient.fn} has been Checked In Successfully`,
                placement: 'bottomRight',
            });
            const updatedAppointments = this.state.appointments.map((apt) => {
                if (apt.aid === a.aid) {
                    const updatedApt: AP = {
                        ...apt,
                        status: 'CK',
                        visit_checkin: new Date().toISOString(),
                        token: data?.token,
                    };
                    return updatedApt;
                }
                return apt;
            });
            this.setState({ appointments: updatedAppointments });
        } else {
            notification.error({
                message:
                    !!error && typeof error === 'string'
                        ? error
                        : 'Failed to checkin appointment. Please make sure you have Internet Connectivity',
                placement: 'bottomRight',
            });
        }
    };

    C_VISIT_STATUS = {
        header: <div className="visit-status">Visit Status</div>,
        renderCell: (a: AP): JSX.Element => (
            <div className="visit-status">
                {this.props.section === 'today-queue' && a.status === 'BK' ? (
                    <p
                        className={'flex items-center cursor-pointer'}
                        onClick={() => {
                            track('TDY_QUEUE_CK_CLICK', { aid: a.aid });
                            this.onCheckIn(a);
                        }}
                    >
                        <AntTooltip
                            content={
                                'This Appointment is Booked. Check In to monitor patient wait time'
                            }
                            placement={'top'}
                        >
                            <div>
                                <ApptItemChip
                                    icon={
                                        <svg
                                            width="17"
                                            height="14"
                                            viewBox="0 0 17 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            className={'h-10 w-10 fill-darwin-primary-500'}
                                        >
                                            <path d="M11.7188 12.7188L15.7188 8.71875C16.125 8.34375 16.125 7.6875 15.7188 7.3125L11.7188 3.34375C11.3438 2.9375 10.6875 2.9375 10.3125 3.34375C9.90625 3.71875 9.90625 4.34375 10.3125 4.75L12.5938 7H2.5C2.25 7 2 6.78125 2 6.5V1C2 0.46875 1.5625 0 1 0C0.46875 0 0 0.46875 0 1V6.5C0 7.90625 1.125 9 2.5 9H12.5938L10.3125 11.3125C10.125 11.5 10 11.75 10 12C10 12.2188 10.125 12.5 10.3125 12.6875C10.6875 13.125 11.3438 13.125 11.7188 12.7188Z" />
                                        </svg>
                                    }
                                    text="Check In"
                                    textColor="#004BA8"
                                    backgroundColor="#F4F6F9"
                                />
                            </div>
                        </AntTooltip>
                    </p>
                ) : this.props.section !== 'follow-ups' && a.status === 'BK' ? (
                    <p className="font-700 text-status-success">Booked</p>
                ) : a.status === 'CK' ? (
                    <p className="font-700 text-status-success">Checked In</p>
                ) : a.status === 'OG' || a.status === 'PA' ? (
                    <span
                        className="cursor-pointer"
                        onClick={() => onAppointmentAction(a, this.props.cId)}
                    >
                        {getAppointmentActionText(getAppointmentAction(a))}
                    </span>
                ) : a.status === 'AB' ? (
                    <p>Aborted</p>
                ) : a.status === 'PNR' ? (
                    <p>Payment Not Received</p>
                ) : a.visit_type === 'FLW' && ['IN', 'RV', 'PC', 'BK', 'RE'].includes(a.status) ? (
                    <div className={'flex space-x-7 items-start'}>
                        {this.getFollowupVisitStatus(a)}
                    </div>
                ) : (
                    <p>{WAIT_SINCE_TEXT[a.status] || ''}</p>
                )}
            </div>
        ),
    };

    C_REMINDER_RESPONSE = {
        header: <div className="visit-status">Reminder Response</div>,
        renderCell: (a: AP) => {
            const sentMessageStatusWhitelist = new Set<CommunicationMetaItem['status']>([
                'Delivered',
                'Responded',
                'Read',
            ]);

            const reminderMessage = a.comm_meta?.find(
                (item) =>
                    item.purpose === FollowupMessagePurpose.APPOINTMENT_REMINDER &&
                    (sentMessageStatusWhitelist.has(item.status) || item.status === 'Sent'),
            );

            if (reminderMessage) {
                const reminderMessageResponse = reminderMessage?.messageResponse?.toLowerCase();

                if (reminderMessageResponse && reminderMessageResponse.includes('yes')) {
                    return (
                        <div className="visit-status">
                            <ConfirmedVisitMessageResponse />
                        </div>
                    );
                } else if (reminderMessageResponse && reminderMessageResponse.includes('no')) {
                    const notComingResponseMessage = a.comm_meta?.find(
                        (item) =>
                            item.purpose ===
                            FollowupMessagePurpose.APPOINTMENT_REMINDER_NO_RESPONSE,
                    )?.messageResponse;
                    return (
                        <div className="visit-status">
                            <NotComingMessageResponse cancelReason={notComingResponseMessage} />
                        </div>
                    );
                } else if (reminderMessageResponse && reminderMessageResponse.includes('later')) {
                    return (
                        <div className="visit-status">
                            <NotComingMessageResponse cancelReason={reminderMessageResponse} />
                        </div>
                    );
                }

                return (
                    <div className="visit-status">
                        <AwaitingMessageResponse />
                    </div>
                );
            }

            return <div className="visit-status">--</div>;
        },
    };

    C_APT_REMINDER_STATUS = {
        header: <div className="reminders-count">Reminder Status</div>,
        renderCell: (a: AP) => (
            <PatientFlwReminderStatus
                {...a}
                messagePurpose={FollowupMessagePurpose.APPOINTMENT_REMINDER}
            />
        ),
    };

    C_HEALTH_ID = {
        header: <div className="reminders-count">ABHA ID</div>,
        renderCell: (a: AP) => {
            const healthId = a.patient?.['health-ids']?.[0];
            if (healthId) {
                return (
                    <div className="reminders-count flex space-x-7">
                        <div className="flex flex-col">
                            <p className={'text-13 font-700'}>{healthId}</p>
                        </div>
                    </div>
                );
            } else {
                return (
                    <div className="reminders-count flex space-x-7">
                        <div
                            className="cursor-pointer"
                            onClick={() => {
                                track('CREATE_ABHA_APT_QUEUE', {
                                    patient: a.patient?.oid || '',
                                });
                                onCreateAbha(a);
                            }}
                        >
                            <ApptItemChip
                                text="+ Create ABHA"
                                textColor="#004BA8"
                                backgroundColor="#F4F6F9"
                            />
                        </div>
                    </div>
                );
            }
        },
    };

    C_PAYMENT_STATUS = {
        header: <div className="payment-status">Payment Status</div>,
        renderCell: (a: AP) => <div className="payment-status">{renderPaymentStatus(a)}</div>,
    };

    C_AMOUNT = {
        header: <div className="amount">Amount</div>,
        renderCell: (a: AP) => {
            const fallbackBillTotal = getReceiptPaidAmount(a);

            return (
                <div className="amount">
                    {a.billTotal
                        ? `₹${a.billTotal.toLocaleString('en-IN')}`
                        : fallbackBillTotal
                        ? `₹${fallbackBillTotal.toLocaleString('en-IN')}`
                        : '-'}
                </div>
            );
        },
    };

    C_ABOUT_PATIENT = {
        header: <div className="uhid">About</div>,
        renderCell: (a: AP) => (
            <div className="uhid">{a.formData?.find((fd) => fd.key === 'tag')?.value || '--'}</div>
        ),
    };

    reminderStatusRenderCell = (a: AP): JSX.Element => <PatientFlwReminderStatus {...a} />;

    C_REMINDERS_STATUS = {
        header: <div className="reminders-count">Reminder Status</div>,
        renderCell: this.reminderStatusRenderCell,
    };

    C_FOLLOWUP = {
        header: <div className="follow-up">Follow Up</div>,
        renderCell: (a: AP): JSX.Element => {
            const now = new Date();
            return (
                <div
                    onClick={() => {
                        if (!!a.follow_up) {
                            return;
                        }
                        this.setState({
                            showFollowupDateSelectorFor: a.aid,
                        });
                    }}
                    className={`follow-up relative flex ${!a.follow_up ? 'cursor-pointer' : ''}`}
                >
                    <DatePicker
                        open={this.state.showFollowupDateSelectorFor === a.aid}
                        onOpenChange={(open) => {
                            if (!open) {
                                this.setState({ showFollowupDateSelectorFor: undefined });
                            }
                        }}
                        format={'Do MMM'}
                        onChange={(date) => {
                            const selectedFlwDate = date?.format('YYYY-MM-DD');
                            if (selectedFlwDate) {
                                track('ADD_FLW_DATE_COMPLETED_QUEUE', { aid: a.aid });
                                this.changeFollowupDate(a.aid, selectedFlwDate);
                            }
                        }}
                        disabledDate={(date) => {
                            if (date.unix() * 1000 < now.getTime()) {
                                return true;
                            }
                            return false;
                        }}
                    />
                    {!!a.follow_up ? (
                        moment(a.follow_up).format('Do MMM YY')
                    ) : (
                        <ApptItemChip
                            text="+ Follow up"
                            textColor="#004BA8"
                            backgroundColor="#F4F6F9"
                        />
                    )}
                </div>
            );
        },
    };

    C_ASSESSMENT_STATUS = {
        header: <div className="sa-status">Self Assessment</div>,
        renderCell: (a: AP) =>
            a.sa_id ? (
                <div className="sa-status">
                    <div className="inline-flex flex-col xl:flex-row items-center space-x-4 bg-status-successBG px-8 justify-center rounded-2 py-2">
                        <div className="hidden xl:flex items-center justify-center w-14 h-14">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="fill-status-success w-14 h-14"
                            >
                                <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                            </svg>
                        </div>
                        <p className="text-status-success text-13">Filled</p>
                        <a
                            className="text-actionPrimary underline font-700 cursor-pointer text-13 ml-4"
                            onClick={() => this.onShowSa(a)}
                        >
                            View
                        </a>
                    </div>
                </div>
            ) : (
                <div className="sa-status">
                    <div className="space-x-4 flex py-10 rounded-2 items-center px-8">
                        <div className="hidden xl:flex items-center justify-center w-14 h-14">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="w-14 h-14 fill-status-warning"
                            >
                                <path d="M256,8C119,8,8,119,8,256S119,504,256,504,504,393,504,256,393,8,256,8Zm92.49,313h0l-20,25a16,16,0,0,1-22.49,2.5h0l-67-49.72a40,40,0,0,1-15-31.23V112a16,16,0,0,1,16-16h32a16,16,0,0,1,16,16V256l58,42.5A16,16,0,0,1,348.49,321Z"></path>
                            </svg>
                        </div>
                        <p className="text-status-warning text-13 leading-4">Not Filled</p>
                    </div>
                </div>
            ),
    };

    getFollowupVisitStatus = (a: AP) => {
        if (a.status === 'IN') {
            return <AwaitingMessageResponse />;
        } else if (a.status === 'PC') {
            const comm =
                a?.comm_meta?.find(
                    (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_REJECTION_RESPONSE,
                ) ||
                a?.comm_meta?.find(
                    (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_REMINDER,
                );

            const cancelReason = comm?.messageResponse ?? '';

            return <NotComingMessageResponse cancelReason={cancelReason} />;
        } else if (a.status === 'RV' || a.status === 'BK') {
            const comm = a.comm_meta?.find(
                (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_ACCEPTANCE_RESPONSE,
            );

            const responseMessage =
                a.status === 'RV' && !!comm?.messageResponse ? comm.messageResponse : '';

            let formattedResponseMessage = '';
            if (responseMessage) {
                const [startTime, endTime, timeOfDay] = responseMessage
                    .replace(/\s/g, '')
                    .split('-');
                formattedResponseMessage = `Prefer to visit around ${startTime} - ${endTime} in the ${timeOfDay}`;
            }

            return (
                <ConfirmedVisitMessageResponse
                    formattedResponseMessage={formattedResponseMessage}
                />
            );
        } else if (a.status === 'RE') {
            const comm = a.comm_meta?.find(
                (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_RESCHEDULE_RESPONSE,
            );
            const responseMessage = comm?.messageResponse ?? '';
            return <RescheduledMessageResponse responseMessage={responseMessage} />;
        }
        return null;
    };

    getFollowupVisitStatusForExcel = (a: AP) => {
        if (a.status === 'IN') {
            return 'Awaiting Response';
        } else if (a.status === 'PC') {
            const comm =
                a?.comm_meta?.find(
                    (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_REJECTION_RESPONSE,
                ) ||
                a?.comm_meta?.find(
                    (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_REMINDER,
                );

            const cancelReason = comm?.messageResponse ?? '';

            return `Not Coming${cancelReason ? ` - ${cancelReason}` : ''}`;
        } else if (a.status === 'RV' || a.status === 'BK') {
            const comm = a.comm_meta?.find(
                (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_ACCEPTANCE_RESPONSE,
            );

            const responseMessage =
                a.status === 'RV' && !!comm?.messageResponse ? comm.messageResponse : '';

            let formattedResponseMessage = '';
            if (responseMessage) {
                const [startTime, endTime, timeOfDay] = responseMessage
                    .replace(/\s/g, '')
                    .split('-');
                formattedResponseMessage = `Prefer to visit around ${startTime} - ${endTime} in the ${timeOfDay}`;
            }

            return `Confirmed Visit${
                formattedResponseMessage ? ` - ${formattedResponseMessage}` : ''
            }`;
        } else if (a.status === 'RE') {
            const comm = a.comm_meta?.find(
                (comm) => comm.purpose === FollowupMessagePurpose.FOLLOWUP_RESCHEDULE_RESPONSE,
            );
            const responseMessage = comm?.messageResponse ?? '';
            return `Rescheduled${responseMessage ? ` - ${responseMessage}` : ''}`;
        }

        return '';
    };

    onArchiveAppointment = (a: AP) => {
        Modal.confirm({
            title: `Are you sure you want to archive ${a.patient.fln}'s appointment?`,
            okText: 'Yes',
            cancelText: 'Cancel',
            onOk: async () => {
                const { success } = await archiveAppointment({ aid: a.aid });
                if (success) {
                    let countToUpdate: keyof CountResponse = 'today';
                    const { section } = this.props;

                    if (section === 'completed') {
                        countToUpdate = 'completed';
                    } else if (section === 'upcoming') {
                        countToUpdate = 'upcoming';
                    } else if (section === 'follow-ups') {
                        countToUpdate = 'followup';
                    } else if (section === 'others') {
                        countToUpdate = 'other';
                    }

                    this.setState((state) => ({
                        appointments: state.appointments.filter((item) => item.aid !== a.aid),
                        todayAppointments: state.todayAppointments.filter(
                            (item) => item.aid !== a.aid,
                        ),
                        countResponse: state.countResponse?.[countToUpdate]?.count
                            ? {
                                  ...state.countResponse,
                                  [countToUpdate]: {
                                      count: state.countResponse[countToUpdate]!.count! - 1,
                                  },
                              }
                            : state.countResponse,
                    }));
                } else {
                    notification.error({
                        message: 'Failed to archive appointment',
                        placement: 'bottomRight',
                    });
                }
            },
        });
    };

    generateOngoingApptOptionItems = (a: AP) => {
        const isDoctorPersonaOnly = isDoctorOnly();
        return (
            <Menu>
                <OptionItem onClick={() => this.onPatientNameClick(a)} label={'Patient Details'} />
                <OptionItem
                    onClick={() => this.onEnterVitals(a)}
                    label={'Add vitals & lab results'}
                />
                {a.visit_type === 'FLW' && a.status === 'IN' ? (
                    <OptionItem
                        onClick={() => this.onSendFollowupReminder(a)}
                        label={'Send Followup Reminder'}
                    />
                ) : null}
                {a.status === 'BK' ? (
                    <OptionItem
                        onClick={() => this.onSendAppointmentReminder(a)}
                        label={'Send Appointment Reminder'}
                    />
                ) : null}
                {a.visit_type === 'FLW' && ['IN', 'RV', 'PC', 'BK'].includes(a.status) ? (
                    <OptionItem
                        onClick={() => {
                            track('FLW_QUEUE_CHECKIN', { aid: a.aid });
                            this.onCheckIn(a);
                        }}
                        label={'Check In'}
                    />
                ) : null}
                {a.status !== 'OG' &&
                    !(a.visit_type === 'FLW' && ['IN', 'RV', 'PC'].includes(a.status)) && (
                        <OptionItem
                            onClick={() => this.onAppointmentViewDetials(a.aid, true)}
                            label={'Cancel / Reschedule Appointment'}
                        />
                    )}

                {a.status === 'RV' || a.status === 'IN' || a.status === 'PC' ? (
                    <OptionItem
                        onClick={() => this.onBookFollowupSlot(a.aid)}
                        label={'Book Appointment'}
                    />
                ) : null}

                {this.state.canOrderMedicine && (
                    <OptionItem onClick={() => this.onAddAddress(a)} label={'Add Address'} />
                )}
                {isDoctorPersonaOnly ? (
                    <OptionItem
                        onClick={() => this.onRequestMedicalRecords(a)}
                        label={'Request Medical Records'}
                    />
                ) : null}
                {!!a.sa_id ? (
                    <OptionItem onClick={() => this.onShowSa(a)} label={'View Assessment'} />
                ) : null}

                {a.cp && a.status !== 'PA' && (
                    <OptionItem
                        onClick={() => this.onParkAppointment(a)}
                        label={'Park Appointment'}
                    />
                )}
                {a.can_pay ||
                    a.receipts.length > 0 ||
                    (!!a.patient.amount_pending && a.patient.amount_pending > 0) ||
                    (a.payment_id && a.payment_status !== 'success')}
                <Menu.SubMenu
                    title={<OptionItemContent label="Bill" />}
                    className="apt-drdn-submenu font-lato"
                    icon={null}
                >
                    <OptionItem onClick={() => this.onSendInvoice(a)} label={'Bill Patient'} />
                    {a.receipts.length > 0 ? (
                        <OptionItem
                            onClick={() => this.onViewReceipts(a)}
                            label={'View Receipts'}
                        />
                    ) : null}
                    {!!a.patient.amount_pending && a.patient.amount_pending > 0 ? (
                        <OptionItem
                            onClick={() => {
                                onSettleDuePopupAction(a);
                            }}
                            label={`Clear Dues (₹ ${a.patient.amount_pending})`}
                        />
                    ) : null}
                    {a.can_pay && (
                        <OptionItem
                            onClick={() => this.onRequestPayment(a)}
                            label={'Request Payment'}
                        />
                    )}
                    {a.payment_id && a.payment_status !== 'success' && (
                        <OptionItem onClick={() => this.onMarkAsPaid(a)} label={'Mark as Paid'} />
                    )}
                </Menu.SubMenu>
                {a.can_abort && (
                    <OptionItem
                        onClick={() => this.onAbortAppointment(a)}
                        label={'Abort Appointment'}
                    />
                )}
                <OptionItem
                    onClick={() => this.onArchiveAppointment(a)}
                    label={'Archive Appointment'}
                />
                {isDoctorPersonaOnly ? (
                    <OptionItem
                        onClick={() => this.setMedicalCertPt(a)}
                        label={'Create Medical Certificate'}
                    />
                ) : null}
                <OptionItem onClick={() => this.onReferADoc(a)} label={'Refer to Doctor'} />
                <OptionItem onClick={() => this.onEndVisit(a)} label={'End Visit'} />
                <OptionItem
                    onClick={() => this.onSendReviewLink(a)}
                    label={'Send Google Review Link'}
                />
            </Menu>
        );
    };

    C_ACTIONS = {
        header: <div className="cta">Status / Actions</div>,
        renderCell: (a: AP) => {
            return (
                <div className="cta">
                    <div className="flex space-x-4 items-center justify-end">
                        <div
                            onClick={() => onAppointmentAction(a, this.props.cId)}
                            className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4"
                        >
                            {getAppointmentActionTextForAction(getAppointmentAction(a))}
                        </div>

                        <div
                            style={
                                !isCNRE(a.status) &&
                                a.status !== 'AB' &&
                                a.status !== 'PNR' &&
                                a.status !== 'NS'
                                    ? {}
                                    : {
                                          visibility: 'hidden',
                                      }
                            }
                            className="flex items-center justify-center w-16 h-28 bg-neutral-9 rounded-4 p-4 relative"
                        >
                            <Dropdown
                                overlay={this.generateOngoingApptOptionItems(a)}
                                placement="bottomRight"
                                arrow={true}
                            >
                                <svg
                                    className="text-neutral-2 w-16 h-16 cursor-pointer"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 128 512"
                                >
                                    <path d="M128 64c0-35.39-28.62-64-64-64S0 28.61 0 64s28.62 64 64 64S128 99.39 128 64zM128 256c0-35.39-28.62-64-64-64S0 220.6 0 256s28.62 64 64 64S128 291.4 128 256zM128 448c0-35.39-28.62-64-64-64s-64 28.61-64 64s28.62 64 64 64S128 483.4 128 448z"></path>
                                </svg>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            );
        },
    };

    generateCompletedApptOptionItems = (a: AP) => {
        const isDoctorPersonaOnly = isDoctorOnly();
        return (
            <Menu>
                <OptionItem onClick={() => this.onPatientNameClick(a)} label={'Patient Details'} />
                <OptionItem
                    onClick={() => this.onAppointmentViewDetials(a.aid)}
                    label={'Appointment Details'}
                />
                <OptionItem
                    onClick={() => this.onArchiveAppointment(a)}
                    label={'Archive Appointment'}
                />
                {a.can_pay ||
                (a.payment_id && a.payment_status !== 'success') ||
                a.status !== 'AB' ||
                (a.status !== 'AB' && a.receipts.length > 0) ||
                (a.status !== 'AB' &&
                    !!a.patient.amount_pending &&
                    a.patient.amount_pending > 0) ? (
                    <Menu.SubMenu
                        title={<OptionItemContent label="Bill" />}
                        className="apt-drdn-submenu font-lato"
                        icon={null}
                    >
                        {a.status !== 'AB' && (
                            <>
                                <OptionItem
                                    onClick={() => this.onSendInvoice(a)}
                                    label={'Bill Patient'}
                                />
                                {a.receipts.length > 0 ? (
                                    <OptionItem
                                        onClick={() => this.onViewReceipts(a)}
                                        label={'View Receipts'}
                                    />
                                ) : null}
                                {!!a.patient.amount_pending && a.patient.amount_pending > 0 ? (
                                    <OptionItem
                                        onClick={() => onSettleDuePopupAction(a)}
                                        label={`Clear Dues (₹ ${a.patient.amount_pending})`}
                                    />
                                ) : null}
                            </>
                        )}
                        {a.can_pay && (
                            <OptionItem
                                onClick={() => this.onRequestPayment(a)}
                                label={'Request Payment'}
                            />
                        )}
                        {a.payment_id && a.payment_status !== 'success' && (
                            <OptionItem
                                onClick={() => this.onMarkAsPaid(a)}
                                label={'Mark as Paid'}
                            />
                        )}
                    </Menu.SubMenu>
                ) : null}

                <OptionItem onClick={() => this.onReferADoc(a)} label={'Refer to Doctor'} />

                {a.prescription_id && (
                    <OptionItem
                        onClick={() => this.onCreateTemplate(a)}
                        label={'Create a Template'}
                    />
                )}
                <OptionItem
                    onClick={() => this.onSendReviewLink(a)}
                    label={'Send Google Review Link'}
                />
                {isDoctorPersonaOnly ? (
                    <OptionItem
                        onClick={() => this.setMedicalCertPt(a)}
                        label={'Create Medical Certificate'}
                    />
                ) : null}
            </Menu>
        );
    };

    C_COMPLETED_ACTIONS = {
        header: <div className="cta">Status / Actions</div>,
        renderCell: (a: AP) => {
            // TODO take latest CTA styles
            return (
                <div className="cta">
                    <div className="flex space-x-4 items-center justify-end">
                        <div className="flex flex-col xl:flex-row xl:space-x-4 xl:space-y-0 space-y-4 space-x-4">
                            <Dropdown
                                overlay={this.renderInitatePaymentDialog(a)}
                                placement="bottomRight"
                                arrow={true}
                            >
                                <div className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4 relative">
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            className="w-14 h-14 fill-actionPrimary"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 320 512"
                                        >
                                            <path d="M320 176c0 17.67-14.33 32-32 32h-35.99c-13.32 58.19-61.63 103.2-121.8 110.8l144.3 103.1c14.39 10.28 17.72 30.27 7.453 44.64C275.8 475.3 265.1 480 255.1 480c-6.438 0-12.94-1.938-18.57-5.953l-224-160C2.078 305.1-2.719 291.5 1.531 278.2C5.766 264.1 18.09 256 32 256h80c32.72 0 60.84-19.8 73.22-48H32c-17.67 0-32-14.33-32-32s14.33-32 32-32h153.2C172.8 115.8 144.7 96 112 96H32C14.33 96 0 81.67 0 64s14.33-32 32-32h256c17.67 0 32 14.33 32 32s-14.33 32-32 32h-56.38c9.637 14.36 16.47 30.63 20.44 48H288C305.7 144 320 158.3 320 176z"></path>
                                        </svg>
                                    </div>
                                    <p className="font-700 text-actionPrimary text-13">Initiate</p>
                                </div>
                            </Dropdown>
                            {a.prescription_id && (
                                <div
                                    onClick={() => {
                                        a.prescription_id &&
                                            window.open(
                                                `${getPxPrintUrl(a.prescription_id)}`,
                                                '_blank',
                                                'default',
                                            );
                                        track('APPT_SELECT', { option: 'print' });
                                    }}
                                    className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4"
                                >
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            className="w-14 h-14 fill-actionPrimary"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 448 512"
                                        >
                                            <path d="M321.9 352l119-119c9.375-9.375 9.375-24.56 0-33.94s-24.56-9.375-33.94 0L288 318.1L192.3 222.4C246.3 214.4 288 168.2 288 112C288 50.25 237.8 0 176 0H24C10.75 0 0 10.75 0 24v272.2c0 13.25 10.75 24.01 23.1 24.01S48 309.3 48 296V224h78.06l128 128l-119 119c-9.375 9.375-9.375 24.56 0 33.94C139.7 509.7 145.9 512 152 512s12.28-2.344 16.97-7.031L288 385.9l119 119C411.7 509.7 417.9 512 424 512s12.28-2.344 16.97-7.031c9.375-9.375 9.375-24.56 0-33.94L321.9 352zM48 48h128c35.3 0 64 28.72 64 64s-28.7 64-64 64h-128V48z"></path>
                                        </svg>
                                    </div>
                                    <p className="font-700 text-actionPrimary text-13">Print</p>
                                </div>
                            )}

                            {this.state.canOrderMedicine && this.state.canBookLabTest ? (
                                <Dropdown
                                    overlay={this.renderHEOptions(a)}
                                    placement="bottomRight"
                                    arrow={true}
                                >
                                    <div className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4">
                                        <div className="flex items-center justify-center w-16 h-16">
                                            <svg
                                                className="w-16 h-16 fill-actionPrimary"
                                                role="img"
                                                viewBox="0 0 12 10"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path d="M10.332 4.29297C9.10156 3.08203 7.12891 3.08203 5.89844 4.29297C4.6875 5.52344 4.6875 7.49609 5.89844 8.72656C7.12891 9.9375 9.10156 9.9375 10.332 8.72656C11.543 7.49609 11.543 5.52344 10.332 4.29297ZM6.26953 5.32812L9.29688 8.35547C7.32422 9.60547 5.01953 7.30078 6.26953 5.32812ZM9.96094 7.69141L6.93359 4.66406C8.90625 3.41406 11.2305 5.71875 9.96094 7.69141ZM2.1875 0.875C0.976562 0.875 0 1.87109 0 3.0625V7.41797C0 8.62891 0.976562 9.58594 2.1875 9.58594C3.37891 9.58594 4.375 8.60938 4.375 7.41797V3.0625C4.375 1.87109 3.37891 0.875 2.1875 0.875ZM3.4375 5.25H0.9375V3.0625C0.9375 1.40234 3.4375 1.40234 3.4375 3.0625V5.25Z" />
                                            </svg>
                                        </div>
                                    </div>
                                </Dropdown>
                            ) : this.state.canOrderMedicine ? (
                                <div
                                    onClick={() => this.showOrderMedicineDialogVisible(a)}
                                    className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4"
                                >
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            className="w-16 h-16 fill-actionPrimary"
                                            role="img"
                                            viewBox="0 0 12 10"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path d="M10.332 4.29297C9.10156 3.08203 7.12891 3.08203 5.89844 4.29297C4.6875 5.52344 4.6875 7.49609 5.89844 8.72656C7.12891 9.9375 9.10156 9.9375 10.332 8.72656C11.543 7.49609 11.543 5.52344 10.332 4.29297ZM6.26953 5.32812L9.29688 8.35547C7.32422 9.60547 5.01953 7.30078 6.26953 5.32812ZM9.96094 7.69141L6.93359 4.66406C8.90625 3.41406 11.2305 5.71875 9.96094 7.69141ZM2.1875 0.875C0.976562 0.875 0 1.87109 0 3.0625V7.41797C0 8.62891 0.976562 9.58594 2.1875 9.58594C3.37891 9.58594 4.375 8.60938 4.375 7.41797V3.0625C4.375 1.87109 3.37891 0.875 2.1875 0.875ZM3.4375 5.25H0.9375V3.0625C0.9375 1.40234 3.4375 1.40234 3.4375 3.0625V5.25Z" />
                                        </svg>
                                    </div>
                                </div>
                            ) : this.state.canBookLabTest ? (
                                <div
                                    onClick={() => this.showBookLabTestDialogVisible(a)}
                                    className="p-4 inline-flex items-center space-x-4 cursor-pointer justify-end bg-neutral-9 rounded-4"
                                >
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            className="w-16 h-12 fill-actionPrimary"
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 512 512"
                                        >
                                            <path d="M504.1 183l-176-176C324.3 2.344 318.1 0 312 0s-12.28 2.344-16.97 7.031c-9.375 9.375-9.375 24.56 0 33.94l15.16 15.16l-277.3 276.5c-38.75 38.75-45.13 102-9.375 143.5C44.08 500 72.76 512 101.5 512h.4473c26.38 0 52.75-9.1 72.88-30.12l281-280.1l15.19 15.19C475.7 221.7 481.8 224 488 224s12.28-2.344 16.97-7.031C514.3 207.6 514.3 192.4 504.1 183zM333.4 256H177.7l166.3-165.9l77.82 77.73L333.4 256z"></path>
                                        </svg>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                        <div className="flex items-center justify-center w-16 h-28 bg-neutral-9 rounded-4 p-4 relative">
                            <Dropdown
                                overlay={this.generateCompletedApptOptionItems(a)}
                                placement="bottomRight"
                                arrow={true}
                            >
                                <svg
                                    className="text-neutral-2 w-16 h-16 cursor-pointer"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 128 512"
                                    style={
                                        !isCNRE(a.status) && a.status !== 'AB'
                                            ? {}
                                            : {
                                                  visibility: 'hidden',
                                              }
                                    }
                                >
                                    <path d="M128 64c0-35.39-28.62-64-64-64S0 28.61 0 64s28.62 64 64 64S128 99.39 128 64zM128 256c0-35.39-28.62-64-64-64S0 220.6 0 256s28.62 64 64 64S128 291.4 128 256zM128 448c0-35.39-28.62-64-64-64s-64 28.61-64 64s28.62 64 64 64S128 483.4 128 448z"></path>
                                </svg>
                            </Dropdown>
                        </div>
                    </div>
                </div>
            );
        },
    };
    F_NOT_FILED_SA = {
        label: 'Not Filled SA',
        check: (a: AP): boolean => (a.sa_id ? false : true),
    };
    F_UNBILLED = {
        label: 'Not Billed',
        check: (a: AP): boolean => a.billStatus === 'unbilled',
    };
    F_CANCELLED = {
        label: 'Cancelled',
        check: (a: AP): boolean => ['CN', 'CND', 'CNS'].includes(a.status),
    };
    F_ABORTED = {
        label: 'Aborted',
        check: (a: AP): boolean => a.status === 'AB',
    };
    F_VISIT_CONFIRMED = {
        label: 'Visit Confirmed',
        check: (a: AP): boolean => ['BK', 'RV'].includes(a.status),
    };
    F_PENDING_CONFIRM = {
        label: 'Pending Confirmation',
        check: (a: AP): boolean => a.status === 'IN',
    };
    F_RESCEDHULED = {
        label: 'Rescheduled',
        check: (a: AP) => a.status === 'RE',
    };
    F_NOT_COMING = {
        label: 'Not Coming',
        check: (a: AP) => a.status === 'PC',
    };

    excelHelper = {
        TOKEN: {
            title: 'Token',
            exporter: (a: AP) => a?.token || '',
        },
        PATIENT_DETAILS: {
            title: 'Patient Details',
            exporter: (a: AP) => ({
                'Patient Name': a?.patient?.fln,
                Gender: a.patient?.gen || '',
                Age: a.patient.dob
                    ? getAgeString({
                          dob: a.patient.dob,
                      })
                    : '',
            }),
        },
        ABOUT_PATIENT: {
            title: 'About',
            exporter: (a: AP) => {
                return a.formData?.find((fd) => fd.key === 'tag')?.value || '--';
            },
        },
        UHID: {
            title: 'UHID',
            exporter: (a: AP) => {
                return a.formData?.find((fd) => fd.key === 'uhid')?.value || '--';
            },
        },
        HEALTH_ID: {
            title: 'Health ID',
            exporter: (a: AP) => {
                return a.patient?.['health-ids']?.[0] || '--';
            },
        },
        TAGS: {
            title: 'Tags',
            exporter: (a: AP) => {
                const { appointmentConfigs } = this.state;
                const tagIdToNameMap = new Map<number, string>();

                appointmentConfigs.tags.forEach((item) => {
                    tagIdToNameMap.set(item.tag_id, item.label);
                });

                return a?.tags?.map((item) => tagIdToNameMap.get(item)).join(', ') ?? '';
            },
        },
        CONTACT: {
            title: 'Contact',
            exporter: (a: AP) => {
                return a.patient.mobile;
            },
        },
        VISIT_TYPE: {
            title: 'Visit Type',
            exporter: (a: AP) => {
                const visitType = a['visit-type'] || '';
                const channel = a.channel;
                const service = a.service;

                const config =
                    visitType in VISIT_TYPE_MAP
                        ? VISIT_TYPE_MAP[visitType]
                        : VISIT_TYPE_MAP['default'];

                return `${service ? service?.service_name : config.title} | ${channel}`;
            },
        },
        SLOT: {
            title: 'Slot',
            exporter: (a: AP) => {
                return a.visit_type === 'FLW' && a.status !== 'BK'
                    ? '--'
                    : `${moment(a.start_time).format('hh:mm A')}`;
            },
        },
        VISIT_STATUS: {
            title: 'Visit Status',
            exporter: (a: AP) => {
                return a.status === 'BK'
                    ? 'Booked'
                    : a.status === 'CK'
                    ? 'Checked In'
                    : a.status === 'OG' || a.status === 'PA'
                    ? getAppointmentActionTextForExcel(getAppointmentAction(a))
                    : a.status === 'AB'
                    ? 'Aborted'
                    : a.status === 'PNR'
                    ? 'Payment Not Received'
                    : a.visit_type === 'FLW' && ['IN', 'RV', 'PC', 'BK', 'RE'].includes(a.status)
                    ? this.getFollowupVisitStatusForExcel(a)
                    : WAIT_SINCE_TEXT[a.status] || '';
            },
        },
        APT_REMINDER_RESPONSE: {
            title: 'Reminder Response',
            exporter: (a: AP) => {
                const sentMessageStatusWhitelist = new Set<CommunicationMetaItem['status']>([
                    'Delivered',
                    'Responded',
                    'Read',
                ]);

                const reminderMessage = a.comm_meta?.find(
                    (item) =>
                        item.purpose === FollowupMessagePurpose.APPOINTMENT_REMINDER &&
                        (sentMessageStatusWhitelist.has(item.status) || item.status === 'Sent'),
                );

                if (reminderMessage) {
                    const reminderMessageResponse = reminderMessage?.messageResponse?.toLowerCase();

                    if (reminderMessageResponse && reminderMessageResponse.includes('yes')) {
                        return 'Confirmed Visit';
                    } else if (reminderMessageResponse && reminderMessageResponse.includes('no')) {
                        const notComingResponseMessage = a.comm_meta?.find(
                            (item) =>
                                item.purpose ===
                                FollowupMessagePurpose.APPOINTMENT_REMINDER_NO_RESPONSE,
                        )?.messageResponse;
                        return `Not Coming${
                            notComingResponseMessage ? ` - ${notComingResponseMessage}` : ''
                        }`;
                    } else if (
                        reminderMessageResponse &&
                        reminderMessageResponse.includes('later')
                    ) {
                        return `Not Coming${
                            reminderMessageResponse ? ` - ${reminderMessageResponse}` : ''
                        }`;
                    }

                    return 'Awaiting Response';
                }

                return '--';
            },
        },
        APT_REMINDER_STATUS: {
            title: 'Reminder Status',
            exporter: (a: AP) => {
                return reminderStatusForExcel({
                    ...a,
                    messagePurpose: FollowupMessagePurpose.APPOINTMENT_REMINDER,
                });
            },
        },
        FLW_REMINDER_STATUS: {
            title: 'Reminder Status',
            exporter: (a: AP) => {
                return reminderStatusForExcel(a);
            },
        },
        WAIT_TIME: {
            title: 'Wait Time',
            exporter: (a: AP) => {
                if (!a.visit_checkin) {
                    return '--';
                }

                const checkinTime = moment(a.visit_checkin);
                const emdTime = a.visit_end ? moment(a.visit_end) : moment();

                const minutes = emdTime.diff(checkinTime, 'minutes') % 60;
                const hours = emdTime.diff(checkinTime, 'hours');

                return `${hours ? `${hours}h ` : ''}${minutes}m`;
            },
        },
        PAYMENT_STATUS: {
            title: 'Payment Status',
            exporter: (a: AP) => {
                return a.billStatus || '';
            },
        },
        AMOUNT: {
            title: 'Amount',
            exporter: (a: AP) => {
                return a.billTotal ? a.billTotal : '';
            },
        },
        SA_STATUS: {
            title: 'Assesment Status',
            exporter: (a: AP) => {
                return a.sa_id ? 'Filled' : 'Not Filled';
            },
        },
        FOLLOW_UP: {
            title: 'Follow Up',
            exporter: (a: AP) => {
                if (a.follow_up) {
                    return moment(a.follow_up).format('Do MMM YY');
                }

                return '';
            },
        },
    };

    state: State = {
        searchInput: '',
        appointments: [],
        todayAppointments: [],
        isLoading: true,
        isTodayLoading: true,
        activeFilterIndex: [],
        filteredVisitType: new Set(),
        filteredTags: new Set(),
        appointmentConfigs: {
            tags: [],
            user_preferences: getDefaultColumnPreference(),
            eligibile_for_payments: true,
        },
        selectedQueueWindowDates: null,
        showFollowupFeaturesModal: false,
        showFollowupDateSelectorFor: undefined,
        isAppointmentsExporting: false,
        tokenSortPreference: 'NONE',
        todayQueueOptions: [
            {
                priority: 0,
                key: 'SQN',
                renderer: this.C_SEQ,
            },
            {
                priority: 1,
                key: 'TOKN',
                renderer: this.TOKEN_RENDERER,
                exportInExcel: this.excelHelper.TOKEN,
            },
            {
                priority: 2,
                key: 'PDT',
                renderer: this.C_PATIENT_DETAILS_TODAY,
                exportInExcel: this.excelHelper.PATIENT_DETAILS,
            },
            {
                priority: 3,
                key: 'PTAG',
                renderer: this.C_ABOUT_PATIENT,
                exportInExcel: this.excelHelper.ABOUT_PATIENT,
            },
            {
                priority: 4,
                key: 'UHID',
                renderer: this.C_UHID,
                exportInExcel: this.excelHelper.UHID,
            },
            {
                priority: 5,
                key: 'AHID',
                renderer: this.C_HEALTH_ID,
                exportInExcel: this.excelHelper.HEALTH_ID,
            },
            {
                priority: 6,
                key: 'TAGS',
                renderer: this.C_TAGS,
                exportInExcel: this.excelHelper.TAGS,
            },
            {
                priority: 7,
                key: 'CON',
                renderer: this.C_CONTACT,
                exportInExcel: this.excelHelper.CONTACT,
            },
            {
                priority: 8,
                key: 'VITY',
                renderer: this.C_VISIT_TYPE,
                exportInExcel: this.excelHelper.VISIT_TYPE,
            },
            {
                priority: 9,
                key: 'SLOT',
                renderer: this.C_SLOT,
                exportInExcel: this.excelHelper.SLOT,
            },
            {
                priority: 10,
                key: 'VIST',
                renderer: this.C_VISIT_STATUS,
                exportInExcel: this.excelHelper.VISIT_STATUS,
            },
            {
                priority: 11,
                key: 'ARRE',
                renderer: this.C_REMINDER_RESPONSE,
                exportInExcel: this.excelHelper.APT_REMINDER_RESPONSE,
            },
            {
                priority: 12,
                key: 'ARST',
                renderer: this.C_APT_REMINDER_STATUS,
                exportInExcel: this.excelHelper.APT_REMINDER_STATUS,
            },
            {
                priority: 13,
                key: 'WAIT',
                renderer: this.C_WAITING_TIME,
                exportInExcel: this.excelHelper.WAIT_TIME,
            },
            {
                priority: 14,
                key: 'PAYS',
                renderer: this.C_PAYMENT_STATUS,
                exportInExcel: this.excelHelper.PAYMENT_STATUS,
            },
            {
                priority: 15,
                key: 'AMOU',
                renderer: this.C_AMOUNT,
                exportInExcel: this.excelHelper.AMOUNT,
            },
            {
                priority: 16,
                key: 'ASST',
                renderer: this.C_ASSESSMENT_STATUS,
                exportInExcel: this.excelHelper.SA_STATUS,
            },
            {
                priority: 17,
                key: 'ACTS',
                renderer: this.C_ACTIONS,
                required: true,
            },
        ],
        completedQueueOptions: [
            {
                priority: 0,
                key: 'TOKN',
                renderer: this.TOKEN_RENDERER,
                exportInExcel: this.excelHelper.TOKEN,
            },
            {
                priority: 1,
                key: 'PDT',
                renderer: this.C_PATIENT_DETAILS_WITHOUT_COUNT,
                exportInExcel: this.excelHelper.PATIENT_DETAILS,
            },
            {
                priority: 2,
                key: 'PTAG',
                renderer: this.C_ABOUT_PATIENT,
                exportInExcel: this.excelHelper.ABOUT_PATIENT,
            },
            {
                priority: 3,
                key: 'UHID',
                renderer: this.C_UHID,
                exportInExcel: this.excelHelper.UHID,
            },
            {
                priority: 4,
                key: 'AHID',
                renderer: this.C_HEALTH_ID,
                exportInExcel: this.excelHelper.HEALTH_ID,
            },
            {
                priority: 5,
                key: 'TAGS',
                renderer: this.C_TAGS,
                exportInExcel: this.excelHelper.TAGS,
            },
            {
                priority: 6,
                key: 'CON',
                renderer: this.C_CONTACT,
                exportInExcel: this.excelHelper.CONTACT,
            },
            {
                priority: 7,
                key: 'VITY',
                renderer: this.C_VISIT_TYPE,
                exportInExcel: this.excelHelper.VISIT_TYPE,
            },
            {
                priority: 8,
                key: 'ASST',
                renderer: this.C_ASSESSMENT_STATUS,
                exportInExcel: this.excelHelper.SA_STATUS,
            },
            {
                priority: 9,
                key: 'PAYS',
                renderer: this.C_PAYMENT_STATUS,
                exportInExcel: this.excelHelper.PAYMENT_STATUS,
            },
            {
                priority: 10,
                key: 'AMOU',
                renderer: this.C_AMOUNT,
                exportInExcel: this.excelHelper.AMOUNT,
            },
            {
                priority: 11,
                key: 'FLWP',
                renderer: this.C_FOLLOWUP,
                exportInExcel: this.excelHelper.FOLLOW_UP,
            },
            {
                priority: 12,
                key: 'ACTS',
                renderer: this.C_COMPLETED_ACTIONS,
                required: true,
            },
        ],
        upcomingQueueOptions: [
            {
                priority: 0,
                key: 'SQN',
                renderer: this.C_SEQ,
            },
            {
                priority: 1,
                key: 'TOKN',
                renderer: this.TOKEN_RENDERER,
                exportInExcel: this.excelHelper.TOKEN,
            },
            {
                priority: 2,
                key: 'PDT',
                renderer: this.C_PATIENT_DETAILS_UPCOMING,
                exportInExcel: this.excelHelper.PATIENT_DETAILS,
            },
            {
                priority: 3,
                key: 'PTAG',
                renderer: this.C_ABOUT_PATIENT,
                exportInExcel: this.excelHelper.ABOUT_PATIENT,
            },
            {
                priority: 4,
                key: 'UHID',
                renderer: this.C_UHID,
                exportInExcel: this.excelHelper.UHID,
            },
            {
                priority: 5,
                key: 'AHID',
                renderer: this.C_HEALTH_ID,
                exportInExcel: this.excelHelper.HEALTH_ID,
            },
            {
                priority: 6,
                key: 'TAGS',
                renderer: this.C_TAGS,
                exportInExcel: this.excelHelper.TAGS,
            },
            {
                priority: 7,
                key: 'CON',
                renderer: this.C_CONTACT,
                exportInExcel: this.excelHelper.CONTACT,
            },
            {
                priority: 8,
                key: 'VITY',
                renderer: this.C_VISIT_TYPE,
                exportInExcel: this.excelHelper.VISIT_TYPE,
            },
            {
                priority: 9,
                key: 'SLOT',
                renderer: this.C_SLOT,
                exportInExcel: this.excelHelper.SLOT,
            },
            {
                priority: 10,
                key: 'VIST',
                renderer: this.C_VISIT_STATUS,
                exportInExcel: this.excelHelper.VISIT_STATUS,
            },
            {
                priority: 11,
                key: 'ARRE',
                renderer: this.C_REMINDER_RESPONSE,
                exportInExcel: this.excelHelper.APT_REMINDER_RESPONSE,
            },
            {
                priority: 12,
                key: 'ARST',
                renderer: this.C_APT_REMINDER_STATUS,
                exportInExcel: this.excelHelper.APT_REMINDER_STATUS,
            },
            {
                priority: 13,
                key: 'PAYS',
                renderer: this.C_PAYMENT_STATUS,
                exportInExcel: this.excelHelper.PAYMENT_STATUS,
            },
            {
                priority: 14,
                key: 'AMOU',
                renderer: this.C_AMOUNT,
                exportInExcel: this.excelHelper.AMOUNT,
            },
            {
                priority: 15,
                key: 'ACTS',
                renderer: this.C_ACTIONS,
                required: true,
            },
        ],
        othersQueueOptions: [
            {
                priority: 0,
                key: 'TOKN',
                renderer: this.TOKEN_RENDERER,
                exportInExcel: this.excelHelper.TOKEN,
            },
            {
                priority: 1,
                key: 'PDT',
                renderer: this.C_PATIENT_DETAILS_WITHOUT_COUNT,
                exportInExcel: this.excelHelper.PATIENT_DETAILS,
            },
            {
                priority: 2,
                key: 'PTAG',
                renderer: this.C_ABOUT_PATIENT,
                exportInExcel: this.excelHelper.ABOUT_PATIENT,
            },
            {
                priority: 3,
                key: 'UHID',
                renderer: this.C_UHID,
                exportInExcel: this.excelHelper.UHID,
            },
            {
                priority: 4,
                key: 'AHID',
                renderer: this.C_HEALTH_ID,
                exportInExcel: this.excelHelper.HEALTH_ID,
            },
            {
                priority: 5,
                key: 'TAGS',
                renderer: this.C_TAGS,
                exportInExcel: this.excelHelper.TAGS,
            },
            {
                priority: 6,
                key: 'CON',
                renderer: this.C_CONTACT,
                exportInExcel: this.excelHelper.CONTACT,
            },
            {
                priority: 7,
                key: 'VITY',
                renderer: this.C_VISIT_TYPE,
                exportInExcel: this.excelHelper.VISIT_TYPE,
            },
            {
                priority: 8,
                key: 'SLOT',
                renderer: this.C_SLOT,
                exportInExcel: this.excelHelper.SLOT,
            },
            {
                priority: 9,
                key: 'VIST',
                renderer: this.C_VISIT_STATUS,
                exportInExcel: this.excelHelper.VISIT_STATUS,
            },
            {
                priority: 10,
                key: 'ARRE',
                renderer: this.C_REMINDER_RESPONSE,
                exportInExcel: this.excelHelper.APT_REMINDER_RESPONSE,
            },
            {
                priority: 11,
                key: 'ARST',
                renderer: this.C_APT_REMINDER_STATUS,
                exportInExcel: this.excelHelper.APT_REMINDER_STATUS,
            },
            {
                priority: 12,
                key: 'PAYS',
                renderer: this.C_PAYMENT_STATUS,
                exportInExcel: this.excelHelper.PAYMENT_STATUS,
            },
            {
                priority: 13,
                key: 'ASST',
                renderer: this.C_ASSESSMENT_STATUS,
                exportInExcel: this.excelHelper.SA_STATUS,
            },
            {
                priority: 14,
                key: 'ACTS',
                renderer: this.C_ACTIONS,
                required: true,
            },
        ],
        followupQueueOptions: [
            {
                priority: 0,
                key: 'SQN',
                renderer: this.C_SEQ,
            },
            {
                priority: 1,
                key: 'TOKN',
                renderer: this.TOKEN_RENDERER,
                exportInExcel: this.excelHelper.TOKEN,
            },
            {
                priority: 2,
                key: 'PDT',
                renderer: this.C_PATIENT_DETAILS_UPCOMING,
                exportInExcel: this.excelHelper.PATIENT_DETAILS,
            },
            {
                priority: 3,
                key: 'PTAG',
                renderer: this.C_ABOUT_PATIENT,
                exportInExcel: this.excelHelper.ABOUT_PATIENT,
            },
            {
                priority: 4,
                key: 'UHID',
                renderer: this.C_UHID,
                exportInExcel: this.excelHelper.UHID,
            },
            {
                priority: 5,
                key: 'AHID',
                renderer: this.C_HEALTH_ID,
                exportInExcel: this.excelHelper.HEALTH_ID,
            },
            {
                priority: 6,
                key: 'TAGS',
                renderer: this.C_TAGS,
                exportInExcel: this.excelHelper.TAGS,
            },
            {
                priority: 7,
                key: 'CON',
                renderer: this.C_CONTACT,
                exportInExcel: this.excelHelper.CONTACT,
            },
            {
                priority: 8,
                key: 'SLOT',
                renderer: this.C_SLOT,
                exportInExcel: this.excelHelper.SLOT,
            },
            {
                priority: 9,
                key: 'VIST',
                renderer: this.C_VISIT_STATUS,
                exportInExcel: this.excelHelper.VISIT_STATUS,
            },
            {
                priority: 10,
                key: 'PAYS',
                renderer: this.C_PAYMENT_STATUS,
                exportInExcel: this.excelHelper.PAYMENT_STATUS,
            },
            {
                priority: 11,
                key: 'AMOU',
                renderer: this.C_AMOUNT,
                exportInExcel: this.excelHelper.AMOUNT,
            },
            {
                priority: 12,
                key: 'RECO',
                renderer: this.C_REMINDERS_STATUS,
                exportInExcel: this.excelHelper.FLW_REMINDER_STATUS,
            },
            {
                priority: 13,
                key: 'ACTS',
                renderer: this.C_ACTIONS,
                required: true,
            },
        ],
        filteredAppointmentList: [],
        isFiltered: false,
        ...(this.props.date && this.props.section != 'today-queue'
            ? {
                  queueWindowType: 'custom',
                  customQueueWindowDate: [
                      moment(this.props.date).format('YYYY-MM-DD'),
                      moment(this.props.date).format('YYYY-MM-DD'),
                  ],
              }
            : {}),
    };

    polling?: NodeJS.Timeout = undefined;
    appointmentsFuse = new Fuse([] as AP[], fuseConfig);
    visitTypeMap: Set<string> = new Set();

    todaysDate = new Date();

    getQueueWindowDurationsBySection = (
        section: APP_VALID_SECTIONS,
    ): SectionData['queueWindowDurations'] => {
        switch (section) {
            case 'today-queue': {
                return [];
            }
            case 'completed': {
                return [
                    {
                        type: 'today',
                        label: 'Today',
                    },
                    {
                        type: 'last-week',
                        label: 'Last 7 Days',
                    },
                    {
                        type: 'custom',
                        label: 'Custom',
                    },
                ];
            }
            case 'upcoming': {
                return [
                    {
                        type: 'tomorrow',
                        label: 'Tomorrow',
                    },
                    {
                        type: 'next-week',
                        label: 'Next 7 Days',
                    },
                    {
                        type: 'custom',
                        label: 'Custom',
                    },
                ];
            }
            case 'others': {
                return [
                    {
                        type: 'today',
                        label: 'Today',
                    },
                    {
                        type: 'last-week',
                        label: 'Last 7 Days',
                    },
                    {
                        type: 'custom',
                        label: 'Custom',
                    },
                ];
            }
            case 'follow-ups': {
                return [
                    { type: 'today', label: 'Today' },
                    { type: 'next-week', label: 'Next 7 Days' },
                    {
                        type: 'custom',
                        label: 'Custom',
                    },
                ];
            }
            default: {
                return [];
            }
        }
    };

    getSectionData = (sectionName: string): Omit<SectionData, 'renderBoxContent'> | undefined => {
        switch (sectionName) {
            case 'today-queue': {
                return {
                    index: 0,
                    label: 'Today Queue',
                    api: fetchTodayQueue,
                    showDateReperator: false,
                    tableClassName: 'appointments-table',
                    filters: [this.F_NOT_FILED_SA, this.F_UNBILLED],
                    queueWindowDurations: this.getQueueWindowDurationsBySection('today-queue'),
                    columns: this.state.todayQueueOptions.reduce<QueueColumnOptions>(
                        (prev, curr) => {
                            if (
                                this.state.appointmentConfigs.user_preferences[
                                    todayQueueColumnKeys
                                ].includes(curr.key)
                            ) {
                                prev.push(curr);
                            }
                            return prev;
                        },
                        [],
                    ),
                    searchPlaceholder: 'Search today’s queue',
                    emptyText: 'There are no patients in your queue right now',
                };
            }
            case 'completed': {
                return {
                    index: 2,
                    label: 'completed',
                    filters: [this.F_NOT_FILED_SA, this.F_UNBILLED],
                    columns: this.state.completedQueueOptions.reduce<QueueColumnOptions>(
                        (prev, curr) => {
                            if (
                                this.state.appointmentConfigs.user_preferences[
                                    completedQueueColumnKeys
                                ].includes(curr.key)
                            ) {
                                prev.push(curr);
                            }
                            return prev;
                        },
                        [],
                    ),
                    defaultWindowDuration: 'today',
                    queueWindowDurations: this.getQueueWindowDurationsBySection('completed'),
                    api: fetchCompletedAllTime,
                    showDateReperator: true,
                    searchPlaceholder: 'Search completed appts',
                    tableClassName: 'completed-appointments-table',
                    emptyText: 'You have not completed any visits yet',
                };
            }
            case 'upcoming': {
                return {
                    index: 3,
                    label: 'upcoming',
                    defaultWindowDuration: 'tomorrow',
                    queueWindowDurations: this.getQueueWindowDurationsBySection('upcoming'),
                    filters: [this.F_NOT_FILED_SA, this.F_UNBILLED],
                    columns: this.state.upcomingQueueOptions.reduce<QueueColumnOptions>(
                        (prev, curr) => {
                            if (
                                this.state.appointmentConfigs.user_preferences[
                                    upcomingQueueColumnKeys
                                ].includes(curr.key)
                            ) {
                                prev.push(curr);
                            }
                            return prev;
                        },
                        [],
                    ),
                    tableClassName: 'appointments-table',
                    api: fetchUpcomingAppointmentAllTime,
                    showDateReperator: true,
                    emptyText: 'There are no patients in your queue right now',
                    searchPlaceholder: 'Search upcoming appts',
                };
            }
            case 'others': {
                return {
                    index: 4,
                    label: 'others',
                    defaultWindowDuration: 'today',
                    queueWindowDurations: this.getQueueWindowDurationsBySection('others'),
                    filters: [this.F_CANCELLED, this.F_ABORTED],
                    columns: this.state.othersQueueOptions.reduce<QueueColumnOptions>(
                        (prev, curr) => {
                            if (
                                this.state.appointmentConfigs.user_preferences[
                                    othersQueueColumnKeys
                                ].includes(curr.key)
                            ) {
                                prev.push(curr);
                            }
                            return prev;
                        },
                        [],
                    ),
                    tableClassName: 'appointments-table',
                    api: fetchOtherAppointmentAllTime,
                    showDateReperator: true,
                    emptyText:
                        'Nothing here! Any visit that you cancel, abort or is left incomplete sits here',
                    searchPlaceholder: 'Search other appts',
                };
            }
            case 'follow-ups': {
                return {
                    index: 1,
                    label: 'Follow up Queue',
                    defaultWindowDuration: 'today',
                    api: fetchFollowupQueue,
                    showDateReperator: true,
                    tableClassName: 'appointments-table',
                    filters: [
                        this.F_PENDING_CONFIRM,
                        this.F_VISIT_CONFIRMED,
                        this.F_NOT_COMING,
                        this.F_RESCEDHULED,
                    ],
                    queueWindowDurations: this.getQueueWindowDurationsBySection('follow-ups'),
                    columns: this.state.followupQueueOptions.reduce<QueueColumnOptions>(
                        (prev, curr) => {
                            if (
                                this.state.appointmentConfigs.user_preferences[
                                    followupQueueColumnKeys
                                ].includes(curr.key)
                            ) {
                                prev.push(curr);
                            }
                            return prev;
                        },
                        [],
                    ),
                    searchPlaceholder: 'Search follow up queue',
                    emptyText: 'There are no follow ups queue right now',
                };
            }
        }
    };

    getAllColumnsForQueueType = (sectionName: string): QueueColumnOptions => {
        switch (sectionName) {
            case 'today-queue': {
                return this.state.todayQueueOptions.reduce<QueueColumnOptions>((prev, curr) => {
                    prev.push(curr);
                    return prev;
                }, []);
            }
            case 'completed': {
                return this.state.completedQueueOptions.reduce<QueueColumnOptions>((prev, curr) => {
                    prev.push(curr);
                    return prev;
                }, []);
            }
            case 'upcoming': {
                return this.state.upcomingQueueOptions.reduce<QueueColumnOptions>((prev, curr) => {
                    prev.push(curr);
                    return prev;
                }, []);
            }
            case 'others': {
                return this.state.othersQueueOptions.reduce<QueueColumnOptions>((prev, curr) => {
                    prev.push(curr);
                    return prev;
                }, []);
            }
            case 'follow-ups': {
                return this.state.followupQueueOptions.reduce<QueueColumnOptions>((prev, curr) => {
                    prev.push(curr);
                    return prev;
                }, []);
            }
            default: {
                return [];
            }
        }
    };

    getCurrentSectionData = () => {
        const currentSection = this.props.section;
        return this.getSectionData(currentSection);
    };

    getUsedQueueWindowLabelInASection = (sectionName: string) => {
        const { queueWindowDurations, defaultWindowDuration } =
            this.getSectionData(sectionName) || {};

        const { queueWindowType = defaultWindowDuration } = this.state;

        const usedWindowType = queueWindowDurations?.find(({ type }) => type === queueWindowType);

        return ` (${
            usedWindowType?.type === 'custom' ? 'Custom Duration' : usedWindowType?.label
        }) `;
    };

    tabSections: {
        [k in APP_VALID_SECTIONS]: {
            index: number;
            label: string;
            renderBoxContent: (countResponse?: CountResponse) => ReactNode;
        };
    } = {
        'today-queue': {
            index: 0,
            label: 'Today Queue',
            renderBoxContent: (countResponse?: CountResponse) => (
                <Fragment>
                    <p className="text-13 label">
                        <span className="label-meta">Today’s queue </span>
                        <span>(24hrs)</span>
                    </p>
                    {countResponse?.today ? (
                        <div className="flex items-baseline justify-between">
                            <p className="text-18 info-count">{countResponse?.today?.count}</p>
                            <p className="text-10 uppercase font-700 text-status-warning">
                                <span>SA Not filled: </span>
                                <span>
                                    {countResponse?.today?.saCount !== undefined
                                        ? this.state.todayAppointments.length -
                                          countResponse?.today?.saCount
                                        : '--'}
                                </span>
                            </p>
                        </div>
                    ) : (
                        <div className="h-16 w-full shimmer"></div>
                    )}
                </Fragment>
            ),
        },
        'follow-ups': {
            index: 1,
            label: 'Follow ups',
            renderBoxContent: (countResponse?: CountResponse) => {
                return (
                    <Fragment>
                        <p className="text-13 label">
                            <div className="flex items-center justify-between space-x-8">
                                <span className="label-meta">Follow ups</span>
                                {/* <FeatureTag
                                    className={
                                        'bg-darwin-accent-medical-history-orange-800 text-white py-1 px-5 text-11 leading-none rounded-4 select-none'
                                    }
                                    text={'NEW'}
                                /> */}
                            </div>
                        </p>
                        {countResponse?.followup ? (
                            <div className="flex items-baseline justify-between">
                                {countResponse?.followup?.count ||
                                countResponse?.followup?.count === 0 ? (
                                    <span className="text-18 info-count">
                                        <span> {countResponse?.followup?.count}</span>
                                        <span className="text-12 text-neutral-1">
                                            {this.getUsedQueueWindowLabelInASection('follow-ups')}
                                        </span>
                                    </span>
                                ) : (
                                    <p className="text-12 text-neutral-1">{'(Tap to refresh)'}</p>
                                )}
                            </div>
                        ) : (
                            <div className="h-16 w-full shimmer"></div>
                        )}
                    </Fragment>
                );
            },
        },
        completed: {
            index: 2,
            label: 'completed',
            renderBoxContent: (countResponse?: CountResponse) => (
                <Fragment>
                    <p className="text-13 label">
                        <span className="label-meta">Completed </span>
                    </p>
                    {countResponse?.completed ? (
                        <div className="flex items-baseline justify-between">
                            {countResponse?.completed?.count ||
                            countResponse?.completed.count === 0 ? (
                                <span className="text-18 info-count">
                                    <span> {countResponse?.completed?.count}</span>
                                    <span className="text-12 text-neutral-1">
                                        {this.getUsedQueueWindowLabelInASection('completed')}
                                    </span>
                                </span>
                            ) : (
                                <p className="text-12 text-neutral-1">{'(Tap to refresh)'}</p>
                            )}
                        </div>
                    ) : (
                        <div className="h-16 w-full shimmer"></div>
                    )}
                </Fragment>
            ),
        },
        upcoming: {
            index: 3,
            label: 'upcoming',
            renderBoxContent: (countResponse?: CountResponse) => {
                return (
                    <Fragment>
                        <p className="text-13 label">
                            <span className="label-meta">Upcoming </span>
                        </p>
                        {countResponse?.upcoming ? (
                            <div className="flex items-baseline justify-between">
                                {countResponse?.upcoming?.count ||
                                countResponse?.upcoming?.count === 0 ? (
                                    <span className="text-18 info-count">
                                        <span> {countResponse?.upcoming?.count}</span>
                                        <span className="text-12 text-neutral-1">
                                            {this.getUsedQueueWindowLabelInASection('upcoming')}
                                        </span>
                                    </span>
                                ) : (
                                    <p className="text-12 text-neutral-1">{'(Tap to refresh)'}</p>
                                )}
                            </div>
                        ) : (
                            <div className="h-16 w-full shimmer"></div>
                        )}
                    </Fragment>
                );
            },
        },
        others: {
            index: 4,
            label: 'others',
            renderBoxContent: (countResponse?: CountResponse) => (
                <Fragment>
                    <p className="text-13 label">
                        <span className="label-meta">Others </span>
                    </p>
                    {countResponse?.other ? (
                        <div className="flex items-baseline justify-between">
                            {countResponse?.other?.count || countResponse?.other.count === 0 ? (
                                <span className="text-18 info-count">
                                    <span>{countResponse?.other?.count}</span>
                                    <span className="text-12 text-neutral-1">
                                        {this.getUsedQueueWindowLabelInASection('others')}
                                    </span>
                                </span>
                            ) : (
                                <p className="text-12 text-neutral-1">{'(Tap to refresh)'}</p>
                            )}
                        </div>
                    ) : (
                        <div className="h-16 w-full shimmer"></div>
                    )}
                </Fragment>
            ),
        },
    };

    onTagSelect = (tagId: number): void => {
        // console.log('Tag Id Selected', tagId);
        const currentTagSet = this.state.filteredTags;
        const newTagSet = new Set([...currentTagSet, tagId]);
        this.setState({ filteredTags: newTagSet });
    };

    onTagDeselect = (tagId: number): void => {
        // console.log('Tag Id Deselected', tagId);
        const currentTagSet = this.state.filteredTags;
        const newTagSet = new Set(currentTagSet);
        newTagSet.delete(tagId);
        this.setState({ filteredTags: newTagSet });
    };

    removeOrAddTagOfAptInApptList = (
        aptList: AP[],
        tagId: number,
        aid: string,
    ): { data: AP[]; action: TagLinkAction } => {
        let action: TagLinkAction = 'NONE';

        const appointmentList = aptList.map((apt) => {
            const { aid: aptId, tags } = apt;

            if (aptId === aid) {
                const tagsSet = new Set(tags);
                if (tagsSet.has(tagId)) {
                    action = 'DELETING';
                    tagsSet.delete(tagId);
                } else {
                    tagsSet.add(tagId);
                    action = 'ADDING';
                }

                return { ...apt, tags: [...tagsSet] };
            }

            return apt;
        });

        return { data: appointmentList, action };
    };

    getUpdatedAppointmentsWithNewTag = (
        aid: string,
        tagId: number,
    ): Pick<State, 'todayAppointments' | 'appointments' | 'appointmentConfigs'> => {
        const { data: newAptsList, action: aptAction } = this.removeOrAddTagOfAptInApptList(
            this.state.appointments ?? [],
            tagId,
            aid,
        );

        const {
            data: newTodayAptsList,
            action: todayAptAction,
        } = this.removeOrAddTagOfAptInApptList(this.state.todayAppointments ?? [], tagId, aid);

        linkTagWithApt(getMe()?.business?.id || '', tagId, aid);

        const allAptTags = this.state.appointmentConfigs.tags.map((tagData) => {
            if (tagData.tag_id === tagId) {
                const { count } = tagData;

                let newCount = count;

                if (aptAction === 'ADDING' || todayAptAction === 'ADDING') {
                    newCount += 1;
                } else if (aptAction === 'DELETING' || todayAptAction === 'DELETING') {
                    newCount -= 1;
                }

                return { ...tagData, count: newCount };
            }

            return tagData;
        });

        return {
            todayAppointments: newTodayAptsList,
            appointments: newAptsList,
            appointmentConfigs: {
                ...this.state.appointmentConfigs,
                tags: allAptTags,
            },
        };
    };

    onTagCreation = async (
        tagRecord: CreateTagBody,
        aid?: string,
    ): Promise<{
        success: boolean;
        data?: TagRecord | undefined;
        error?: string | undefined;
    }> => {
        track('TAG_CREATED', { label: tagRecord.label });

        const tagCreationResponse = await createAptTag(
            this.myProfile?.business.id,
            tagRecord,
        ).catch(() => ({
            success: false,
            error: 'Failed to create tag',
            data: undefined,
        }));

        const { success, data } = tagCreationResponse;

        if (success && data) {
            const currentAptConfigs = this.state.appointmentConfigs;
            const currentTags = currentAptConfigs.tags;
            const updatedTags = [...currentTags, { ...data, count: 1 }];

            const updatedAptConfigs = { ...currentAptConfigs, tags: updatedTags };

            const updatedAppointments = !!aid
                ? this.getUpdatedAppointmentsWithNewTag(aid, data.tag_id)
                : {};

            this.setState({
                ...updatedAppointments,
                appointmentConfigs: updatedAptConfigs,
            });
        }
        return tagCreationResponse;
    };

    toggleTagForAnAppointment = (aid: string, tagId: number): void => {
        track('TAG_TOGGLED', { aid });
        const {
            appointments,
            todayAppointments,
            appointmentConfigs,
        } = this.getUpdatedAppointmentsWithNewTag(aid, tagId);

        this.setState({
            appointments: appointments,
            todayAppointments: todayAppointments,
            appointmentConfigs,
        });
    };

    onCreateTemplate = (a: AP): void => {
        onCreateTemplateAction(a);
    };

    closeCreateTemplateDialog = (): void => {
        this.setState({
            createTemplateAppointment: undefined,
        });
    };

    changeFollowupDate = async (aid: string, date: string) => {
        track('CHANGE_FOLLOWUP_DATE', { aid });
        //make api call;

        const { success } = await changeFollowupDateOfApt(aid, date).catch((e) => {
            console.log('CHANGE FOLLOWUP DATE', e);
            return { success: false };
        });

        if (success) {
            const updatedAppointments = this.state.appointments.map((apt) => {
                if (apt.aid === aid) {
                    return { ...apt, follow_up: date } as AP;
                }
                return apt;
            });
            this.setState({
                appointments: updatedAppointments,
                showFollowupDateSelectorFor: undefined,
            });
            notification.success({
                message: 'Added follow up date and created the follow up appointment successfully',
                placement: 'bottomRight',
            });
        } else {
            notification.error({
                message: 'Failed to add followup date',
                placement: 'bottomRight',
            });
        }
    };

    renderInitatePaymentDialog = (initDialogPayment: AP): JSX.Element => {
        const { receipts, patient } = initDialogPayment;
        return (
            <Menu>
                <div className={`${!initDialogPayment.can_pay ? 'opacity-40' : ''}`}>
                    <OptionItem
                        onClick={(e) => {
                            e.stopPropagation();
                            if (!initDialogPayment.can_pay) {
                                return;
                            }
                            this.onRequestPayment(initDialogPayment);
                        }}
                        label={initDialogPayment.can_pay ? 'Request Payment' : `Already requested`}
                    />
                </div>
                <OptionItem
                    onClick={(e) => {
                        e.stopPropagation();
                        this.onSendInvoice(initDialogPayment);
                    }}
                    label={'Bill Patient'}
                />
                {receipts.length > 0 ? (
                    <OptionItem
                        onClick={(e) => {
                            e.stopPropagation();
                            this.onViewReceipts(initDialogPayment);
                        }}
                        label={'View Receipts'}
                    />
                ) : null}
                {patient.amount_pending ? (
                    <OptionItem
                        onClick={(e) => {
                            e.stopPropagation();
                            onSettleDuePopupAction(initDialogPayment);
                        }}
                        label={`Clear Dues (₹${patient.amount_pending})`}
                    />
                ) : null}
            </Menu>
        );
    };

    renderHEOptions = (appt: AP): JSX.Element => {
        return (
            <Menu>
                <OptionItem
                    onClick={() => this.showOrderMedicineDialogVisible(appt)}
                    label={'Order Medicines'}
                />

                <OptionItem
                    onClick={() => this.showBookLabTestDialogVisible(appt)}
                    label={'Book Lab Tests'}
                />
            </Menu>
        );
    };

    refreshAppointmentConfigs = async (appointments: AP[]) => {
        const getAppointmentConfigPromise = fetchApptConfigs(getMe()?.business?.id || '').catch(
            (e) => {
                console.log(e);
            },
        );

        const aptConfigResponse = await getAppointmentConfigPromise;

        if (!aptConfigResponse) {
            console.error('Failed to get appt config');
            return;
        }

        const { data } = aptConfigResponse;

        const tagsCountMap: Record<number, number> = {};

        appointments.forEach((apt) => {
            apt.tags?.forEach((tagId) => {
                tagsCountMap[tagId] = (tagsCountMap[tagId] ?? 0) + 1;
            });
        });

        if (data) {
            data.tags.sort((l, r) => l.label.localeCompare(r.label));

            this.setState({
                appointmentConfigs: {
                    ...this.state.appointmentConfigs,
                    user_preferences: {
                        ...this.state.appointmentConfigs.user_preferences,
                        ...data.user_preferences,
                    },
                    tags: data?.tags.map((tagData) => {
                        const { tag_id } = tagData;
                        return { ...tagData, count: tagsCountMap[tag_id] ?? 0 };
                    }),
                },
            });
        }
    };

    onEkaEvents: EventListener = (e: Event): void => {
        const { detail } = e as CustomEvent<IncomingEvents>;
        switch (detail.type) {
            case REFRESH_APPT:
                this.hardRefresh();
                break;
        }
    };

    componentDidMount(): void {
        console.log('Appointment module loaded');
        this.getAllAppointments();
        window.addEventListener(EKA_EVENT, this.onEkaEvents);

        isCanOrderMedicine().then((canOrder) => {
            this.setState({
                canOrderMedicine: canOrder,
            });
        });

        isCanBookLabTest().then((canOrder) => {
            this.setState({
                canBookLabTest: canOrder,
            });
        });
    }

    componentDidUpdate(prevProps: Props): void {
        const hasCidOrSectionChanged =
            this.props.cId !== prevProps.cId || this.props.section !== prevProps.section;

        if (this.props.cId !== prevProps.cId) {
            //if clinic change -> reset selectedDoc
            this.setState({ selectedDoctor: undefined });
        }
        if (hasCidOrSectionChanged) {
            this.hardRefresh({ clearQueueWindow: hasCidOrSectionChanged });
            this.removeFilter();
        }
    }

    componentWillUnmount = (): void => {
        if (this.polling) {
            clearInterval(this.polling);
        }

        window.removeEventListener(EKA_EVENT, this.onEkaEvents);
    };

    getStateUpdatesForSelectedSection = async (sectionData: AppointmentsResponse | void) => {
        if (!sectionData?.response?.apt) {
            return {};
        }

        const list = sectionData.response.apt.filter((t) => t.patient);
        const visitIds = list.map((a) => a.aid);
        const localVisits = await getLocalVisitByIds(visitIds);
        const localContextList: AP[] = list.map((a, index) => ({
            ...a,
            isLocalPresnt: localVisits[index] !== undefined,
            serialNum: index + 1,
            localRxId: localVisits[index]?.prescriptionId,
        }));

        this.visitTypeMap = new Set(list.map((l) => `${l['type-display']}`).filter(Boolean));

        //  TODO: update type here, it's not partial
        const stateUpdates: Partial<State> = {
            appointments: localContextList
                ?.map((apt, idx) => ({ ...apt, serialNum: idx + 1 }))
                ?.sort(sortComparator(this.state.tokenSortPreference)),
            todayAppointments:
                this.props.section === 'today-queue'
                    ? localContextList
                    : this.state.todayAppointments,
            isTodayLoading:
                this.props.section === 'today-queue' ? false : this.state.isTodayLoading,
            countResponse: {
                today: sectionData.response.countResponse?.today,
                completed: {},
                upcoming: {},
                other: {},
                followup: {},
            },
            totalCountResponse: sectionData.response.countResponse,
        };

        if (this.props.section !== 'today-queue') {
            let key: keyof CountResponse | undefined;
            const count = localContextList.length;

            switch (this.props.section) {
                case 'completed':
                    key = 'completed';
                    break;
                case 'upcoming':
                    key = 'upcoming';
                    break;
                case 'others':
                    key = 'other';
                    break;
                case 'follow-ups':
                    key = 'followup';
                    break;
            }

            if (key) {
                stateUpdates.countResponse = {
                    ...(this.state.countResponse || {}),
                    upcoming: {},
                    other: {},
                    completed: {},
                    followup: {},
                    [key]: {
                        count: count,
                    },
                };
            }
        }

        this.appointmentsFuse = new Fuse(
            localContextList.map((l) => ({
                ...l,

                _name: getPatientNameText(l.patient),
                _phone: l.patient?.mobile,
                _uhid: l.formData?.find((f) => f.key === 'uhid')?.value,
            })),
            fuseConfig,
        );

        return stateUpdates;
    };

    getDateToPoll = (
        queueWindowType: QueueWindowDurationType,
        customDateRange?: [string | undefined, string | undefined],
    ): { startDate?: string; endDate?: string } => {
        const today = new Date();
        const formatString = 'yyyy-MM-DD';
        let startDate: string, endDate: string;

        switch (queueWindowType) {
            case 'today': {
                startDate = moment(today).format(formatString);
                return { startDate };
            }
            case 'tomorrow': {
                startDate = moment(today).add(1, 'day').format(formatString);
                return { startDate };
            }
            case 'yesterday': {
                startDate = moment(today).subtract(1, 'day').format(formatString);
                return { startDate };
            }
            case 'next-week': {
                startDate = moment(today).add(1, 'day').format(formatString);
                endDate = moment(today).add(7, 'days').format(formatString);
                return { startDate, endDate };
            }

            case 'last-week': {
                startDate = moment(today).subtract(6, 'days').format(formatString);
                endDate = moment(today).format(formatString);
                return { startDate, endDate };
            }

            case 'custom': {
                return { startDate: customDateRange?.[0], endDate: customDateRange?.[1] };
            }

            default: {
                return {};
            }
        }
    };

    poll = async (): Promise<void> => {
        const selectedSection = this.getCurrentSectionData();
        let shouldConfigsRefresh = false;

        if (!selectedSection || !this.props.cId) {
            return;
        }

        const isInit = this.pollingCount === 0;
        const localPollCount = ++this.pollingCount;

        const {
            queueWindowType = selectedSection.defaultWindowDuration,
            customQueueWindowDate,
        } = this.state;

        const { startDate, endDate } = queueWindowType
            ? this.getDateToPoll(queueWindowType, customQueueWindowDate)
            : { startDate: undefined, endDate: undefined };

        const selectedSectionPromise = selectedSection
            .api({
                cid: this.props.cId,
                startDate,
                endDate,
                docIds: this.myProfile.doctors.map((item) => item.id),
            })
            .catch((e) => console.log(e));

        // const todayQueuePromise =
        //     this.props.section !== 'today-queue'
        //         ? fetchTodayQueue(this.props.cId).catch((e) => console.log(e))
        //         : undefined;

        const isHmisEnabledRequest = isInit ? isHmisEnabled() : undefined;

        const apptConfigPromise = isInit
            ? fetchApptConfigs(getMe()?.business?.id ?? '').catch((e) => console.log(e))
            : undefined;

        const availableTagsSet = new Set(
            this.state.appointmentConfigs.tags.map(({ tag_id }) => tag_id),
        );

        const selectedSectionResponse = await selectedSectionPromise;

        // TODO add tracking here

        const stateUpdatesPromise = this.getStateUpdatesForSelectedSection(selectedSectionResponse);

        const tagsIdToCountMap = new Map<number, number>();

        selectedSectionResponse?.response?.apt.forEach(({ tags }) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            tags?.forEach((tagId) => {
                tagsIdToCountMap.set(tagId, (tagsIdToCountMap.get(tagId) ?? 0) + 1);
                if (!availableTagsSet.has(tagId)) {
                    shouldConfigsRefresh = true;
                }
            });
        });

        const stateUpdates = await stateUpdatesPromise;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore

        let appointmentConfigs = this.state.appointmentConfigs;

        if (!!apptConfigPromise) {
            const apptConfigResponse = await apptConfigPromise;
            const isHmisEnabled = isHmisEnabledRequest ? await isHmisEnabledRequest : false;

            if (apptConfigResponse) {
                const { success, data } = apptConfigResponse;
                if (success && !!data) {
                    appointmentConfigs = {
                        ...appointmentConfigs,
                        tags: data?.tags.map((tagData) => ({
                            ...tagData,
                            count: tagsIdToCountMap.get(tagData.tag_id) ?? 0,
                        })),
                        user_preferences: {
                            ...this.state.appointmentConfigs.user_preferences,
                            ...getDefaultColumnPreference({ isHmisDoctor: isHmisEnabled }),
                            ...data.user_preferences,
                        },
                        eligibile_for_payments: !!data.eligibile_for_payments,
                    };
                }
            }
        } else {
            appointmentConfigs = {
                ...appointmentConfigs,
                tags: this.state.appointmentConfigs.tags?.map((tagData) => ({
                    ...tagData,
                    count: tagsIdToCountMap.get(tagData.tag_id) ?? 0,
                })),
            };
        }

        const patientFormData = await getEnabledDynamicFormFields();

        const allStateUpdates: Partial<State> = {
            ...stateUpdates,
            isLoading: false,
            appointmentConfigs,
            todayQueueOptions: this.addPatientDataColumns(
                this.state.todayQueueOptions,
                patientFormData,
            ),
            upcomingQueueOptions: this.addPatientDataColumns(
                this.state.upcomingQueueOptions,
                patientFormData,
            ),
            completedQueueOptions: this.addPatientDataColumns(
                this.state.completedQueueOptions,
                patientFormData,
            ),
            followupQueueOptions: this.addPatientDataColumns(
                this.state.followupQueueOptions,
                patientFormData,
            ),
            othersQueueOptions: this.addPatientDataColumns(
                this.state.othersQueueOptions,
                patientFormData,
            ),
        };

        if (localPollCount !== 1 && localPollCount < this.pollingCount) {
            return;
        }

        this.setState(allStateUpdates as State);
        const {
            searchInput,
            activeFilterIndex,
            filteredTags,
            filteredVisitType,
            selectedDoctor,
        } = this.state;

        this.applyFilterToAppointments({
            appointments: allStateUpdates.appointments || this.state.appointments,
            searchInput,
            activeFilterIndex,
            filteredTags,
            filteredVisitType,
            selectedDoctor,
        });

        // if (!!todayQueuePromise) {
        //     const todayQueueResponse = await todayQueuePromise;
        //     if (!todayQueueResponse?.response?.apt) {
        //         return;
        //     }
        //     // TODO add tracking here
        //     const list = todayQueueResponse.response.apt.filter((t) => t.patient);
        //     const visitIds = list.map((a) => a.aid);
        //     const localVisits = await getLocalVisitByIds(visitIds);
        //     const localContextList: AP[] = list.map((a, index) => ({
        //         ...a,
        //         isLocalPresnt: localVisits[index] !== undefined,
        //         serialNum: index + 1,
        //         localRxId: localVisits[index]?.prescriptionId,
        //     }));

        //     this.setState({
        //         todayAppointments: localContextList,
        //         isTodayLoading: false,
        //         countResponse: {
        //             ...(this.state.countResponse || {}),
        //             today: todayQueueResponse.response.countResponse?.today,
        //         },
        //         totalCountResponse: todayQueueResponse.response.countResponse,
        //     });
        // }

        if (!isInit && shouldConfigsRefresh) {
            this.refreshAppointmentConfigs(stateUpdates.appointments ?? []);
        }
    };

    addPatientDataColumns = (
        previousQueueOptions: QueueColumnOptions,
        patientFormData: DFormEntity[],
    ): QueueColumnOptions => {
        const allColumnKeys = new Set<string>(['uhid', 'tag']);
        const toBeNewlyAddedDataFieldsIntoColumn = new Set<string>();

        previousQueueOptions.forEach((item) => allColumnKeys.add(item.key));

        patientFormData?.forEach((item) => {
            if (!allColumnKeys.has(item.key)) {
                toBeNewlyAddedDataFieldsIntoColumn.add(item.key);
            }
        });

        if (!patientFormData || !patientFormData.length) {
            return previousQueueOptions;
        } else {
            const queueOptions = [...previousQueueOptions];

            const itemsAtEnd = queueOptions.splice(
                queueOptions.findIndex((item) => item.key === 'PDT') + 1,
            );

            patientFormData.forEach((dataItem, idx) => {
                if (toBeNewlyAddedDataFieldsIntoColumn.has(dataItem.key)) {
                    queueOptions.push({
                        priority: idx,
                        key: dataItem.key,
                        required: false,
                        renderer: {
                            header: (
                                <div className={`${dataItem.key} pt-fields capitalize`}>
                                    {dataItem.label}
                                </div>
                            ),
                            renderCell: (a, idx) => {
                                return (
                                    <div className={`${dataItem.key} pt-fields`}>
                                        {a.formData.find((item) => item.key === dataItem.key)
                                            ?.value || '--'}
                                    </div>
                                );
                            },
                        },
                        exportInExcel: {
                            title: dataItem.label,
                            exporter: (a: AP) => {
                                return (
                                    a.formData.find((item) => item.key === dataItem.key)?.value ||
                                    ''
                                );
                            },
                        },
                    });
                }
            });

            return queueOptions.concat(itemsAtEnd);
        }
    };

    getAllAppointments = (): void => {
        this.poll();
        this.polling = setInterval(this.poll, POLL_INTERVAL_MS);
    };

    onPatientNameClick = (a: Appointment): void => {
        if (a.patient_oid) {
            navigate('push', `/app/patient/${a.patient_oid}/visit`);
        } else {
            alert('Something went wrong');
        }
        return;
    };

    onEnterVitals = (a: AP): void => {
        if (getAppointmentAction(a) === ADD_PATIENT) {
            notification.error({
                message: 'Add Patient for this appointment first',
                duration: 2,
            });
            return;
        }

        onAddVitalsAction(a);
    };

    onSearchChange = (input: string): void => {
        this.setState({
            searchInput: input,
        });
        const {
            appointments,
            activeFilterIndex,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        } = this.state;

        this.applyFilterToAppointments({
            appointments,
            searchInput: input,
            activeFilterIndex,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        });
    };

    onAppointmentViewDetials = (aid: string, manage?: boolean): void => {
        toggleIframeActionDialog(
            `${BOOKING_SLUG}/appointment/${aid}${manage ? '?manage=true' : ''}`,
        );
    };

    onBookFollowupSlot = (aid: string) => {
        const doctor = getDoc();
        if (!doctor) {
            return;
        }
        const {
            profile: {
                professional: { username },
            },
        } = doctor;

        track('BOOK_FLW_SLOT', { aid });
        toggleIframeActionDialog(
            `${BOOKING_SLUG}/doctor/${username}/calendar?f_aid=${aid}&autoOpenSlots=true`,
        );
    };

    onSendAssessmentClick = (aid: string, name?: string | null) => {
        sendSelfAssessmentLink(aid)
            .then((res: Res) => {
                if (res.success) {
                    notification.success({
                        message: `Assessment send to ${name}`,
                        placement: 'bottomRight',
                    });
                } else {
                    notification.error({
                        message: res?.error?.type,
                        placement: 'bottomRight',
                    });
                }
            })
            .catch((e) => {
                message.error('Request failed retry!');
                console.error(e);
            });
    };

    onSendFollowupReminder = async (a: AP): Promise<void> => {
        const { success } = await sendFollowupReminders({ aid: a.aid }).catch((e) => {
            console.log(e);
            return { success: false, error: 'Failed to send followup reminder' };
        });

        track('SEND_FOLLOWUP_REMINDER_MESSAGE', { success: `${success}` });

        if (success) {
            notification.success({
                message: `Successfully sent Followup Reminder to ${a.patient.fn}`,
                placement: 'bottomRight',
            });
        } else {
            notification.error({
                message: `Failed to send followup Reminder to ${a.patient.fn}.`,
                placement: 'bottomRight',
                description: 'You might have exceeded the number of times messages could be sent.',
            });
        }
    };

    onSendAppointmentReminder = async (a: AP): Promise<void> => {
        const { success } = await sendAppointmentReminder({ aid: a.aid }).catch((e) => {
            console.log(e);
            return { success: false, error: 'Failed to send appointment reminder' };
        });

        track('SEND_APT_REMINDER_MESSAGE', { success: `${success}` });

        if (success) {
            notification.success({
                message: `Successfully sent Appointment Reminder to ${a.patient.fn}`,
                placement: 'bottomRight',
            });
        } else {
            notification.error({
                message: `Failed to send Appointment Reminder to ${a.patient.fn}.`,
                placement: 'bottomRight',
                description: 'You might have exceeded the number of times messages could be sent.',
            });
        }
    };

    onAbortAppointment = (a: Appointment): void => {
        endAppointment({
            status: 'AB',
            visitId: a.aid,
            docId: getDoc()?._id || '',
            ptOid: a.patient_oid || '',
            startDateTime: a.start_time,
            endDateTime: new Date().toISOString(),
            clinicId: a.clinic_id,
        })
            .then((res) => {
                if (res.success !== true) {
                    alert('Failed to abort appointment');
                } else {
                    this.hardRefresh();
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to abort appointment');
            });
    };

    onEndVisit = (a: Appointment): void => {
        endAppointment({
            status: 'CMNP',
            visitId: a.aid,
            docId: getDoc()?._id || '',
            ptOid: a.patient_oid || '',
            startDateTime: a.start_time,
            endDateTime: new Date().toISOString(),
            clinicId: a.clinic_id,
        })
            .then((res) => {
                if (res.success !== true) {
                    alert('Failed to end visit');
                } else {
                    this.hardRefresh();
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to end visit');
            });
    };

    onSendReviewLink = (a: Appointment): void => {
        sendReviewLink(a.aid)
            .then((res) => {
                track('SEND_REVIEW_LINK', { success: `${res.success}` });
                if (res.success) {
                    notification.info({
                        message: `Google Review Link Sent Successfully`,
                        placement: 'bottomRight',
                        duration: 3,
                    });
                } else {
                    notification.error({
                        message: res?.error?.message || 'Failed to send review link',
                        placement: 'bottomRight',
                        duration: 3,
                    });
                }
            })
            .catch(() => {
                alert('Failed to send Google Review Link');
            });
    };

    onReferADoc = (a: AP): void => {
        if (!a.patient.oid) {
            alert('Add patient first to refer');
            return;
        }
        onReferAdocAction(a);
    };

    onParkAppointment = (a: Appointment): void => {
        endAppointment({
            status: 'PA',
            visitId: a.aid,
            docId: getDoc()?._id || '',
            ptOid: a.patient_oid || '',
            clinicId: a.clinic_id,
        })
            .then((res) => {
                if (res.success !== true) {
                    alert('Failed to park appointment');
                } else {
                    this.setState({
                        isLoading: true,
                    });
                    this.poll();
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to park appointment');
            });
    };

    onMarkAsPaid = (a: Appointment): void => {
        markPaymentAsPaid({
            visitId: a.aid,
        })
            .then((res) => {
                if (res.error) {
                    alert('Failed to mark as paid');
                } else {
                    this.setState({
                        isLoading: true,
                    });
                    this.poll();
                }
            })
            .catch((e) => {
                console.error(e);
                alert('Failed to markPaymentAsPaid');
            });
    };

    hardRefresh = ({ clearQueueWindow = false }: { clearQueueWindow?: boolean } = {}): void => {
        const dataToUpdate: Pick<
            State,
            'isLoading' | 'queueWindowType' | 'customQueueWindowDate' | 'selectedQueueWindowDates'
        > = { isLoading: true };

        if (clearQueueWindow) {
            dataToUpdate['customQueueWindowDate'] = undefined;
            dataToUpdate['selectedQueueWindowDates'] = null;
            dataToUpdate['queueWindowType'] = undefined;
        }

        this.setState(dataToUpdate, this.poll);
    };

    onRequestPayment = (a: AP): void => {
        if (!this.state.appointmentConfigs.eligibile_for_payments) {
            notification.error({
                message: 'Your bank details are not setup to receive payments.',
                description:
                    'Head over to Profile > Activate Payments and fill your bank details to request payment',
                placement: 'bottomRight',
            });
        } else {
            onRequestPaymentAction(a);
        }
    };

    onRequestMedicalRecords = (a: AP): void => {
        onRequestMedicalRecordsAction(a);
    };

    onAddAddress = (a: AP): void => {
        onAddAddressAction(a);
    };

    onShowSa = (a: AP): void => {
        track('OPEN_SA', {
            channel: 'APPOINTMENTS',
            apptVisitType: a['visit-type'],
            apptChannel: a.channel,
        });
        onShowSAAction(a);
    };

    onCloseSa = (): void => {
        this.setState({
            saAppointment: undefined,
        });
    };

    closeReferalDialog = (): void => {
        this.setState({
            referAP: undefined,
        });
    };

    onSendInvoice = (a: AP): void => {
        const isCreateReceiptAllowed = isAccessAvailable(SUBJECT_KEYS.CAN_CREATE_RECEIPT_WITH_APT);
        if (isCreateReceiptAllowed) {
            onBillingPopupAction(a);
        } else {
            showAccessDeniedError();
        }
    };

    onViewReceipts = (a: AP): void => {
        onViewReceiptsPopupAction(a);
    };

    removeFilter = (): void => {
        track('REMOVE_ALL_FILTERS');

        const dataToUpdate: Pick<
            State,
            | 'searchInput'
            | 'activeFilterIndex'
            | 'filteredVisitType'
            | 'filteredTags'
            | 'filteredAppointmentList'
            | 'isFiltered'
            | 'selectedDoctor'
        > = {
            searchInput: '',
            activeFilterIndex: {},
            filteredVisitType: new Set<string>(),
            filteredTags: new Set<number>(),
            filteredAppointmentList: [],
            isFiltered: false,
        };

        this.setState(dataToUpdate);
    };

    toggleTagsFilters = (): void => {
        this.setState({ tagsFilterDialogVisible: !this.state.tagsFilterDialogVisible });
    };

    toggleVisitTypeFilters = (): void => {
        this.setState({ visitTypeFilterDialogVisible: !this.state.visitTypeFilterDialogVisible });
    };

    toggleGenericFilters = (): void => {
        this.setState({ genericFilterVisible: !this.state.genericFilterVisible });
    };

    hideVisitTypeFilters = (): void => {
        this.setState({
            visitTypeFilterDialogVisible: false,
        });
    };

    applyVisitTypeFilterMap = (map: Set<string>): void => {
        this.setState({ filteredVisitType: map });
        const {
            appointments,
            searchInput,
            filteredTags,
            activeFilterIndex,
            selectedDoctor,
        } = this.state;

        this.applyFilterToAppointments({
            appointments,
            searchInput,
            filteredVisitType: map,
            filteredTags,
            activeFilterIndex,
            selectedDoctor,
        });
    };

    applyFilteredTags = (tags: Set<number>): void => {
        this.setState({
            filteredTags: tags,
        });
        track('TAGS_SELECT');
        const {
            appointments,
            searchInput,
            filteredVisitType,
            activeFilterIndex,
            selectedDoctor,
        } = this.state;

        this.applyFilterToAppointments({
            appointments,
            searchInput,
            filteredVisitType,
            filteredTags: tags,
            activeFilterIndex,
            selectedDoctor,
        });
    };

    applyFilterToAppointments = ({
        appointments,
        searchInput,
        activeFilterIndex,
        filteredVisitType,
        filteredTags,
        selectedDoctor,
    }: Pick<
        State,
        | 'appointments'
        | 'searchInput'
        | 'activeFilterIndex'
        | 'filteredVisitType'
        | 'filteredTags'
        | 'selectedDoctor'
    >): void => {
        const selectedSection = this.getCurrentSectionData();

        if (!selectedSection) {
            return;
        }

        let filteredList = appointments;
        let isFiltered = false;

        if (searchInput) {
            filteredList = this.appointmentsFuse
                .search(
                    searchInput.length === 10 && /^\d+$/.test(searchInput)
                        ? `=+91${searchInput}`
                        : searchInput,
                )
                .map((option) => option.item);

            isFiltered = true;
        }

        const filters = Object.entries(activeFilterIndex);

        if (filters.filter((f) => f[1]).length > 0) {
            isFiltered = true;
            filteredList = filteredList.filter((a) => {
                for (let i = 0; i < filters.length; i++) {
                    if (filters[i][1]) {
                        if (selectedSection.filters[parseInt(filters[i][0] as string)]?.check(a)) {
                            return true;
                        }
                    }
                }
            });
        }

        if (filteredVisitType.size > 0) {
            filteredList = filteredList.filter((t) =>
                filteredVisitType.has(`${t['type-display']}`),
            );
            isFiltered = true;
        }

        if (filteredTags.size > 0) {
            filteredList = filteredList.filter((apt) => {
                let tagExists = false;

                for (const tagId of apt.tags || []) {
                    tagExists = filteredTags.has(tagId) || tagExists;
                }

                return tagExists;
            });

            isFiltered = true;
        }

        if (selectedDoctor) {
            filteredList = filteredList.filter((item) => item.doctor_oid);
        }

        this.setState({
            isFiltered,
            filteredAppointmentList: filteredList,
            activeFilterIndex,
            filteredVisitType,
            filteredTags,
            searchInput,
        });
    };

    renderTable = () => {
        const {
            searchInput,
            isLoading,
            activeFilterIndex,
            filteredTags,
            appointmentConfigs: { tags: appointmentTags },
            filteredAppointmentList,
            isFiltered,
            appointments,
            filteredVisitType,
        } = this.state;

        const filteredList = isFiltered ? filteredAppointmentList : appointments;

        const selectedSection = this.getCurrentSectionData();

        if (!selectedSection) {
            return null;
        }

        const filters = Object.entries(activeFilterIndex);

        if (filteredList.length === 0 && !isLoading && !(isFiltered || searchInput)) {
            return <EmptyAppointmentView text={selectedSection.emptyText} />;
        }

        const addedFilterLabels = filters
            .map(
                ([i]) =>
                    activeFilterIndex[parseInt(i)] && selectedSection.filters[parseInt(i)].label,
            )
            .filter(Boolean);

        const tagIdToTagMap = new Map<number, TagRecordWithCount>();
        for (const tagData of appointmentTags) {
            const { tag_id } = tagData;
            tagIdToTagMap.set(tag_id, tagData);
        }

        const filteredWithVisitTypes = Array.from(filteredVisitType);

        const tagsFilteredWith = [...filteredTags].map((tagId) => {
            const x = tagIdToTagMap.get(tagId);
            return x?.label.toLowerCase();
        });

        const filStr = [...addedFilterLabels, ...filteredWithVisitTypes, ...tagsFilteredWith].join(
            ' | ',
        );

        return (
            <div className="flex flex-1 flex-auto overflow-hidden mb-32 bg-neutral-release-test">
                <div
                    className={`${selectedSection.tableClassName} ${
                        (isFiltered || searchInput) && 'filter-applied'
                    }`}
                >
                    {(isFiltered || searchInput) && (
                        <div className="filter-applied-label">
                            <div className="flex items-center justify-between w-full">
                                <p>
                                    <span className="font-400">
                                        Showing {filteredList.length} patients
                                    </span>
                                    {filStr ? (
                                        <span className={'capitalize'}>{`: with ${filStr}`}</span>
                                    ) : null}
                                </p>
                                <p onClick={this.removeFilter} className="cursor-pointer">
                                    {isFiltered ? 'Remove Filter' : 'Clear Search'}
                                </p>
                            </div>
                        </div>
                    )}

                    {isLoading &&
                        Array.from(new Array(2)).map((_, idx) => (
                            <div key={idx} className="row">
                                <div className="w-5p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-5p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-20p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-10p mx-16 my-16 h-16 shimmer"></div>
                                <div className="w-15p mx-16 my-16 h-16 shimmer"></div>
                            </div>
                        ))}

                    {!isLoading && (
                        <Fragment>
                            <div className="header row w-full">
                                {selectedSection.columns.map((c) => c.renderer.header)}
                            </div>
                            <div className="h-full overflow-hidden relative">
                                <div
                                    className="flex flex-col absolute inset-0 overflow-hidden"
                                    style={{ bottom: isFiltered || searchInput ? 75 : 36 }}
                                >
                                    <Virtuoso
                                        style={{ width: '100%' }}
                                        totalCount={filteredList.length + 5}
                                        overscan={25}
                                        data={filteredList}
                                        itemContent={(i: number, a?: AP) => {
                                            if (!a) {
                                                return;
                                            }

                                            const aDate = moment(a.date).format('DD MMMM YYYY');
                                            let aCount = 1;

                                            for (let j = i + 1; j < filteredList.length; j++) {
                                                if (
                                                    moment(filteredList[j].date).format(
                                                        'DD MMMM YYYY',
                                                    ) === aDate
                                                ) {
                                                    aCount++;
                                                }
                                            }
                                            return (
                                                <Fragment>
                                                    {selectedSection.showDateReperator &&
                                                        (i === 0 ||
                                                            aDate !==
                                                                moment(
                                                                    filteredList[i - 1].date,
                                                                ).format('DD MMMM YYYY')) && (
                                                            <div key={`${a.aid}-sep`}>
                                                                <p className="px-8 pt-16 pb-10 text-neutral-black font-700 text-13">
                                                                    <span>
                                                                        {getDayWord(a.date)}
                                                                    </span>
                                                                    ,{' '}
                                                                    <span>
                                                                        {moment(a.date).format(
                                                                            'Do MMM YYYY',
                                                                        )}
                                                                    </span>{' '}
                                                                    :{' '}
                                                                    <p className="inline bg-neutral-9 text-13 text-neutral-black px-8 py-4 rounded-4 font-400">
                                                                        {aCount} appts
                                                                    </p>
                                                                </p>
                                                            </div>
                                                        )}

                                                    <div
                                                        key={a.aid}
                                                        className={`${
                                                            [ONGOING, RE_START].includes(
                                                                getAppointmentAction(a),
                                                            )
                                                                ? 'appt-ongoing-row'
                                                                : ''
                                                        } ${
                                                            isCNRE(a.status)
                                                                ? 'appt-cancelled-row'
                                                                : ''
                                                        } ${
                                                            a.status === 'AB' || a.status === 'PC'
                                                                ? 'appt-aborted-row'
                                                                : ''
                                                        }
                                                        ${
                                                            a.status === 'PNR' ? 'appt-pnr-row' : ''
                                                        } ${
                                                            [
                                                                PARKED_ON_OTHER_DEVICE,
                                                                PARKED,
                                                            ].includes(getAppointmentAction(a))
                                                                ? 'appt-parked-row'
                                                                : ''
                                                        } row`}
                                                        style={{ marginBottom: 0 }}
                                                    >
                                                        {selectedSection.columns.map((c) =>
                                                            c.renderer.renderCell(a, i),
                                                        )}
                                                    </div>
                                                </Fragment>
                                            );
                                        }}
                                    />
                                </div>
                            </div>
                        </Fragment>
                    )}
                </div>
            </div>
        );
    };

    toggleFilterAtIndex = (i: number) => {
        const updatedActiveFilteredIndex = {
            ...this.state.activeFilterIndex,
            [i]: !this.state.activeFilterIndex[i],
        };

        this.setState({
            activeFilterIndex: updatedActiveFilteredIndex,
        });

        const selectedSection = this.getCurrentSectionData();
        const {
            appointments,
            searchInput,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        } = this.state;

        if (!selectedSection) {
            return null;
        }

        track('FILTER_SELECT', { label: selectedSection.filters[i].label, action: 'toggled' });

        this.applyFilterToAppointments({
            appointments,
            searchInput,
            activeFilterIndex: updatedActiveFilteredIndex,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        });
    };

    applyGenericFilters = (filterSet: Set<number>) => {
        const activeFilterIndex: Record<number, boolean> = {};
        const {
            appointments,
            searchInput,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        } = this.state;

        Array.from(filterSet).forEach((filterIndex) => {
            activeFilterIndex[filterIndex] = true;
        });

        this.setState({
            activeFilterIndex,
        });

        this.applyFilterToAppointments({
            appointments,
            searchInput,
            activeFilterIndex,
            filteredVisitType,
            filteredTags,
            selectedDoctor,
        });
    };

    applyQueueWindow = (
        windowType: QueueWindowDurationType,
        customDateRange?: [string | undefined, string | undefined],
        performHardRefresh = true,
    ): void => {
        track('QUEUE_WINDOW_FILTER', {
            windowType,
            customDateRange: customDateRange?.join('/'),
            tabName: this.props.section,
        });
        const dataToUpdate: Partial<
            Pick<State, 'queueWindowType' | 'customQueueWindowDate' | 'selectedQueueWindowDates'>
        > = {
            queueWindowType: windowType,
        };

        if (customDateRange) {
            dataToUpdate['customQueueWindowDate'] = customDateRange;
        }

        if (windowType !== 'custom') {
            dataToUpdate['customQueueWindowDate'] = undefined;
            dataToUpdate['selectedQueueWindowDates'] = null;
        }
        this.setState(dataToUpdate, () => {
            if (performHardRefresh) {
                this.hardRefresh();
                this.removeFilter();
            }
        });
    };

    renderSectionBoxes = () => {
        const { countResponse } = this.state;

        const selectedSection = this.getCurrentSectionData();

        if (!selectedSection) {
            return null;
        }

        return (
            <div className="px-16 pt-16 pb-8 flex items-start space-x-16">
                {Object.entries(this.tabSections)
                    .sort((a, b) => a[1].index - b[1].index)
                    .map(([key, sec]) => (
                        <div
                            onClick={() => {
                                navigate(
                                    'replace',
                                    `/app/appointments/clinic/${this.props.cId}/${
                                        key as APP_VALID_SECTIONS
                                    }`,
                                );
                                if (selectedSection.index !== sec.index) {
                                    this.setState({ tokenSortPreference: 'NONE' });
                                }
                            }}
                            key={sec.label}
                            className="flex flex-col items-center"
                        >
                            <div
                                className={`appointments-filter-box ${
                                    selectedSection.index === sec.index && 'active'
                                }`}
                            >
                                {sec.renderBoxContent(countResponse)}
                            </div>
                            <div className="flex items-center justify-center w-16 h-16">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 320 512"
                                    className="fill-actionPrimary w-16 h-16"
                                >
                                    <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z"></path>
                                </svg>
                            </div>
                        </div>
                    ))}
            </div>
        );
    };

    onSearchInputFocus = () => {
        track('SEARCH_INPUT_FOCUS', { where: 'Appointments' });
    };

    getColumnDataForCurrentSection = () => {
        const currentSection = this.props.section;

        switch (currentSection) {
            case 'today-queue': {
                return {
                    options: this.state.todayQueueOptions,
                    selectedColumns: this.state.appointmentConfigs.user_preferences[
                        todayQueueColumnKeys
                    ],
                };
            }
            case 'completed': {
                return {
                    options: this.state.completedQueueOptions,
                    selectedColumns: this.state.appointmentConfigs.user_preferences[
                        completedQueueColumnKeys
                    ],
                };
            }
            case 'others': {
                return {
                    options: this.state.othersQueueOptions,
                    selectedColumns: this.state.appointmentConfigs.user_preferences[
                        othersQueueColumnKeys
                    ],
                };
            }
            case 'upcoming': {
                return {
                    options: this.state.upcomingQueueOptions,
                    selectedColumns: this.state.appointmentConfigs.user_preferences[
                        upcomingQueueColumnKeys
                    ],
                };
            }
            case 'follow-ups': {
                return {
                    options: this.state.followupQueueOptions,
                    selectedColumns: this.state.appointmentConfigs.user_preferences[
                        followupQueueColumnKeys
                    ],
                };
            }
        }
    };

    onPatientDetailsNameClick = (a: Appointment): void => {
        if (a.patient_oid) {
            const currentSection = this.props.section;
            let route = `/app/patient/${a.patient_oid}/visit`;

            if (currentSection === 'today-queue' || currentSection === 'upcoming') {
                route += `?aid=${a.aid}&cid=${a.clinic_id}`;
            }

            navigate('push', route);
        } else {
            alert('Something went wrong');
        }
        return;
    };

    setNewColumnPreference = (columns: string[]) => {
        const currentSection = this.props.section;
        track('APPLY_COLUMN_SELECTION', { section: currentSection });

        let sectionColumnFieldName = '';

        if (currentSection === 'today-queue') {
            sectionColumnFieldName = todayQueueColumnKeys;
        } else if (currentSection === 'completed') {
            sectionColumnFieldName = completedQueueColumnKeys;
        } else if (currentSection === 'others') {
            sectionColumnFieldName = othersQueueColumnKeys;
        } else if (currentSection === 'upcoming') {
            sectionColumnFieldName = upcomingQueueColumnKeys;
        } else if (currentSection === 'follow-ups') {
            sectionColumnFieldName = followupQueueColumnKeys;
        }

        if (!!sectionColumnFieldName) {
            postQueueColumnConfig({ preferenceFor: sectionColumnFieldName, preferences: columns });
            this.setState({
                appointmentConfigs: {
                    ...this.state.appointmentConfigs,
                    user_preferences: {
                        ...this.state.appointmentConfigs.user_preferences,
                        [sectionColumnFieldName]: columns,
                    },
                },
            });
        }
    };

    downloadAppointments = () => {
        const { isFiltered, filteredAppointmentList, appointments } = this.state;
        const renderedAppointments = isFiltered ? filteredAppointmentList : appointments;
        const currentSectionData = this.getCurrentSectionData();

        if (!currentSectionData) {
            this.setState({ isAppointmentsExporting: false });
            return;
        }

        const columnsToExport = this.getAllColumnsForQueueType(this.props.section);

        const {
            queueWindowType = currentSectionData.defaultWindowDuration,
            customQueueWindowDate,
        } = this.state;

        const { startDate, endDate } = queueWindowType
            ? this.getDateToPoll(queueWindowType, customQueueWindowDate)
            : { startDate: undefined, endDate: undefined };

        const formattedData = renderedAppointments.map((appointment) => {
            let dataItem: Record<string, string | number> = {};

            dataItem['date'] = moment(appointment.date).format('YYYY-MM-DD');

            columnsToExport?.forEach((item) => {
                if (item.exportInExcel) {
                    const dataBlockItem = item.exportInExcel.exporter(appointment);
                    if (typeof dataBlockItem === 'object') {
                        dataItem = { ...dataItem, ...dataBlockItem };
                    } else {
                        dataItem[item.exportInExcel.title] = dataBlockItem;
                    }
                }
            });

            return dataItem;
        });

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const ws = XLSX.utils.json_to_sheet(formattedData);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const wb = XLSX.utils.book_new();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        XLSX.utils.book_append_sheet(wb, ws, 'Eka Appointments');

        const datePart = [startDate ?? new Date(), endDate]
            .filter(Boolean)
            .map((item) => moment(item).format('YYYY-MM-DD'))
            .join('-to-');

        const filename = `${this.props.section}-${datePart}.xls`;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        XLSX.writeFile(wb, filename);
        this.setState({ isAppointmentsExporting: false });
    };

    renderFilterbar = () => {
        const selectedSection = this.getCurrentSectionData();

        const {
            searchInput,
            activeFilterIndex,
            visitTypeFilterDialogVisible: visitTypeFilter,
            filteredVisitType,
            tagsFilterDialogVisible,
            genericFilterVisible = false,
            queueWindowType = selectedSection?.defaultWindowDuration,
            filteredAppointmentList,
            isFiltered,
            appointments,
        } = this.state;

        const columnData = this.getColumnDataForCurrentSection();

        if (!selectedSection || !columnData || !columnData.options) {
            return null;
        }

        const { options: columnOptions, selectedColumns } = columnData;

        const sortedTags = [...this.state.appointmentConfigs.tags].sort(
            (l, r) => r.count - l.count,
        );

        const allTagIdsSet = new Set<number>();
        const nameMapForTags = new Map<number, string>();

        sortedTags.forEach((tagData) => {
            if (tagData.archive === false) {
                const { tag_id, label, count } = tagData;
                allTagIdsSet.add(tag_id);
                nameMapForTags.set(tag_id, `${label} (${count})`);
            }
        });

        const allGenericFilters = new Set<number>();
        const selectedGenericFilters = new Set<number>();
        const nameMap = new Map<number, string>();

        selectedSection.filters.forEach((f, i) => {
            if (!!activeFilterIndex[i]) {
                selectedGenericFilters.add(i);
            }
            nameMap.set(i, f.label);
            allGenericFilters.add(i);
        });

        const customDateRange = this.state.selectedQueueWindowDates;

        const dateRangeValue = [
            customDateRange?.[0] ? moment(customDateRange?.[0]) : null,
            customDateRange?.[1] ? moment(customDateRange?.[1]) : null,
        ];

        const renderedAppointments = isFiltered ? filteredAppointmentList : appointments;

        return (
            <div className="px-16 select-none">
                <div className="flex items-center justify-between bg-neutral-9 px-16 py-8">
                    <div className="flex w-50p space-x-12 items-center">
                        <div className="w-30p appts-search-filter">
                            <input
                                type="text"
                                className="w-90p focus:outline-none py-2 text-12 placeholder-neutral-4 text-neutral-black"
                                placeholder={selectedSection.searchPlaceholder}
                                onChange={(e) => this.onSearchChange(e.target.value)}
                                value={searchInput}
                                onFocus={this.onSearchInputFocus}
                            />
                            <div className="search-icon">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="fill-neutral-6 w-14 h-14"
                                >
                                    <path d="M508.5 468.9L387.1 347.5c-2.3-2.3-5.3-3.5-8.5-3.5h-13.2c31.5-36.5 50.6-84 50.6-136C416 93.1 322.9 0 208 0S0 93.1 0 208s93.1 208 208 208c52 0 99.5-19.1 136-50.6v13.2c0 3.2 1.3 6.2 3.5 8.5l121.4 121.4c4.7 4.7 12.3 4.7 17 0l22.6-22.6c4.7-4.7 4.7-12.3 0-17zM208 368c-88.4 0-160-71.6-160-160S119.6 48 208 48s160 71.6 160 160-71.6 160-160 160z"></path>
                                </svg>
                            </div>
                            <div
                                onClick={() => this.onSearchChange('')}
                                className="clear-icon cursor-pointer"
                            >
                                <svg
                                    className="fill-neutral-6 w-14 h-14"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 320 512"
                                >
                                    <path d="M312.1 375c9.369 9.369 9.369 24.57 0 33.94s-24.57 9.369-33.94 0L160 289.9l-119 119c-9.369 9.369-24.57 9.369-33.94 0s-9.369-24.57 0-33.94L126.1 256L7.027 136.1c-9.369-9.369-9.369-24.57 0-33.94s24.57-9.369 33.94 0L160 222.1l119-119c9.369-9.369 24.57-9.369 33.94 0s9.369 24.57 0 33.94L193.9 256L312.1 375z"></path>
                                </svg>
                            </div>
                        </div>
                        {selectedSection.queueWindowDurations.map((f) => {
                            const onCalendarChange = (
                                val: RangeValue<moment.Moment> | undefined,
                            ) => {
                                console.log('Calling on calendar Change');
                                const startDate = val?.[0]?.format('YYYY-MM-DD') ?? null;
                                const endDate = val?.[1]?.format('YYYY-MM-DD') ?? null;

                                this.setState({
                                    selectedQueueWindowDates: [startDate, endDate],
                                });
                            };

                            if (f.type === 'custom') {
                                return (
                                    <div
                                        key={f.label}
                                        className={`appt-table-filter date-picker ${
                                            queueWindowType === f.type && 'active'
                                        }`}
                                    >
                                        <CustomDatePicker
                                            dates={
                                                !!customDateRange
                                                    ? [dateRangeValue[0], dateRangeValue[1]]
                                                    : null
                                            }
                                            onDatesSelected={(startDate, endDate) => {
                                                console.log('On dates selected called');
                                                const start = moment(startDate).format(
                                                    'YYYY-MM-DD',
                                                );
                                                const end = moment(endDate).format('YYYY-MM-DD');

                                                this.applyQueueWindow(f.type, [start, end]);
                                            }}
                                            noOfDays={31}
                                            setDates={onCalendarChange}
                                            enableFuture={true}
                                            disablePast={this.props.section === 'upcoming'}
                                        />
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={f.label}
                                    onClick={() => this.applyQueueWindow(f.type)}
                                    className={`appt-table-filter ${
                                        queueWindowType === f.type && 'active'
                                    }`}
                                >
                                    {f.label}
                                </div>
                            );
                        })}
                    </div>
                    <div className="flex items-center space-x-12">
                        <div className="flex items-center">
                            <p className="uppercase font-700 text-neutral-4 text-11">SHOW ONLY:</p>
                            <div className="flex items-center bg-white rounded-2 border border-neutral-7 ml-12 relative">
                                <div
                                    className="flex items-center justify-between px-8 py-4 bg-white cursor-pointer w-96 xl:w-120 select-none"
                                    onClick={(e) => {
                                        this.toggleGenericFilters();
                                        e.stopPropagation();
                                    }}
                                >
                                    <p className="text-11 text-neutral-3">
                                        Filters{' '}
                                        {selectedGenericFilters.size > 0 &&
                                            `(${selectedGenericFilters.size})`}
                                    </p>
                                    <div className="w-16 h-16 flex items-center justify-between">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 320 512"
                                            className="w-16 h-16 fill-neutral-2"
                                        >
                                            <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
                                        </svg>
                                    </div>
                                </div>
                                {genericFilterVisible && (
                                    <FilterDialogV2
                                        filteredMap={selectedGenericFilters}
                                        wholeMap={allGenericFilters}
                                        apply={this.applyGenericFilters}
                                        alternateNameMap={nameMap}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="border-l border-neutral-7 h-24"></div>
                        <div className="flex items-center bg-white rounded-2 border border-neutral-7 relative">
                            <div
                                className="flex items-center justify-between px-8 py-4 bg-white cursor-pointer w-96 xl:w-120 select-none"
                                onClick={(e) => {
                                    this.toggleVisitTypeFilters();
                                    e.stopPropagation();
                                }}
                            >
                                <p className="text-11 text-neutral-3">
                                    Visit Type{' '}
                                    {this.state.filteredVisitType.size > 0 &&
                                        `(${this.state.filteredVisitType.size})`}
                                </p>
                                <div className="w-16 h-16 flex items-center justify-between">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512"
                                        className="w-16 h-16 fill-neutral-2"
                                    >
                                        <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
                                    </svg>
                                </div>
                            </div>
                            {visitTypeFilter && (
                                <FilterDialogV2
                                    filteredMap={filteredVisitType}
                                    wholeMap={this.visitTypeMap}
                                    apply={this.applyVisitTypeFilterMap}
                                />
                            )}
                        </div>
                        <div className="flex items-center bg-white rounded-2 border border-neutral-7 relative">
                            <div
                                className="flex items-center justify-between px-8 py-4 bg-white cursor-pointer w-96 xl:w-120 select-none"
                                onClick={(e) => {
                                    track('SHOW_TAGS_FILTER_DROPDOWN');
                                    this.toggleTagsFilters();
                                    e.stopPropagation();
                                }}
                            >
                                <p className="text-11 text-neutral-3">
                                    Select Tags{' '}
                                    {this.state.filteredTags.size > 0 &&
                                        `(${this.state.filteredTags.size})`}
                                </p>
                                <div className="w-16 h-16 flex items-center justify-between">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 320 512"
                                        className="w-16 h-16 fill-neutral-2"
                                    >
                                        <path d="M31.3 192h257.3c17.8 0 26.7 21.5 14.1 34.1L174.1 354.8c-7.8 7.8-20.5 7.8-28.3 0L17.2 226.1C4.6 213.5 13.5 192 31.3 192z" />
                                    </svg>
                                </div>
                            </div>
                            {tagsFilterDialogVisible && (
                                <FilterDialogV2
                                    filteredMap={this.state.filteredTags}
                                    wholeMap={allTagIdsSet}
                                    apply={this.applyFilteredTags}
                                    alternateNameMap={nameMapForTags}
                                    wrapTitle={true}
                                    containerStyle={{
                                        right: '0px',
                                        maxWidth: '200px',
                                        minWidth: '200px',
                                    }}
                                />
                            )}
                        </div>
                        <div
                            onClick={() => {
                                if (
                                    this.state.isAppointmentsExporting ||
                                    !renderedAppointments.length
                                ) {
                                    return;
                                }
                                const isDownloadQueueEnabled = isAccessAvailable(
                                    SUBJECT_KEYS.DOWNLOAD_QUEUE,
                                );
                                if (!isDownloadQueueEnabled) {
                                    showAccessDeniedError();
                                    return;
                                }
                                this.setState({ isAppointmentsExporting: true });
                                track('DOWNLOAD_APPOINTMENTS', {
                                    queue: this.props.section,
                                });
                                const xlsx = document.createElement('script');
                                xlsx.onload = this.downloadAppointments;
                                xlsx.src = '/xlsx-sheet.js';
                                document.getElementsByTagName('head')[0].appendChild(xlsx);
                            }}
                            title="Export Appointments"
                            className={`max-w-24 max-h-24 min-w-24 min-h-24 flex items-center justify-center cursor-pointer ${
                                renderedAppointments.length > 0
                                    ? ''
                                    : 'opacity-60 cursor-not-allowed'
                            }`}
                        >
                            {this.state.isAppointmentsExporting ? (
                                <svg
                                    className="w-14 h-14 text-neutral-4 opacity-60 animate-spin"
                                    role="img"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    height="1em"
                                    viewBox="0 0 512 512"
                                    className={'w-14 h-14 text-neutral-4'}
                                >
                                    <path
                                        fill="currentColor"
                                        d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zm368 56a24 24 0 1 1 0 48 24 24 0 1 1 0-48z"
                                    />
                                </svg>
                            )}
                        </div>
                        <Popover
                            overlayClassName="popover-p-0"
                            overlayInnerStyle={{ borderRadius: '8px', overflow: 'hidden' }}
                            visible={!!this.state.columnsSelectorVisible}
                            onVisibleChange={(visibility) => {
                                track('COLUMN_SELECTOR_POPOVER_VIEW', {
                                    visibility: `${visibility}`,
                                });
                                this.setState({ columnsSelectorVisible: visibility });
                            }}
                            placement={'bottomLeft'}
                            destroyTooltipOnHide={true}
                            trigger={'click'}
                            content={
                                <ColumnSelector
                                    options={columnOptions.map(({ renderer, key }) => ({
                                        label: renderer.columnSelectorHeader || renderer.header,
                                        value: key,
                                    }))}
                                    preSelectedColumns={selectedColumns}
                                    mandatoryColumns={columnOptions.reduce<string[]>(
                                        (prev, curr) => {
                                            if (curr.required) {
                                                prev.push(curr.key);
                                            }
                                            return prev;
                                        },
                                        [],
                                    )}
                                    closeCallback={() => {
                                        this.setState({ columnsSelectorVisible: false });
                                    }}
                                    applySelectedColumns={this.setNewColumnPreference}
                                    maxColumnsAllowed={11}
                                />
                            }
                        >
                            <div
                                onClick={() => {
                                    this.setState({
                                        columnsSelectorVisible: !this.state.columnsSelectorVisible,
                                    });
                                }}
                                className="flex items-center justify-center w-16 h-16 cursor-pointer"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-16 h-16 fill-neutral-5"
                                >
                                    <path d="M487.4 315.7l-42.6-24.6c4.3-23.2 4.3-47 0-70.2l42.6-24.6c4.9-2.8 7.1-8.6 5.5-14-11.1-35.6-30-67.8-54.7-94.6-3.8-4.1-10-5.1-14.8-2.3L380.8 110c-17.9-15.4-38.5-27.3-60.8-35.1V25.8c0-5.6-3.9-10.5-9.4-11.7-36.7-8.2-74.3-7.8-109.2 0-5.5 1.2-9.4 6.1-9.4 11.7V75c-22.2 7.9-42.8 19.8-60.8 35.1L88.7 85.5c-4.9-2.8-11-1.9-14.8 2.3-24.7 26.7-43.6 58.9-54.7 94.6-1.7 5.4.6 11.2 5.5 14L67.3 221c-4.3 23.2-4.3 47 0 70.2l-42.6 24.6c-4.9 2.8-7.1 8.6-5.5 14 11.1 35.6 30 67.8 54.7 94.6 3.8 4.1 10 5.1 14.8 2.3l42.6-24.6c17.9 15.4 38.5 27.3 60.8 35.1v49.2c0 5.6 3.9 10.5 9.4 11.7 36.7 8.2 74.3 7.8 109.2 0 5.5-1.2 9.4-6.1 9.4-11.7v-49.2c22.2-7.9 42.8-19.8 60.8-35.1l42.6 24.6c4.9 2.8 11 1.9 14.8-2.3 24.7-26.7 43.6-58.9 54.7-94.6 1.5-5.5-.7-11.3-5.6-14.1zM256 336c-44.1 0-80-35.9-80-80s35.9-80 80-80 80 35.9 80 80-35.9 80-80 80z"></path>
                                </svg>
                            </div>
                        </Popover>
                    </div>
                </div>
            </div>
        );
    };

    toggleRcAccordian = (): void => {
        const { isCallbackAccordionOpen } = this.state;
        this.setState({
            isCallbackAccordionOpen: !isCallbackAccordionOpen,
        });
    };

    setMedicalCertPt = async (appt?: AP): Promise<void> => {
        if (!appt) {
            return;
        }

        initMedicalCertificate(appt);
    };

    showOrderMedicineDialogVisible = (appt: AP): void => {
        onOrderMedicineAction(appt);
        track('ORDER_MEDICINE');
    };

    showBookLabTestDialogVisible = (appt: AP): void => {
        onBookLabTestAction(appt);
        track('BOOK_LAB_TEST');
    };

    myProfile = getMe();

    onDoctorSelect = (doctor: MyAccessibleDoctor | undefined) => {
        this.setState({ selectedDoctor: doctor });
        const {
            searchInput,
            activeFilterIndex,
            filteredTags,
            filteredVisitType,
            appointments,
        } = this.state;

        this.applyFilterToAppointments({
            appointments,
            searchInput,
            activeFilterIndex,
            filteredTags,
            filteredVisitType,
            selectedDoctor: doctor,
        });
    };

    render(): JSX.Element {
        const { todayAppointments, isCallbackAccordionOpen, downloadExcelInProgress } = this.state;

        const rcList = todayAppointments.filter((a) => a.rc);

        const selectedSection = this.getCurrentSectionData();

        if (!selectedSection) {
            return <></>;
        }

        return (
            <div
                onClick={() =>
                    this.setState({
                        visitTypeFilterDialogVisible: undefined,
                        tagsFilterDialogVisible: undefined,
                        genericFilterVisible: false,
                    })
                }
                className="flex flex-col w-full h-full bg-white font-lato"
            >
                <div className="w-full h-full min-w-840 overflow-x-auto overflow-y-hidden">
                    <div className="flex flex-col w-full h-full col-span-11 overflow-hidden">
                        <DoctorFilter
                            doctors={this.myProfile.doctors}
                            appointments={this.state.appointments}
                            selectedDoctor={this.state.selectedDoctor}
                            setSelectedDoctor={this.onDoctorSelect}
                            clinicId={this.props.cId}
                        />
                        <div className="flex items-center justify-between w-full">
                            {this.renderSectionBoxes()}
                            {/* <div className="hidden lg:block pr-16 w-30p">
                                <DashboardInviteCodePanel
                                    withInviteCode={false}
                                    withInviteLink={false}
                                    downloadExcel={exportPtAsExcel}
                                    downloadExcelInProgress={downloadExcelInProgress}
                                />
                            </div> */}
                        </div>
                        {rcList.length > 0 && (
                            <div className="px-16 py-8">
                                <motion.div
                                    className="flex space-x-8 items-center justify-between text-14 text-actionPrimary px-16 py-8 bg-neutral-9 rounded-4 font-700 shadow-tight-1 cursor-pointer"
                                    initial={false}
                                    onClick={this.toggleRcAccordian}
                                >
                                    <div className="space-x-8 flex items-center">
                                        <div
                                            className="w-10 h-10 video-animate-flicker"
                                            style={{
                                                backgroundColor: 'red',
                                                border: 1,
                                                borderRadius: 20,
                                                boxShadow: '1px 1px 2px grey',
                                            }}
                                        ></div>
                                        <p>{rcList.length} patient(s) requested callback</p>
                                    </div>
                                    <div className="w-16 h-16 flex items-center justify-center">
                                        <svg
                                            className={`w-16 h-16 fill-neutral-black ${
                                                isCallbackAccordionOpen ? 'rotate-180' : ''
                                            } transform duration-200`}
                                            role="img"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 448 512"
                                        >
                                            <path
                                                fill="currentColor"
                                                d="M432.6 209.3l-191.1 183.1C235.1 397.8 229.1 400 224 400s-11.97-2.219-16.59-6.688L15.41 209.3C5.814 200.2 5.502 184.1 14.69 175.4c9.125-9.625 24.38-9.938 33.91-.7187L224 342.8l175.4-168c9.5-9.219 24.78-8.906 33.91 .7187C442.5 184.1 442.2 200.2 432.6 209.3z"
                                            ></path>
                                        </svg>
                                    </div>
                                </motion.div>

                                <AnimatePresence initial={false}>
                                    {isCallbackAccordionOpen && (
                                        <motion.section
                                            className="space-y-8 py-8 px-8"
                                            key="content"
                                            initial="collapsed"
                                            animate="open"
                                            exit="collapsed"
                                            variants={{
                                                open: { opacity: 1, height: 'auto' },
                                                collapsed: { opacity: 0, height: 0 },
                                            }}
                                            transition={{
                                                duration: 0.8,
                                                ease: [0.04, 0.62, 0.23, 0.98],
                                            }}
                                        >
                                            {rcList.map((a) => (
                                                <CallBackStrp
                                                    appointment={a}
                                                    onAction={() =>
                                                        onAppointmentAction(a, this.props.cId)
                                                    }
                                                />
                                            ))}
                                        </motion.section>
                                    )}
                                </AnimatePresence>
                            </div>
                        )}

                        {this.renderFilterbar()}

                        {this.renderTable()}
                    </div>
                </div>
                {/* {this.state.showFollowupFeaturesModal ? (
                    <Modal
                        contentCssClasses="min-w-504"
                        closeMe={() => {
                            track('FLW_FEATURES_MODAL_CLOSE');
                            this.setState({ showFollowupFeaturesModal: false });
                        }}
                    >
                        <FollowupFeaturesModal
                            onButtonClick={() => {
                                track('FLW_BANNER_GOT_IT_CLICKS');
                                this.setState({ showFollowupFeaturesModal: false });
                            }}
                            onClose={() => {
                                track('FLW_BANNER_CLOSE_CLICKS');
                                this.setState({ showFollowupFeaturesModal: false });
                            }}
                        />
                    </Modal>
                ) : null} */}
            </div>
        );
    }
}

const sortComparator = (sortDirection: 'ASC' | 'DESC' | 'NONE') => (a: AP, b: AP) => {
    const defaultToken = sortDirection === 'ASC' ? 9999 : -9999;

    const leftToken = a.token || defaultToken;
    const rightToken = b.token || defaultToken;

    if (!leftToken && !rightToken) {
        return 0;
    }

    if (!leftToken) {
        return 999;
    }

    if (!rightToken) {
        return -999;
    }

    if (sortDirection === 'ASC') {
        return leftToken - rightToken;
    } else if (sortDirection === 'DESC') {
        return rightToken - leftToken;
    } else {
        return a.serialNum - b.serialNum;
    }
};

export default Appointments;

const VISIT_TYPE_MAP: {
    [key: string]: {
        icon: ReactNode;
        titleTextClassName: string;
        followUpIconFillClassName: string;
        title: string;
    };
} = {
    TELE: {
        icon: (
            <svg className="w-16 h-16 fill-icons-brand" viewBox="0 0 576 512">
                <path d="M557.6 102.3c-11.53-7.406-25.88-8.391-38.28-2.688L416 147V128c0-35.35-28.65-64-64-64H64C28.65 64 0 92.65 0 128v256c0 35.35 28.65 64 64 64h288c35.35 0 64-28.65 64-64v-19.02l103.3 47.36c5.344 2.453 11.03 3.672 16.69 3.672c7.531 0 15.02-2.141 21.59-6.359C569.1 402.3 576 389.7 576 375.1V136C576 122.3 569.1 109.8 557.6 102.3zM368 384c0 8.822-7.178 16-16 16H64c-8.822 0-16-7.178-16-16V128c0-8.822 7.178-16 16-16h288c8.822 0 16 7.178 16 16V384zM528 363.5L416 312.2V199.8l112-51.33V363.5z"></path>
            </svg>
        ),
        titleTextClassName: 'text-icons-brand',
        followUpIconFillClassName: 'fill-icons-brand',
        title: 'Teleconsultation',
    },
    WALKIN: {
        icon: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                className="fill-accent-blue w-16 h-16"
            >
                <path d="M208 96c26.5 0 48-21.5 48-48S234.5 0 208 0s-48 21.5-48 48 21.5 48 48 48zm94.5 149.1l-23.3-11.8-9.7-29.4c-14.7-44.6-55.7-75.8-102.2-75.9-36-.1-55.9 10.1-93.3 25.2-21.6 8.7-39.3 25.2-49.7 46.2L17.6 213c-7.8 15.8-1.5 35 14.2 42.9 15.6 7.9 34.6 1.5 42.5-14.3L81 228c3.5-7 9.3-12.5 16.5-15.4l26.8-10.8-15.2 60.7c-5.2 20.8.4 42.9 14.9 58.8l59.9 65.4c7.2 7.9 12.3 17.4 14.9 27.7l18.3 73.3c4.3 17.1 21.7 27.6 38.8 23.3 17.1-4.3 27.6-21.7 23.3-38.8l-22.2-89c-2.6-10.3-7.7-19.9-14.9-27.7l-45.5-49.7 17.2-68.7 5.5 16.5c5.3 16.1 16.7 29.4 31.7 37l23.3 11.8c15.6 7.9 34.6 1.5 42.5-14.3 7.7-15.7 1.4-35.1-14.3-43zM73.6 385.8c-3.2 8.1-8 15.4-14.2 21.5l-50 50.1c-12.5 12.5-12.5 32.8 0 45.3s32.7 12.5 45.2 0l59.4-59.4c6.1-6.1 10.9-13.4 14.2-21.5l13.5-33.8c-55.3-60.3-38.7-41.8-47.4-53.7l-20.7 51.5z"></path>
            </svg>
        ),
        titleTextClassName: 'text-accent-blue',
        followUpIconFillClassName: 'fill-accent-blue',
        title: 'Walkin',
    },
    default: {
        icon: (
            <svg
                className="w-16 h-16 fill-textColor-secondary"
                role="img"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 448 512"
            >
                <path d="M216.1 408.1C207.6 418.3 192.4 418.3 183 408.1L119 344.1C109.7 335.6 109.7 320.4 119 311C128.4 301.7 143.6 301.7 152.1 311L200 358.1L295 263C304.4 253.7 319.6 253.7 328.1 263C338.3 272.4 338.3 287.6 328.1 296.1L216.1 408.1zM128 0C141.3 0 152 10.75 152 24V64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0zM400 192H48V448C48 456.8 55.16 464 64 464H384C392.8 464 400 456.8 400 448V192z"></path>
            </svg>
        ),
        titleTextClassName: 'text-textColor-secondary',
        followUpIconFillClassName: 'fill-textColor-secondary',
        title: 'Appointment',
    },
};

export const getVisitType = (a: AP): ReactNode => {
    const visitType = a['visit-type'] || '';
    const channel = a.channel;
    const service = a.service;

    const config =
        visitType in VISIT_TYPE_MAP ? VISIT_TYPE_MAP[visitType] : VISIT_TYPE_MAP['default'];

    return (
        <div className="flex flex-col xl:flex-row items-center space-x-4 py-8 px-8 break-all">
            <div className="flex items-center justify-center w-16 h-16">{config.icon}</div>
            <div>
                <p className="hidden xl:flex text-textColor-secondary text-13 flex items-center space-x-4">
                    <span
                        className={`hidden xl:block truncate max-w-100 ${config.titleTextClassName} text-13`}
                    >
                        {service?.service_name || config.title}
                    </span>

                    {service?.appointment_type === 'FOLLOW_UP' && (
                        <div className="w-16 h-16">
                            {followUpIcon(config.followUpIconFillClassName)}
                        </div>
                    )}
                </p>
                {visitType !== 'WALKIN' && <p className="text-neutral-5 text-11">{channel}</p>}
            </div>
        </div>
    );
};

type A_ACTION_TYPE =
    | 'ONGOING'
    | 'START_VISIT'
    | 'ADD_PATIENT'
    | 'APPOINTMENT_DETAILS'
    | 'RE_START'
    | 'PARKED_ON_OTHER_DEVICE'
    | 'PARKED'
    | 'ABORTED';

export const ONGOING = 'ONGOING';
export const PARKED = 'PARKED';
export const PARKED_ON_OTHER_DEVICE = 'PARKED_ON_OTHER_DEVICE';
export const RE_START = 'RE_START';
export const START_VISIT = 'START_VISIT';
export const ADD_PATIENT = 'ADD_PATIENT';
export const APPOINTMENT_DETAILS = 'APPOINTMENT_DETAILS';
export const ABORTED = 'ABORTED';

type OptionItemProps = {
    onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
    label: string;
    subLabel?: string;
    labelClassName?: string;
    isLoading?: boolean;
    rightIconFill?: string;
};

export const OptionItem: React.FC<OptionItemProps> = ({
    onClick,
    labelClassName,
    isLoading = false,
    label,
    subLabel,
    rightIconFill = 'fill-neutral-5',
}) => {
    return (
        <Menu.Item key={label + subLabel} className="font-lato">
            <OptionItemContent
                onClick={onClick}
                labelClassName={labelClassName}
                isLoading={isLoading}
                label={label}
                subLabel={subLabel}
                rightIconFill={rightIconFill}
            ></OptionItemContent>
        </Menu.Item>
    );
};

export function OptionItemContent({
    onClick,
    labelClassName,
    isLoading = false,
    label,
    subLabel,
    rightIconFill = 'fill-neutral-5',
}: OptionItemProps) {
    return (
        <div
            onClick={(e) => {
                onClick?.(e);
                track('OPTION_SELECT', { option: label });
            }}
            className="flex justify-between py-8 px-4 text-neutral-2 font-600 cursor-pointer"
        >
            <div>
                <p className={labelClassName}>{label}</p>
                {subLabel && <p className="text-10 text-neutral-1 opacity-50">{subLabel}</p>}
            </div>
            {isLoading ? (
                <div
                    className="pulse flex items-center mr-6 scale-110"
                    style={{ transform: 'scale(1.2)', height: '25px' }}
                >
                    <svg width="16px" height="12px">
                        <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                    </svg>
                </div>
            ) : (
                <svg
                    width="25"
                    height="25"
                    viewBox="0 0 28 28"
                    className={`${rightIconFill} cursor-pointer`}
                    preserveAspectRatio="xMidYMid meet"
                >
                    <path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z"></path>
                </svg>
            )}
        </div>
    );
}

export function getAppointmentAction(a: AP): A_ACTION_TYPE {
    if (a.visit_type === 'FLW' && ['IN', 'RV', 'BK'].includes(a.status)) {
        return START_VISIT;
    }
    return a.status === 'BK' || a.status === 'CK' || a.status === 'OG' || a.status === 'PA'
        ? a.stage === 'ONGOING'
            ? a.isLocalPresnt
                ? ONGOING
                : RE_START
            : a.stage === 'PARKED'
            ? a.isLocalPresnt
                ? PARKED
                : PARKED_ON_OTHER_DEVICE
            : a.patient_oid && a.patient.gen
            ? START_VISIT
            : ADD_PATIENT
        : a.status === 'AB'
        ? ABORTED
        : APPOINTMENT_DETAILS;
}

export const getAppointmentActionText = (t: A_ACTION_TYPE): React.ReactNode => {
    switch (t) {
        case ONGOING:
            return <p className="font-700 text-status-success">Ongoing</p>;
        case START_VISIT:
            return 'Start Visit';
        case PARKED:
            return (
                <p className="font-700" style={{ color: '#D49A00' }}>
                    Parked
                </p>
            );
        case PARKED_ON_OTHER_DEVICE:
        case RE_START:
            return (
                <div className="relative">
                    <p className="font-700 text-status-success inline-flex">
                        {t === RE_START ? (
                            'Ongoing'
                        ) : (
                            <p className="font-700" style={{ color: '#D49A00' }}>
                                Parked
                            </p>
                        )}{' '}
                        <span className="self-start w-4 h-4 min-w-4 min-h-4  animate-pulse rounded-full bg-actionPrimary" />
                    </p>
                    {/* TODO ad tooltip */}
                    {/* <p className="tooltip"></p>
                    <span className="">
                        This will restart your visit. Any progress made will be lost.
                    </span> */}
                </div>
            );
        case ADD_PATIENT:
            return 'Add Patient';
        case ABORTED:
        case APPOINTMENT_DETAILS:
            return 'Appointment Details';
        default:
            return '';
    }
};

export const getAppointmentActionTextForExcel = (t: A_ACTION_TYPE): string => {
    switch (t) {
        case ONGOING:
            return 'Ongoing';
        case START_VISIT:
            return 'Start Visit';
        case PARKED:
            return 'Parked';
        case PARKED_ON_OTHER_DEVICE:
        case RE_START:
            return t === RE_START ? 'Ongoing' : 'Parked';
        case ADD_PATIENT:
            return 'Add Patient';
        case ABORTED:
        case APPOINTMENT_DETAILS:
            return 'Appointment Details';
        default:
            return '';
    }
};

const renderCTAWithText = (text: 'Start Visit' | 'Resume Visit' | string) => {
    if (text === 'Resume Visit') {
        return (
            <Fragment>
                <div className="hidden xl:flex items-center justify-center w-16 h-16">
                    <svg
                        className="w-16 h-16 fill-textColor-secondary"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 640 512"
                    >
                        <path d="M543.3 214.5C543.5 212.3 544 210.2 544 208C544 146.1 493.9 96 432 96c-11.41 0-22.19 2.207-32.56 5.377C368.9 59.51 319.8 32 264 32C170.8 32 95.41 107.9 96.08 200.8C40.24 220.6 0 273.4 0 336C0 415.5 64.47 480 144 480h360c75.11 0 136-60.89 136-136C640 282.7 599.1 231.4 543.3 214.5zM504 432H144c-52.94 0-96-43.07-96-96c0-40.2 25.77-76.36 64.13-89.97l32.19-11.42c-.2734-38.62-.2246-33.15-.293-37.17C145.4 132.4 198.7 80 264 80c38.4 0 73.63 18.1 96.66 49.67l20.14 27.6l32.67-9.988c48.86-14.94 84.23 25.56 81.96 64.4l-2.193 37.72l36.15 10.99C566.8 271.8 592 305.4 592 344C592 392.5 552.5 432 504 432zM336.1 175c-2.211-2.211-4.863-3.961-7.797-5.178c-5.867-2.43-12.48-2.43-18.34 0C307.9 171.1 305.2 172.8 303 175l-88 88c-9.375 9.375-9.375 24.56 0 33.94s24.56 9.375 33.94 0L296 249.9V376c0 13.25 10.75 24 24 24s24-10.75 24-24V249.9l47.03 47.03C395.7 301.7 401.8 304 408 304s12.28-2.344 16.97-7.031c9.375-9.375 9.375-24.56 0-33.94L336.1 175z"></path>
                    </svg>
                </div>
                <p className="font-700 text-actionPrimary text-13 whitespace-nowrap">{text}</p>
            </Fragment>
        );
    } else {
        return (
            <Fragment>
                <div className="hidden xl:flex items-center justify-center w-16 h-16">
                    <svg
                        className="w-14 h-14 fill-actionPrimary"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M416 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c17.67 0 32 14.33 32 32v256c0 17.67-14.33 32-32 32h-64c-17.67 0-32 14.33-32 32s14.33 32 32 32h64c53.02 0 96-42.98 96-96V128C512 74.98 469 32 416 32zM342.6 233.4l-128-128c-12.51-12.51-32.76-12.49-45.25 0c-12.5 12.5-12.5 32.75 0 45.25L242.8 224H32C14.31 224 0 238.3 0 256s14.31 32 32 32h210.8l-73.38 73.38c-12.5 12.5-12.5 32.75 0 45.25s32.75 12.5 45.25 0l128-128C355.1 266.1 355.1 245.9 342.6 233.4z"></path>
                    </svg>
                </div>
                <p className="font-700 text-actionPrimary text-13">{text}</p>
            </Fragment>
        );
    }
};

export const getAppointmentActionTextForAction = (
    t: A_ACTION_TYPE,
    avoidCTAStyling?: boolean,
): React.ReactNode => {
    switch (t) {
        case ADD_PATIENT:
            return (
                <Fragment>
                    {avoidCTAStyling ? (
                        'Add Patient'
                    ) : (
                        <AntTooltip
                            placement="left"
                            content={'Add Gender before starting the visit'}
                        >
                            <div className="flex items-center space-x-4">
                                {renderCTAWithText('Add Patient')}
                            </div>
                        </AntTooltip>
                    )}
                </Fragment>
            );
        case ABORTED:
        case APPOINTMENT_DETAILS:
            return avoidCTAStyling ? (
                'Appt Details'
            ) : (
                <Fragment>
                    <div className="hidden xl:flex items-center justify-center w-16 h-16">
                        <svg
                            className="w-14 h-14 fill-actionPrimary"
                            role="img"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 448 512"
                        >
                            <path d="M152 64H296V24C296 10.75 306.7 0 320 0C333.3 0 344 10.75 344 24V64H384C419.3 64 448 92.65 448 128V448C448 483.3 419.3 512 384 512H64C28.65 512 0 483.3 0 448V128C0 92.65 28.65 64 64 64H104V24C104 10.75 114.7 0 128 0C141.3 0 152 10.75 152 24V64zM48 448C48 456.8 55.16 464 64 464H384C392.8 464 400 456.8 400 448V192H48V448z"></path>
                        </svg>
                    </div>
                    <p className="font-700 text-actionPrimary text-13">Appt Details</p>
                </Fragment>
            );
        case ONGOING:
        case PARKED:
            return avoidCTAStyling ? 'Resume Visit' : renderCTAWithText('Resume Visit');
        case START_VISIT:
            return avoidCTAStyling ? 'Start Visit' : renderCTAWithText('Start Visit');

        case PARKED_ON_OTHER_DEVICE:
        case RE_START:
            return (
                <Fragment>
                    {avoidCTAStyling ? 'Resume Visit' : renderCTAWithText('Resume Visit')}
                    <p
                        className={`self-start w-4 h-4 min-w-4 min-h-4 rounded-full ${
                            avoidCTAStyling ? 'bg-white' : 'bg-actionPrimary'
                        } animate-pulse`}
                    ></p>
                    {!avoidCTAStyling && (
                        <div className="tooltip-tip left">
                            This visit might have updates from your staff.
                        </div>
                    )}
                </Fragment>
            );

        default:
            return '';
    }
};

export const getPatientNameText = (patient: AppointmentPatient): string => {
    return [patient.fn, patient.mn, patient.ln].filter(Boolean).join(' ');
};

export const getPhoneNumber = (patient: AppointmentPatient): string => {
    const phoneNumber =
        patient.mobile.length > 0
            ? patient.mobile.substr(patient.mobile.length - 10)
            : patient.mobile.replace('+91', '');

    return phoneNumber;
};

const CallBackStrp: React.FC<{ appointment: AP; onAction: () => void }> = ({
    appointment,
    onAction,
}) => {
    const onPtnameClick = async () => {
        appointment.patient_oid &&
            navigate('push', `/app/patient/${appointment.patient_oid}/visit`);

        return;
    };

    const [seconds, setSeconds] = useState(
        (new Date().getTime() - new Date(appointment.callback_requested || new Date()).getTime()) /
            1000,
    );

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined = undefined;
        interval = setInterval(() => {
            setSeconds((seconds) => seconds + 1);
        }, 1000);

        return () => interval && clearInterval(interval);
    }, [seconds]);

    return (
        <div className="flex items-center justify-between px-10 py-8 shadow-tight-1 rounded-4 bg-status-infoBG">
            <div className="flex items-center space-x-8">
                <div className="flex items-center justify-center w-18 h-18">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 640 512"
                        width="18"
                        height="18"
                        className="fill-actionPrimary"
                    >
                        <path d="M112,192a24,24,0,0,0-24-24H24a24,24,0,0,0,0,48H88A24,24,0,0,0,112,192ZM51.66,64.78l55.42,32a24,24,0,1,0,24-41.56l-55.42-32a24,24,0,1,0-24,41.56ZM520.94,100a23.8,23.8,0,0,0,12-3.22l55.42-32a24,24,0,0,0-24-41.56l-55.42,32a24,24,0,0,0,12,44.78ZM320,512a64,64,0,0,0,64-64H256A64,64,0,0,0,320,512ZM616,168H552a24,24,0,0,0,0,48h64a24,24,0,0,0,0-48ZM479.92,208c0-77.69-54.48-139.91-127.94-155.16V32a32,32,0,1,0-64,0V52.84C214.56,68.09,160.08,130.31,160.08,208c0,102.31-36.14,133.53-55.47,154.28A31.28,31.28,0,0,0,96,384c.11,16.41,13,32,32.09,32H511.91c19.11,0,32-15.59,32.09-32a31.23,31.23,0,0,0-8.61-21.72C516.06,341.53,479.92,310.31,479.92,208Z" />
                    </svg>
                </div>
                <div
                    className="video-animate-flicker"
                    style={{
                        height: 10,
                        width: 10,
                        backgroundColor: 'red',
                        border: 1,
                        borderRadius: 20,
                        boxShadow: '1px 1px 2px grey',
                    }}
                ></div>
                <p className="text-actionPrimary font-700 text-14">
                    <span onClick={onPtnameClick} className="cursor-pointer select-all">
                        {[appointment.patient.fn, appointment.patient.mn, appointment.patient.ln]
                            .filter(Boolean)
                            .join(' ')}
                    </span>
                    <span className="text-neutral-black">
                        {' '}
                        has requested a callback since ({lead0(Math.floor(seconds / (60 * 60)))}:
                        {lead0(Math.floor((seconds % 3600) / 60))}:{lead0(Math.floor(seconds % 60))}
                        )
                    </span>
                </p>
            </div>
            <div className="flex items-center space-x-16 leading-5 ">
                <div onClick={onAction} className="flex space-x-4 cursor-pointer">
                    <div className="flex items-center justify-center w-20 h-20">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 576 512"
                            width="16"
                            height="16"
                            className="fill-actionPrimary"
                        >
                            <path d="M543.9 96c-6.2 0-12.5 1.8-18.2 5.7L416 170.1v-58.3c0-26.4-23.2-47.8-51.8-47.8H51.8C23.2 64 0 85.4 0 111.8v288.4C0 426.6 23.2 448 51.8 448h312.4c28.6 0 51.8-21.4 51.8-47.8v-58.3l109.7 68.3c5.7 4 12.1 5.7 18.2 5.7 16.6 0 32.1-13 32.1-31.5V127.5C576 109 560.5 96 543.9 96zM368 200v198.9c-.6.4-1.8 1.1-3.8 1.1H51.8c-2 0-3.2-.6-3.8-1.1V113.1c.6-.4 1.8-1.1 3.8-1.1h312.4c2 0 3.2.6 3.8 1.1V200zm160 155.2l-112-69.8v-58.7l112-69.8v198.3z" />
                        </svg>
                    </div>
                    <p className="font-700 text-actionPrimary text-14">
                        {getAppointmentActionText(getAppointmentAction(appointment))}
                    </p>
                </div>
            </div>
        </div>
    );
};

export const renderPaymentStatus = (a: AP): ReactNode => {
    return (
        <div className="flex items-center space-x-4 py-10 px-8">
            <div className="hidden xl:flex items-center justify-center w-14 h-14">
                {!a.billStatus ? null : a.billStatus === 'paid' ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="fill-status-success w-14 h-14 "
                    >
                        <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path>
                    </svg>
                ) : a.billStatus === 'unbilled' ? (
                    <svg
                        className="w-14 h-14 fill-status-warning"
                        role="img"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                    >
                        <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM232 152C232 138.8 242.8 128 256 128s24 10.75 24 24v128c0 13.25-10.75 24-24 24S232 293.3 232 280V152zM256 400c-17.36 0-31.44-14.08-31.44-31.44c0-17.36 14.07-31.44 31.44-31.44s31.44 14.08 31.44 31.44C287.4 385.9 273.4 400 256 400z"></path>
                    </svg>
                ) : a.billStatus === 'pending' ? (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 512 512"
                        className="w-16 h-16 fill-actionNeutral"
                    >
                        <path d="M256,8C119,8,8,119,8,256S119,504,256,504,504,393,504,256,393,8,256,8Zm92.49,313h0l-20,25a16,16,0,0,1-22.49,2.5h0l-67-49.72a40,40,0,0,1-15-31.23V112a16,16,0,0,1,16-16h32a16,16,0,0,1,16,16V256l58,42.5A16,16,0,0,1,348.49,321Z"></path>
                    </svg>
                ) : (
                    // : a.payment_status === 'failed' ? (
                    //     <svg
                    //         xmlns="http://www.w3.org/2000/svg"
                    //         viewBox="0 0 320 512"
                    //         className="w-16 h-16 fill-status-error"
                    //     >
                    //         <path d="M207.6 256l107.72-107.72c6.23-6.23 6.23-16.34 0-22.58l-25.03-25.03c-6.23-6.23-16.34-6.23-22.58 0L160 208.4 52.28 100.68c-6.23-6.23-16.34-6.23-22.58 0L4.68 125.7c-6.23 6.23-6.23 16.34 0 22.58L112.4 256 4.68 363.72c-6.23 6.23-6.23 16.34 0 22.58l25.03 25.03c6.23 6.23 16.34 6.23 22.58 0L160 303.6l107.72 107.72c6.23 6.23 16.34 6.23 22.58 0l25.03-25.03c6.23-6.23 6.23-16.34 0-22.58L207.6 256z" />
                    //     </svg>
                    // )
                    ''
                )}
            </div>
            {!a.billStatus ? null : a.billStatus === 'paid' ? (
                <p className="text-13 leading-4 text-status-success">Paid</p>
            ) : a.billStatus === 'unbilled' ? (
                <p className="text-13 text-status-warning">Unbilled</p>
            ) : (
                <p className="text-13 text-actionNeutral">Pending</p>
            )}
        </div>
    );
};

type ApptChipItem = {
    icon?: ReactNode;
    text: string;
    textColor?: string;
    backgroundColor?: string;
    showBorder?: boolean;
    borderColor?: string;
    additionalCssProps?: React.CSSProperties;
};

function ApptItemChip(aptChipItem: ApptChipItem): JSX.Element {
    const {
        icon,
        text,
        textColor = '',
        showBorder = false,
        borderColor = '',
        backgroundColor = '',
        additionalCssProps = {},
    } = aptChipItem;

    let cssProps: React.CSSProperties = {
        color: textColor,
        backgroundColor,
    };

    if (showBorder) {
        cssProps['borderWidth'] = '1px 1px 1px 1px';
        cssProps['borderStyle'] = 'solid';
        cssProps['borderColor'] = borderColor;
    }

    if (Object.keys(additionalCssProps).length > 0) {
        cssProps = { ...cssProps, ...additionalCssProps };
    }

    return (
        <div
            style={cssProps}
            className={`flex items-center space-x-8 px-8 py-4 font-700 rounded-8`}
        >
            {!!icon ? <>{icon}</> : null}
            <p className="font-700 uppercase text-10 truncate">{text}</p>
        </div>
    );
}

function TagPopoverContent({
    showTagForm,
    closeForm,
    onTagCreation,
    aid,
    allTags,
    filteredTags,
    selectedTags,
    onTagClick,
    onAddNewClick,
    closePopover,
}: {
    showTagForm: boolean;
    closeForm: () => void;
    onTagCreation: (tagRecord: CreateTagBody, aid?: string | undefined) => CreateAptTagResponse;
    aid: string;
    allTags: TagRecord[];
    filteredTags: Set<number>;
    selectedTags: number[];
    onTagClick: (aid: string, tagId: number) => void;
    onAddNewClick: () => void;
    closePopover: () => void;
}) {
    useEffect(() => {
        function closePopoverOnEsc(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                closePopover();
            }
        }
        document.addEventListener('keydown', closePopoverOnEsc);
        return () => {
            document.removeEventListener('keydown', closePopoverOnEsc);
        };
    }, []);
    if (showTagForm) {
        return <CreateTagsForm closeForm={closeForm} onAdditionOfTag={onTagCreation} aid={aid} />;
    }

    return (
        <AptTagsPanel
            allAppointmentTags={allTags}
            selectedTags={filteredTags}
            taggedTagIds={selectedTags}
            aid={aid}
            onTagClick={onTagClick}
            onAddNewClick={onAddNewClick}
        />
    );
}

function MinimizedTags({
    filteredTags,
    selectedTags,
    aid,
    onTagCreation,
    onTagClick,
    appointmentConfigs,
}: {
    filteredTags: Set<number>;
    selectedTags: number[];
    aid: string;
    onTagCreation: TagFormPropsType['onAdditionOfTag'];
    onTagClick: AptTagsPanelProps['onTagClick'];
    appointmentConfigs: AppointmentConfigs;
}) {
    const { tags: allTags } = appointmentConfigs;
    const [
        { showAllTagsSelector, showTagForm, noOfTagsToShow, widthOfEachTag },
        setState,
    ] = useState({
        showAllTagsSelector: false,
        showTagForm: false,
        noOfTagsToShow: 0,
        widthOfEachTag: 75,
    });

    const tagContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tagContainerRef.current?.clientWidth) {
            const availableWidthForTags = tagContainerRef.current?.clientWidth - 40;
            let tagWidth = 75;
            const noOfTagsPossibleToShowWithoutConstraints = Math.floor(availableWidthForTags / 75);
            if (noOfTagsPossibleToShowWithoutConstraints === 0) {
                tagWidth = availableWidthForTags;
            }
            const noOfTagsPossibleToShow = Math.max(1, noOfTagsPossibleToShowWithoutConstraints);

            setState((prev) => ({
                ...prev,
                noOfTagsToShow: noOfTagsPossibleToShow,
                widthOfEachTag: tagWidth,
            }));
        }
    }, [tagContainerRef, appointmentConfigs]);

    const tagsMap = new Map<number, TagRecordWithCount>();

    for (const tag of allTags) {
        const { tag_id } = tag;
        tagsMap.set(tag_id, tag);
    }

    const tagsInSortedOrder = [...selectedTags].sort((l, r) => {
        let score = 0;
        if (filteredTags.has(l)) {
            score -= 10;
        } else if (filteredTags.has(r)) {
            score += 10;
        }
        const tagOnLeft = tagsMap.get(l);
        const tagonRight = tagsMap.get(r);

        if (tagOnLeft && tagonRight) {
            score += tagOnLeft?.label.localeCompare(tagonRight?.label);
        }

        return score;
    });

    const tagsToRender = tagsInSortedOrder.filter((tagData, idx) => idx < noOfTagsToShow);

    const noOfTagsHidden = tagsInSortedOrder.length - noOfTagsToShow;

    function closeForm() {
        setState((prev) => ({ ...prev, showTagForm: false }));
    }

    function closePopover() {
        setState((prev) => ({ ...prev, showAllTagsSelector: false }));
    }

    const tooltipContent = (
        <div className={'px-12 py-11 bg-white flex space-x-8'}>
            {tagsInSortedOrder.map((tagId) => {
                const currentTag = tagsMap.get(tagId);

                if (!currentTag) {
                    return null;
                }

                return (
                    <ApptItemChip
                        text={currentTag.label}
                        textColor={currentTag.hex}
                        backgroundColor={currentTag.bg_hex}
                    />
                );
            })}
        </div>
    );

    return (
        <Popover
            trigger={'click'}
            visible={showAllTagsSelector}
            onVisibleChange={(visibility) => {
                track('TAGS_FORM_POPOVER_VIEW', {
                    visibility: `${visibility}`,
                });
                if (visibility) {
                    setState((prev) => ({
                        ...prev,
                        showAllTagsSelector: visibility,
                        showTagForm: false,
                    }));
                } else {
                    setState((prev) => ({
                        ...prev,
                        showAllTagsSelector: visibility,
                    }));
                }
            }}
            overlayClassName={'popover-p-0 popover-rounded-16'}
            placement={'leftTop'}
            overlayInnerStyle={{ borderRadius: '16px' }}
            destroyTooltipOnHide={true}
            content={
                <TagPopoverContent
                    showTagForm={showTagForm}
                    closeForm={closeForm}
                    aid={aid}
                    onTagCreation={onTagCreation}
                    onTagClick={onTagClick}
                    onAddNewClick={() => {
                        setState((prev) => ({ ...prev, showTagForm: true }));
                    }}
                    allTags={allTags}
                    selectedTags={selectedTags}
                    filteredTags={filteredTags}
                    closePopover={closePopover}
                />
            }
        >
            <div
                ref={tagContainerRef}
                className="appt-tags cursor-pointer space-x-5 hide-scrollbar"
            >
                {selectedTags.length === 0 ? (
                    <ApptItemChip
                        text="+ Add a Tag"
                        textColor="#004BA8"
                        backgroundColor="#F4F6F9"
                    />
                ) : (
                    <Popover
                        overlayClassName="popover-p-0 popover-rounded-16"
                        placement="top"
                        content={tooltipContent}
                        visible={
                            detectTouchscreen() ? false : !!showAllTagsSelector ? false : undefined
                        }
                        onVisibleChange={(visibility) => {
                            track('TAGS_OVERVIEW_POPOVER_VIEW', {
                                visibility: `${visibility}`,
                            });
                        }}
                        overlayInnerStyle={{ borderRadius: '16px' }}
                    >
                        <div className="flex space-x-5">
                            {tagsToRender.map((tagId) => {
                                const currentTag = tagsMap.get(tagId);

                                if (!currentTag) {
                                    return null;
                                }

                                return (
                                    <ApptItemChip
                                        text={currentTag.label}
                                        textColor={currentTag.hex}
                                        backgroundColor={currentTag.bg_hex}
                                        additionalCssProps={{
                                            maxWidth: `${widthOfEachTag}px`,
                                        }}
                                    />
                                );
                            })}

                            {noOfTagsHidden > 0 ? (
                                <div
                                    className={`flex flex-wrap max-w-360 items-center px-8 py-4 font-700 rounded-8 bg-darwin-primary-200`}
                                >
                                    <p className="font-700 uppercase text-10 truncate text-darwin-primary-500">{`+ ${noOfTagsHidden}`}</p>
                                </div>
                            ) : null}
                        </div>
                    </Popover>
                )}
            </div>
        </Popover>
    );
}

// function FollowupFeaturesModal({
//     onButtonClick,
//     onClose,
// }: {
//     onButtonClick: () => void;
//     onClose: () => void;
// }) {
//     return (
//         <div className={'flex flex-col pt-30 pb-40 px-30 items-center relative'}>
//             <p className={'text-darwin-neutral-1000 text-24 leading-relaxed text-center mb-26'}>
//                 Introducing Follow up Queue
//             </p>
//             <div className={'flex flex-col space-y-40'}>
//                 <FeatureHighlight
//                     title="Increase follow-up adherence"
//                     subtitle="Eka will send automated timely reminders to all your follow up patients "
//                     icon={
//                         <div
//                             className={
//                                 'min-w-56 min-h-56 w-56 h-56 flex items-center justify-center bg-darwin-accent-mauve-200 rounded-28'
//                             }
//                         >
//                             <svg
//                                 width="24"
//                                 height="24"
//                                 viewBox="0 0 24 24"
//                                 fill="none"
//                                 xmlns="http://www.w3.org/2000/svg"
//                                 className={'fill-darwin'}
//                             >
//                                 <path
//                                     d="M22.5 13.0391C21.6562 13.0391 21 13.7422 21 14.5391C21 17.0234 18.9375 19.0391 16.5 19.0391H9V17.1641C9 16.7422 8.71875 16.3203 8.29688 16.1797C7.92188 15.9922 7.45312 16.0391 7.07812 16.3672L3.32812 19.7422C3.09375 19.9297 3 20.2578 3 20.5391C3 20.8672 3.09375 21.1953 3.32812 21.3828L7.07812 24.7578C7.3125 24.9453 7.59375 25.0391 7.875 25.0391C8.01562 25.0391 8.15625 25.0391 8.29688 24.9453C8.71875 24.8047 9 24.3828 9 23.9141V22.0391H16.5C20.625 22.0391 24 18.7109 24 14.5391C24 13.7422 23.2969 13.0391 22.5 13.0391ZM7.5 7.03906H14.9531L15 8.91406C15 9.38281 15.2344 9.80469 15.6562 9.94531C15.7969 10.0391 15.9375 10.0391 16.0781 10.0391C16.3125 10.0391 16.5938 9.94531 16.8281 9.75781L20.5781 6.38281C20.8594 6.19531 21 5.86719 21 5.53906C21 5.16406 20.8594 4.88281 20.625 4.69531L16.875 1.32031C16.5469 0.992188 16.0312 0.945312 15.6562 1.13281C15.2344 1.32031 14.9531 1.74219 14.9531 2.16406L15 4.03906H7.5C3.32812 4.03906 0 7.41406 0 11.5391C0 12.3828 0.65625 13.0391 1.5 13.0391C2.29688 13.0391 3 12.3828 3 11.5391C3 9.10156 5.01562 7.03906 7.5 7.03906Z"
//                                     fill="#A852D0"
//                                 />
//                             </svg>
//                         </div>
//                     }
//                 />
//                 <FeatureHighlight
//                     title="Know who’s coming & who’s not"
//                     subtitle="Eka’s reminder system gets you confirmation from patients - helping you plan your day better"
//                     icon={
//                         <div
//                             className={
//                                 'min-w-56 min-h-56 w-56 h-56 flex items-center justify-center bg-darwin-accent-green-200 rounded-28'
//                             }
//                         >
//                             <svg
//                                 width="24"
//                                 height="25"
//                                 viewBox="0 0 24 25"
//                                 fill="none"
//                                 xmlns="http://www.w3.org/2000/svg"
//                             >
//                                 <path
//                                     d="M12 0.0390625C5.34375 0.0390625 0 5.42969 0 12.0391C0 18.6953 5.34375 24.0391 12 24.0391C18.6094 24.0391 24 18.6953 24 12.0391C24 5.42969 18.6094 0.0390625 12 0.0390625ZM17.3906 9.97656L11.3906 15.9766C11.1562 16.2578 10.8281 16.3516 10.5 16.3516C10.125 16.3516 9.79688 16.2578 9.5625 15.9766L6.5625 12.9766C6.04688 12.4609 6.04688 11.6641 6.5625 11.1484C7.07812 10.6328 7.875 10.6328 8.39062 11.1484L10.5 13.2109L15.5625 8.14844C16.0781 7.63281 16.875 7.63281 17.3906 8.14844C17.9062 8.66406 17.9062 9.46094 17.3906 9.97656Z"
//                                     fill="#1B9C7D"
//                                 />
//                             </svg>
//                         </div>
//                     }
//                 />
//                 <FeatureHighlight
//                     title="Send reminders when you want"
//                     subtitle="Trigger reminders for particular patients as and when you want outside of scheduled reminders"
//                     icon={
//                         <div
//                             className={
//                                 'min-w-56 min-h-56 w-56 h-56 flex items-center justify-center bg-darwin-accent-bright-yellow-200 rounded-28'
//                             }
//                         >
//                             <svg
//                                 width="21"
//                                 height="25"
//                                 viewBox="0 0 21 25"
//                                 fill="none"
//                                 xmlns="http://www.w3.org/2000/svg"
//                             >
//                                 <path
//                                     d="M10.4531 24.0391C12.1406 24.0391 13.5 22.7266 13.5 21.0391H7.5C7.5 22.7266 8.8125 24.0391 10.4531 24.0391ZM20.5781 17.0547C19.6875 16.0703 17.9531 14.6172 17.9531 9.78906C17.9531 6.17969 15.4219 3.27344 12 2.52344V1.53906C12 0.742188 11.2969 0.0390625 10.5 0.0390625C9.65625 0.0390625 9 0.742188 9 1.53906V2.52344C5.53125 3.27344 3 6.17969 3 9.78906C3 14.6172 1.26562 16.0703 0.375 17.0547C0.09375 17.3359 0 17.7109 0 18.0391C0 18.8359 0.609375 19.5391 1.5 19.5391H19.4531C20.3906 19.5391 20.9531 18.8359 21 18.0391C21 17.7109 20.8594 17.3359 20.5781 17.0547Z"
//                                     fill="#EDAE0C"
//                                 />
//                             </svg>
//                         </div>
//                     }
//                 />
//                 <div className={'flex justify-center items-center'}>
//                     <button
//                         onClick={onButtonClick}
//                         className={
//                             'px-18 py-9 text-white bg-darwin-accent-bright-blue-800 rounded-4 font-700 text-16 leading-tight'
//                         }
//                     >
//                         Ok, Got it
//                     </button>
//                 </div>
//             </div>
//             <div onClick={onClose} className={'absolute right-26 top-22 cursor-pointer'}>
//                 <svg
//                     width="12"
//                     height="12"
//                     viewBox="0 0 12 12"
//                     fill="none"
//                     xmlns="http://www.w3.org/2000/svg"
//                 >
//                     <path
//                         d="M11.3438 10.4336C11.6602 10.7852 11.6602 11.3125 11.3438 11.6289C10.9922 11.9805 10.4648 11.9805 10.1484 11.6289L6 7.44531L1.81641 11.6289C1.46484 11.9805 0.9375 11.9805 0.621094 11.6289C0.269531 11.3125 0.269531 10.7852 0.621094 10.4336L4.80469 6.25L0.621094 2.06641C0.269531 1.71484 0.269531 1.1875 0.621094 0.871094C0.9375 0.519531 1.46484 0.519531 1.78125 0.871094L6 5.08984L10.1836 0.90625C10.5 0.554688 11.0273 0.554688 11.3438 0.90625C11.6953 1.22266 11.6953 1.75 11.3438 2.10156L7.16016 6.25L11.3438 10.4336Z"
//                         fill="black"
//                     />
//                 </svg>
//             </div>
//         </div>
//     );
// }

// function FeatureHighlight({
//     icon,
//     title,
//     subtitle,
// }: {
//     icon: JSX.Element;
//     title: string;
//     subtitle: string;
// }) {
//     return (
//         <div className={'flex flex-row items-start space-x-24'}>
//             {icon}
//             <div className={'flex flex-col space-y-8'}>
//                 <div className="font-700 text-16 leading-tight text-darwin-neutral-1000">
//                     {title}
//                 </div>
//                 <div className="font-400 text-16 leading-tight text-darwin-neutral-700">
//                     {subtitle}
//                 </div>
//             </div>
//         </div>
//     );
// }

function PatientFlwReminderStatus(a: AP & { messagePurpose?: string }): JSX.Element {
    const {
        now,
        alreadySentMessages,
        failedToDeliverMessages,
        sortedMessagesNearestToNow,
    } = useMemo(() => {
        const now = new Date();
        const reminderMessages = a?.comm_meta?.filter((item) => {
            if (a.messagePurpose) {
                return item.purpose === a.messagePurpose;
            }
            return item.purpose === FollowupMessagePurpose.FOLLOWUP_REMINDER;
        });
        const sortedMessagesNearestToNow = (reminderMessages || [])?.sort((a, b) => {
            if (!(a.schFor || a.sentAt)) {
                return 9999;
            } else if (!(b.schFor || b.sentAt)) {
                return -9999;
            }

            const messageASentTime = new Date(a.sentAt || a.schFor!);
            const messageBSentTime = new Date(b.sentAt || b.schFor!);

            const timeForMessageA = Math.abs(now.getTime() - messageASentTime.getTime());
            const timeForMessageB = Math.abs(now.getTime() - messageBSentTime.getTime());
            return timeForMessageA - timeForMessageB;
        });

        const sentMessageStatusWhitelist = new Set<CommunicationMetaItem['status']>([
            'Delivered',
            'Responded',
            'Read',
        ]);

        const alreadySentMessages = sortedMessagesNearestToNow.filter(
            (a) =>
                sentMessageStatusWhitelist.has(a.status) ||
                (a.status === 'Sent' &&
                    a.sentAt &&
                    Math.abs(new Date(a.sentAt).getTime() - now.getTime()) <= 1 * 60 * 1000),
        );

        const failedToDeliverMessages = sortedMessagesNearestToNow.filter(
            (a) =>
                a.status === 'Sent' &&
                a.sentAt &&
                Math.abs(new Date(a.sentAt).getTime() - now.getTime()) > 1 * 60 * 1000,
        );

        return { now, alreadySentMessages, failedToDeliverMessages, sortedMessagesNearestToNow };
    }, [a.comm_meta]);

    const [nearestMessageToNow] = sortedMessagesNearestToNow;

    if (
        (sortedMessagesNearestToNow?.length === 0 && a.visit_type === 'FLW' && !a.messagePurpose) ||
        nearestMessageToNow?.status === 'Scheduled'
    ) {
        let reminderScheduledDate = moment(a.date)
            .subtract(1, 'day')
            .set('minutes', 30)
            .set('hours', 8);

        if (Math.floor(now.getTime() / 1000) - reminderScheduledDate.unix() >= 0) {
            reminderScheduledDate = reminderScheduledDate.add(1, 'day');
        }

        return (
            <PatientReminderStatusText
                title="Scheduled"
                titleColor={'text-darwin-accent-symptoms-blue-900'}
                subtitle={
                    nearestMessageToNow?.status === 'Scheduled'
                        ? `${moment(nearestMessageToNow.schFor).format('DD MMM | h:mm A')}`
                        : `${reminderScheduledDate.format('DD MMM')} | 8:30 AM`
                }
                icon={
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                        className="fill-darwin-accent-symptoms-blue-900 h-14 w-14 mt-4"
                    >
                        <path d="M6.33333 0.375C3.28255 0.375 0.833333 2.8457 0.833333 5.875C0.833333 8.92578 3.28255 11.375 6.33333 11.375C9.36263 11.375 11.8333 8.92578 11.8333 5.875C11.8333 2.8457 9.36263 0.375 6.33333 0.375ZM8.69661 7.25C8.61068 7.42188 8.4388 7.50781 8.26693 7.50781C8.15951 7.50781 8.07357 7.48633 8.00911 7.44336L6.07552 6.32617C5.90365 6.24023 5.81771 6.06836 5.81771 5.875V2.95312C5.81771 2.67383 6.03255 2.4375 6.33333 2.4375C6.61263 2.4375 6.84896 2.67383 6.84896 2.95312V5.5957L8.52474 6.5625C8.76107 6.69141 8.84701 7.01367 8.69661 7.25Z" />
                    </svg>
                }
            />
        );
    }

    if (
        sortedMessagesNearestToNow.filter((item) => item.status !== 'Scheduled').length ===
            failedToDeliverMessages.length &&
        nearestMessageToNow?.status === 'Sent'
    ) {
        //all messages failed to deliver
        return (
            <PatientReminderStatusText
                title="Not Delivered"
                titleColor={'text-darwin-neutral-500'}
                subtitle={
                    failedToDeliverMessages.length >= 1
                        ? `Retry count: ${failedToDeliverMessages.length
                              .toString()
                              .padStart(2, '0')}`
                        : ''
                }
                icon={
                    <AntTooltip
                        placement="left"
                        content={
                            'Eka sends follow up reminders on Whatsapp. Either the phone number is not on Whatsapp or it is unreachable'
                        }
                    >
                        <svg
                            className={'w-14 h-14 fill-darwin-neutral-500 mt-4 h-14 w-14'}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 512 512"
                        >
                            <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM216 336h24V272H216c-13.3 0-24-10.7-24-24s10.7-24 24-24h48c13.3 0 24 10.7 24 24v88h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H216c-13.3 0-24-10.7-24-24s10.7-24 24-24zm40-208a32 32 0 1 1 0 64 32 32 0 1 1 0-64z" />
                        </svg>
                    </AntTooltip>
                }
            />
        );
    }

    if (alreadySentMessages.length > 0 && !!alreadySentMessages[0].sentAt) {
        const minutesSentBefore = Math.floor(
            (now.getTime() - new Date(alreadySentMessages[0].sentAt).getTime()) / (1000 * 60),
        );
        const hoursSentBefore = Math.floor(minutesSentBefore / 60);

        let titleToRender = 'Sent';

        if (minutesSentBefore < 60) {
            titleToRender = `Sent ${minutesSentBefore}m ago`;
        } else if (hoursSentBefore < 72) {
            titleToRender = `Sent ${hoursSentBefore}h ago`;
        }

        return (
            <PatientReminderStatusText
                title={titleToRender}
                icon={
                    <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className={'fill-darwin-neutral-900 mt-4 h-14 w-14'}
                    >
                        <path d="M6.33333 0.375C3.28255 0.375 0.833333 2.8457 0.833333 5.875C0.833333 8.92578 3.28255 11.375 6.33333 11.375C9.36263 11.375 11.8333 8.92578 11.8333 5.875C11.8333 2.8457 9.36263 0.375 6.33333 0.375ZM8.80404 4.92969L6.05404 7.67969C5.94661 7.80859 5.79622 7.85156 5.64583 7.85156C5.47396 7.85156 5.32357 7.80859 5.21615 7.67969L3.84115 6.30469C3.60482 6.06836 3.60482 5.70312 3.84115 5.4668C4.07747 5.23047 4.44271 5.23047 4.67904 5.4668L5.64583 6.41211L7.96615 4.0918C8.20247 3.85547 8.56771 3.85547 8.80404 4.0918C9.04036 4.32812 9.04036 4.69336 8.80404 4.92969Z" />
                    </svg>
                }
                titleColor={'text-darwin-neutral-1000'}
                subtitle={`Total: ${alreadySentMessages.length.toString().padStart(2, '0')}`}
            />
        );
    }

    return <div className="reminders-count">--</div>;
}

function reminderStatusForExcel(a: AP & { messagePurpose?: string }): string {
    const now = new Date();
    const reminderMessages = a?.comm_meta?.filter((item) => {
        if (a.messagePurpose) {
            return item.purpose === a.messagePurpose;
        }
        return item.purpose === FollowupMessagePurpose.FOLLOWUP_REMINDER;
    });
    const sortedMessagesNearestToNow = (reminderMessages || [])?.sort((a, b) => {
        if (!(a.schFor || a.sentAt)) {
            return 9999;
        } else if (!(b.schFor || b.sentAt)) {
            return -9999;
        }

        const messageASentTime = new Date(a.sentAt || a.schFor!);
        const messageBSentTime = new Date(b.sentAt || b.schFor!);

        const timeForMessageA = Math.abs(now.getTime() - messageASentTime.getTime());
        const timeForMessageB = Math.abs(now.getTime() - messageBSentTime.getTime());
        return timeForMessageA - timeForMessageB;
    });

    const sentMessageStatusWhitelist = new Set<CommunicationMetaItem['status']>([
        'Delivered',
        'Responded',
        'Read',
    ]);

    const alreadySentMessages = sortedMessagesNearestToNow.filter(
        (a) =>
            sentMessageStatusWhitelist.has(a.status) ||
            (a.status === 'Sent' &&
                a.sentAt &&
                Math.abs(new Date(a.sentAt).getTime() - now.getTime()) <= 1 * 60 * 1000),
    );

    const failedToDeliverMessages = sortedMessagesNearestToNow.filter(
        (a) =>
            a.status === 'Sent' &&
            a.sentAt &&
            Math.abs(new Date(a.sentAt).getTime() - now.getTime()) > 1 * 60 * 1000,
    );

    const [nearestMessageToNow] = sortedMessagesNearestToNow;

    if (
        (sortedMessagesNearestToNow?.length === 0 && a.visit_type === 'FLW' && !a.messagePurpose) ||
        nearestMessageToNow?.status === 'Scheduled'
    ) {
        let reminderScheduledDate = moment(a.date)
            .subtract(1, 'day')
            .set('minutes', 30)
            .set('hours', 8);

        if (Math.floor(now.getTime() / 1000) - reminderScheduledDate.unix() >= 0) {
            reminderScheduledDate = reminderScheduledDate.add(1, 'day');
        }

        return `Scheduled | ${
            nearestMessageToNow?.status === 'Scheduled'
                ? `${moment(nearestMessageToNow.schFor).format('DD MMM | h:mm A')}`
                : `${reminderScheduledDate.format('DD MMM')} | 8:30 AM`
        }`;
    }

    if (
        sortedMessagesNearestToNow.filter((item) => item.status !== 'Scheduled').length ===
            failedToDeliverMessages.length &&
        nearestMessageToNow?.status === 'Sent'
    ) {
        //all messages failed to deliver
        `Not Delivered ${
            failedToDeliverMessages.length >= 1
                ? ` - Retry count: ${failedToDeliverMessages.length.toString().padStart(2, '0')}`
                : ''
        }`;
    }

    if (alreadySentMessages.length > 0 && !!alreadySentMessages[0].sentAt) {
        const minutesSentBefore = Math.floor(
            (now.getTime() - new Date(alreadySentMessages[0].sentAt).getTime()) / (1000 * 60),
        );
        const hoursSentBefore = Math.floor(minutesSentBefore / 60);

        let titleToRender = 'Sent';

        if (minutesSentBefore < 60) {
            titleToRender = `Sent ${minutesSentBefore}m ago`;
        } else if (hoursSentBefore < 72) {
            titleToRender = `Sent ${hoursSentBefore}h ago`;
        }

        return `${titleToRender} - ${`Total: ${alreadySentMessages.length
            .toString()
            .padStart(2, '0')}`}`;
    }

    return '--';
}

function PatientReminderStatusText({
    icon,
    title,
    subtitle,
    titleColor,
    subtitleColor = 'text-darwin-neutral-500',
}: {
    icon: JSX.Element;
    title: string;
    subtitle?: string;
    titleColor: string;
    subtitleColor?: string;
}) {
    return (
        <div className="reminders-count flex space-x-7 self-start items-start">
            {icon}
            <div className="flex flex-col">
                <p className={`text-13 font-700 ${titleColor}`}>{title}</p>
                {!!subtitle ? (
                    <p className={`text-10 font-400 ${subtitleColor}`}>{subtitle}</p>
                ) : null}
            </div>
        </div>
    );
}

function Triangle({ className, style }: { className?: string; style?: React.CSSProperties }) {
    return (
        <svg
            style={style}
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
        >
            <path
                fill="currentColor"
                d="M290.5 51.8C283.3 39.5 270.2 32 256 32s-27.3 7.5-34.5 19.8l-216 368c-7.3 12.4-7.3 27.7-.2 40.1S25.7 480 40 480H472c14.3 0 27.6-7.7 34.7-20.1s7-27.8-.2-40.1l-216-368z"
            />
        </svg>
    );
}

function TokenHeader({
    onClick,
    canSort = () => true,
}: {
    onClick: (direction: 'ASC' | 'DESC' | 'NONE') => void;
    canSort?: () => boolean;
}) {
    const [sortPreference, setSortPreference] = useState<'ASC' | 'DESC' | 'NONE'>('NONE');
    const allowSorting = canSort();

    return (
        <div
            onClick={() => {
                if (!allowSorting) {
                    return;
                }
                const sortDirection =
                    sortPreference === 'ASC' ? 'DESC' : sortPreference === 'DESC' ? 'NONE' : 'ASC';
                setSortPreference(sortDirection);

                onClick?.(sortDirection);
            }}
            className={`token flex space-x-4 items-center ${
                allowSorting ? 'cursor-pointer' : ''
            } select-none`}
        >
            <p>Token</p>
            {allowSorting ? (
                <div className="flex flex-col space-y-2">
                    <Triangle
                        className={`w-5 h-5 ${
                            sortPreference === 'ASC'
                                ? 'text-darwin-neutral-700'
                                : 'text-darwin-neutral-200'
                        }`}
                    />
                    <Triangle
                        style={{ transform: 'rotate(180deg)' }}
                        className={`w-5 h-5 rotate-180 ${
                            sortPreference === 'DESC'
                                ? 'text-darwin-neutral-700'
                                : 'text-darwin-neutral-200'
                        }`}
                    />
                </div>
            ) : null}
        </div>
    );
}

function PatientDetails({
    a,
    index,
    withVisitCount,
    ptNameClikable,
    showLastVisitedTime,
    lastVisitedDateTime,
    onPatientNameClick,
}: {
    a: AP;
    index: number;
    withVisitCount: boolean;
    ptNameClikable: boolean;
    showLastVisitedTime: boolean;
    lastVisitedDateTime?: string;
    onPatientNameClick?: (a: AP) => void;
}) {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onMouseEnter={() => {
                setIsHovered(true);
            }}
            onMouseLeave={() => {
                setIsHovered(false);
            }}
            className="pt-name-cell"
        >
            <div className="pt-name">
                <div className="flex items-start justify-between w-full">
                    <div className="flex items-start space-x-4 w-87p break-words">
                        <span className="flex w-16 h-16 items-center justify-center ">
                            {a.onApp && (
                                <AntTooltip placement="bottom" content="Connected on eka.care app">
                                    <span className="w-16 h-16">
                                        <EkaCareIcon />
                                    </span>
                                </AntTooltip>
                            )}
                        </span>
                        <span>
                            {a.patient_oid ? (
                                <span
                                    className={`${
                                        ptNameClikable &&
                                        'text-actionPrimary select-all font-700 cursor-pointer'
                                    } capitalize`}
                                    onClick={() => ptNameClikable && onPatientNameClick?.(a)}
                                >
                                    {getPatientNameText(a.patient)}
                                </span>
                            ) : a.patient.fn ? (
                                <i className="text-textColor-grey">
                                    (Booked by{' '}
                                    <span className="select-all">
                                        {getPatientNameText(a.patient)}
                                    </span>
                                    )
                                </i>
                            ) : (
                                <i className="text-textColor-grey">(New Patient)</i>
                            )}
                            {a.patient_oid && a.patient.gen && `, ${a.patient.gen}`}
                            {a.patient_oid && a.patient.dob
                                ? getAgeString(
                                      {
                                          dob: a.patient.dob,
                                      },
                                      ', ',
                                  )
                                : ''}
                        </span>
                        {isHovered ? (
                            <>
                                <svg
                                    onClick={() => {
                                        onPatientEditAction(a);
                                    }}
                                    className="w-12 h-12 text-darwin-neutral-800 cursor-pointer mt-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M362.7 19.3L314.3 67.7 444.3 197.7l48.4-48.4c25-25 25-65.5 0-90.5L453.3 19.3c-25-25-65.5-25-90.5 0zm-71 71L58.6 323.5c-10.4 10.4-18 23.3-22.2 37.4L1 481.2C-1.5 489.7 .8 498.8 7 505s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L421.7 220.3 291.7 90.3z"
                                    />
                                </svg>
                            </>
                        ) : null}
                    </div>
                    <div className="flex items-start">
                        {!!a.patient.amount_pending && a.patient.amount_pending > 0 ? (
                            <AntTooltip
                                placement="top"
                                content={<span>Pending Dues: ₹ {a.patient.amount_pending}</span>}
                            >
                                <div className="xl:ml-8 ml-4 w-20 h-20 bg-darwin-status-warning-100 rounded-full flex justify-center items-center">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        height="1em"
                                        viewBox="0 0 320 512"
                                        fill="currentColor"
                                        className="w-14 h-14 fill-darwin-status-warning-500"
                                    >
                                        <path d="M0 56C0 42.7 10.7 32 24 32h80H296c13.3 0 24 10.7 24 24s-10.7 24-24 24H211.3c17.7 19.8 30.1 44.6 34.7 72h50c13.3 0 24 10.7 24 24s-10.7 24-24 24H246c-11.4 68.1-70.7 120-142 120H98.9L261.9 436.5c10.8 7.7 13.3 22.7 5.6 33.5s-22.7 13.3-33.5 5.6l-224-160C1.6 309.5-2 298.6 1.1 288.7S13.6 272 24 272h80c44.7 0 82.3-30.6 93-72H24c-13.3 0-24-10.7-24-24s10.7-24 24-24H197c-10.7-41.4-48.2-72-93-72H24C10.7 80 0 69.3 0 56z" />
                                    </svg>
                                </div>
                            </AntTooltip>
                        ) : null}
                        {withVisitCount &&
                            a.visit_count !== null &&
                            a.visit_count !== undefined &&
                            (a.visit_count === 0 ? (
                                <p className="ml-4 flex bg-foundations-yellow text-textColor-primary text-12 rounded-8 px-6 py-2 whitespace-nowrap">
                                    New
                                </p>
                            ) : a.visit_count > 0 ? (
                                <span className="visit-no">
                                    <AntTooltip
                                        placement="right"
                                        content={
                                            <span>
                                                {a.visit_count + 1}
                                                {getOrdinalSufics(a.visit_count + 1)} visit
                                            </span>
                                        }
                                    >
                                        <div className="ml-4 inline-flex items-center justify-center font-700 w-20 h-20 min-w-20 min-h-20 rounded-full bg-neutral-9 text-textColor-secondary text-12">
                                            {a.visit_count + 1}
                                        </div>
                                    </AntTooltip>
                                </span>
                            ) : null)}
                    </div>

                    {a.rc && (
                        <span
                            className="video-animate-flicker"
                            style={{
                                height: 10,
                                width: 10,
                                backgroundColor: 'red',
                                color: 'red',
                                border: 1,
                                borderRadius: 20,
                                fontSize: 10,
                                marginLeft: 8,
                                boxShadow: '1px 1px 2px grey',
                            }}
                        ></span>
                    )}
                </div>
            </div>

            {lastVisitedDateTime && moment(lastVisitedDateTime).isValid() && (
                <p className="text-neutral-5 text-12 ml-18">
                    Last Visit:{' '}
                    <span>
                        {moment(lastVisitedDateTime).format('Do MMM YYYY')}
                        {showLastVisitedTime ? (
                            <Fragment>
                                {', '}
                                <span
                                    style={{
                                        borderColor: 'gray',
                                        borderBottomWidth: 1,
                                        borderBottomStyle: 'dotted',
                                    }}
                                    className="font-700"
                                >
                                    {moment(lastVisitedDateTime).format(' hh:mm A')}
                                </span>
                            </Fragment>
                        ) : (
                            <Fragment>
                                {' '}
                                <span
                                    style={{
                                        borderColor: 'gray',
                                        borderBottomWidth: 1,
                                        borderBottomStyle: 'dotted',
                                    }}
                                    className="font-700"
                                >
                                    {moment().diff(moment(lastVisitedDateTime), 'day') <= 30 &&
                                        `(${formatDistanceToNow(new Date(lastVisitedDateTime), {
                                            addSuffix: true,
                                        })})`}
                                </span>
                            </Fragment>
                        )}
                    </span>
                </p>
            )}
        </div>
    );
}

function AwaitingMessageResponse() {
    return (
        <div className={'flex space-x-7 items-start'}>
            <svg
                width="9"
                height="12"
                viewBox="0 0 9 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="fill-darwin-accent-bright-yellow-900 w-14 h-14 mt-4"
            >
                <path d="M7.77083 10V9.54883C7.77083 8.94727 7.57747 8.38867 7.21224 7.89453L5.70833 5.875L7.21224 3.87695C7.57747 3.38281 7.77083 2.80273 7.77083 2.22266V1.75C8.13607 1.75 8.45833 1.44922 8.45833 1.0625C8.45833 0.697266 8.13607 0.375 7.77083 0.375H0.895833C0.509115 0.375 0.208333 0.697266 0.208333 1.0625C0.208333 1.44922 0.509115 1.75 0.895833 1.75V2.22266C0.895833 2.82422 1.06771 3.38281 1.43294 3.87695L2.95833 5.875L1.43294 7.89453C1.06771 8.36719 0.895833 8.94727 0.895833 9.54883V10C0.509115 10 0.208333 10.3223 0.208333 10.6875C0.208333 11.0742 0.509115 11.375 0.895833 11.375H7.77083C8.13607 11.375 8.45833 11.0742 8.45833 10.6875C8.45833 10.3223 8.13607 10 7.77083 10ZM2.5931 3.125L2.52865 3.03906C2.35677 2.80273 2.27083 2.52344 2.27083 2.22266V1.75H6.39583V2.22266C6.39583 2.52344 6.28841 2.80273 6.11654 3.03906L6.05208 3.125H2.5931ZM4.33333 6.34766L6.05208 8.625H2.5931L4.33333 6.34766Z" />
            </svg>
            <p className="text-darwin-accent-bright-yellow-900 text-13 font-700">
                Awaiting Response
            </p>
        </div>
    );
}

function NotComingMessageResponse({ cancelReason }: { cancelReason?: string }) {
    return (
        <div className={'flex space-x-7 items-start'}>
            <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={'fill-darwin-status-warning-500 w-14 h-14 mt-4'}
            >
                <path d="M6.33333 0.375C3.28255 0.375 0.833333 2.8457 0.833333 5.875C0.833333 8.92578 3.28255 11.375 6.33333 11.375C9.36263 11.375 11.8333 8.92578 11.8333 5.875C11.8333 2.8457 9.36263 0.375 6.33333 0.375ZM2.20833 5.875C2.20833 4.99414 2.48763 4.17773 2.96029 3.49023L8.7181 9.24805C8.0306 9.7207 7.21419 10 6.33333 10C4.05599 10 2.20833 8.15234 2.20833 5.875ZM9.6849 8.28125L3.92708 2.52344C4.61458 2.05078 5.43099 1.75 6.33333 1.75C8.58919 1.75 10.4583 3.61914 10.4583 5.875C10.4583 6.77734 10.1576 7.59375 9.6849 8.28125Z" />
            </svg>
            <div className={'flex flex-col'}>
                <p className={'text-13 font-700 text-darwin-status-warning-500'}>Not Coming</p>
                {cancelReason ? (
                    <p className="text-10 font-400 text-darwin-neutral-500">"{cancelReason}"</p>
                ) : null}
            </div>
        </div>
    );
}

function ConfirmedVisitMessageResponse({
    formattedResponseMessage,
}: {
    formattedResponseMessage?: string;
}) {
    return (
        <div className={'flex space-x-7 items-start'}>
            <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="fill-darwin-accent-green-900 w-14 h-14 mt-4 "
            >
                <path d="M6.33333 0.375C3.28255 0.375 0.833333 2.8457 0.833333 5.875C0.833333 8.92578 3.28255 11.375 6.33333 11.375C9.36263 11.375 11.8333 8.92578 11.8333 5.875C11.8333 2.8457 9.36263 0.375 6.33333 0.375ZM8.80404 4.92969L6.05404 7.67969C5.94661 7.80859 5.79622 7.85156 5.64583 7.85156C5.47396 7.85156 5.32357 7.80859 5.21615 7.67969L3.84115 6.30469C3.60482 6.06836 3.60482 5.70312 3.84115 5.4668C4.07747 5.23047 4.44271 5.23047 4.67904 5.4668L5.64583 6.41211L7.96615 4.0918C8.20247 3.85547 8.56771 3.85547 8.80404 4.0918C9.04036 4.32812 9.04036 4.69336 8.80404 4.92969Z" />
            </svg>
            <div className="flex flex-col">
                <p className="text-darwin-accent-green-900 text-13 font-700">Confirmed Visit</p>
                {formattedResponseMessage ? (
                    <p className={'text-10 font-400 text-darwin-neutral-500'}>
                        "{formattedResponseMessage}"
                    </p>
                ) : null}
            </div>
        </div>
    );
}

function RescheduledMessageResponse({ responseMessage }: { responseMessage?: string }) {
    return (
        <div className={'flex space-x-7 items-start'}>
            <svg
                width="13"
                height="12"
                viewBox="0 0 13 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={'fill-darwin-accent-symptoms-blue-900 min-w-14 w-14 h-14 m-4'}
            >
                <path d="M9.77083 2.78125C9.77083 2.22266 9.29818 1.75 8.73958 1.75H7.70833V1.0625C7.70833 0.697266 7.38607 0.375 7.02083 0.375C6.63411 0.375 6.33333 0.697266 6.33333 1.0625V1.75H3.58333V1.0625C3.58333 0.697266 3.26107 0.375 2.89583 0.375C2.50911 0.375 2.20833 0.697266 2.20833 1.0625V1.75H1.17708C0.597005 1.75 0.145833 2.22266 0.145833 2.78125V3.8125H9.77083V2.78125ZM9.42708 5.1875C7.70833 5.1875 6.33333 6.58398 6.33333 8.28125C6.33333 10 7.70833 11.375 9.42708 11.375C11.1243 11.375 12.5208 10 12.5208 8.28125C12.5208 6.58398 11.1243 5.1875 9.42708 5.1875ZM10.4583 8.625H9.27669C9.16927 8.625 9.08333 8.53906 9.08333 8.43164V6.90625C9.08333 6.73438 9.23372 6.5625 9.42708 6.5625C9.59896 6.5625 9.77083 6.73438 9.77083 6.90625V7.9375H10.4583C10.6302 7.9375 10.8021 8.10938 10.8021 8.28125C10.8021 8.47461 10.6302 8.625 10.4583 8.625ZM5.64583 8.28125C5.64583 6.19727 7.32161 4.5 9.42708 4.5H0.145833V10.3438C0.145833 10.9238 0.597005 11.375 1.17708 11.375H7.25716C6.26888 10.709 5.64583 9.57031 5.64583 8.28125Z" />
            </svg>

            <div className="flex flex-col">
                <p className="text-darwin-accent-symptoms-blue-900 text-13 font-700">Rescheduled</p>
                {responseMessage ? (
                    <p className={'text-10 font-400 text-darwin-neutral-500'}>{responseMessage}</p>
                ) : null}
            </div>
        </div>
    );
}

function getDefaultColumnPreference({
    isHmisDoctor,
}: {
    isHmisDoctor?: boolean;
} = {}): State['appointmentConfigs']['user_preferences'] {
    return {
        [todayQueueColumnKeys]: [
            'SQN',
            'PDT',
            'UHID',
            'CON',
            isHmisDoctor ? 'AHID' : 'VITY',
            'SLOT',
            'PAYS',
            'TAGS',
            'ACTS',
        ],
        [completedQueueColumnKeys]: [
            'PDT',
            'UHID',
            'CON',
            isHmisDoctor ? 'AHID' : 'VITY',
            'PAYS',
            'AMOU',
            'FLWP',
            'TAGS',
            'ACTS',
        ],
        [othersQueueColumnKeys]: [
            'PDT',
            'UHID',
            'CON',
            isHmisDoctor ? 'AHID' : 'VITY',
            'SLOT',
            'VIST',
            'PAYS',
            'TAGS',
            'ACTS',
        ],
        [upcomingQueueColumnKeys]: [
            'SQN',
            'PDT',
            'UHID',
            'CON',
            isHmisDoctor ? 'AHID' : 'VITY',
            'SLOT',
            'VIST',
            'PAYS',
            'TAGS',
            'ACTS',
        ],
        [followupQueueColumnKeys]: [
            'SQN',
            'PDT',
            isHmisDoctor ? 'AHID' : 'VITY',
            'UHID',
            'CON',
            'SLOT',
            'VIST',
            'TAGS',
            'RECO',
            'ACTS',
        ],
    };
}

declare type EventValue<Moment> = Moment | null;
export declare type RangeValue<Moment> = [EventValue<Moment>, EventValue<Moment>] | null;
